import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from '../services/api';
import {
  ChevronDown, ChevronUp, CheckCircle2, XCircle, AlertTriangle, Info,
  Download, RefreshCw, Filter, Search, ArrowRight, ChevronRight, ChevronLeft,
  Shield, Lock, Zap, GitBranch, Eye, EyeOff, Plus, PlusCircle, Trash2, ExternalLink,
  FlaskConical, Sparkles, CheckSquare, Square, Ban, Settings, Wrench, Users, TrendingUp,
  Check, X, CheckCheck, ArrowLeft, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { resolveDefaultSession } from '../utils/resolveDefaultSession';
import {
  AddProductFormBody, emptyUnit, emptyProductBase,
  DEFAULT_UNIT_TYPES, DEFAULT_UNIT_LABELS,
} from '../components/shared/AddProductFormBody';
import { DuplicateReviewPanel, DUPLICATE_REASON_MIN } from '../components/shared/DuplicateReviewPanel';

import { useAuth } from '../contexts/AuthContext';
import { AiBatchProgress } from '../components/psb/AiBatchProgress';
import { PsbPriceReviewPanel } from './panels/PsbPriceReviewPanel';

const API = getBackendUrl();

// ── Standard bottle sizes for AI exact-match (Wave 1 Item 7) ─────────────────
// ONLY used for exact equality.  No proximity snap is applied — the user
// must see the raw AI value if it does not exactly match a known standard.
// 660 ml MUST NOT become 700 ml; the AI suggestion is advisory only.
const STANDARD_BOTTLE_ML = [200, 250, 275, 330, 440, 500, 568, 700, 750];
const snapToStandardBottleMl = (ml) => {
  if (!ml || ml <= 0) return ml;
  // Exact match only (or within floating-point rounding: ±0.5 ml)
  const exact = STANDARD_BOTTLE_ML.find(s => Math.abs(s - ml) < 0.5);
  return exact !== undefined ? exact : ml;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v, dec = 2) =>
  typeof v === "number" ? `£${v.toFixed(dec)}` : "—";

const pct = (v) => (typeof v === "number" ? `${v.toFixed(1)}%` : "—");

const STATUS_META = {
  exact:            { label: "Exact",            color: "bg-emerald-100 text-emerald-800 border-emerald-200",  icon: CheckCircle2, bulkAllowed: true,  approvable: true  },
  strong_suggestion:{ label: "Suggested match",       color: "bg-amber-100 text-amber-800 border-amber-200",        icon: AlertTriangle, bulkAllowed: false, approvable: true  },
  review_required:  { label: "Needs your decision",   color: "bg-orange-100 text-orange-800 border-orange-200",     icon: Eye,           bulkAllowed: false, approvable: true  },
  blocked:          { label: "Cannot be matched yet", color: "bg-red-100 text-red-800 border-red-200",              icon: Lock,          bulkAllowed: false, approvable: false },
  proposed_new:     { label: "Create a product",      color: "bg-purple-100 text-purple-800 border-purple-200",     icon: Zap,           bulkAllowed: false, approvable: true  },
};

const DEC_META = {
  approved: { label: "Approved",  color: "bg-green-100 text-green-700",   icon: CheckCircle2 },
  pending:  { label: "Pending",   color: "bg-slate-100 text-slate-600",   icon: Info         },
  rejected: { label: "Rejected",  color: "bg-red-100 text-red-600",       icon: XCircle      },
  excluded: { label: "Excluded",  color: "bg-orange-100 text-orange-700", icon: Ban          },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, color: "bg-slate-100 text-slate-700 border-slate-200", icon: Info };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${m.color}`}>
      <Icon size={11} /> {m.label}
    </span>
  );
}

function DecisionBadge({ decision }) {
  const m = DEC_META[decision] || { label: decision, color: "bg-slate-100 text-slate-600", icon: Info };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${m.color}`}>
      <Icon size={11} /> {m.label}
    </span>
  );
}

function ConfBadge({ conf }) {
  const v = Math.round((conf || 0) * 100);
  const color = v >= 90 ? "text-emerald-700" : v >= 70 ? "text-amber-700" : "text-red-600";
  return (
    <span
      className={`text-xs font-mono font-semibold ${color}`}
      title="Confidence score only — a score of 1.00 (100%) can still be blocked by ties, variant conflicts, or failed identity gates. Score alone is not approval."
    >
      {v}%
    </span>
  );
}

// ── Gate badge ────────────────────────────────────────────────────────────────

function GateBadge({ name, gate }) {
  const label = { category: "Category", variant: "Variant", pack_size: "Pack", measure: "Measure" }[name] || name;
  if (gate.passed) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 border border-emerald-200" title={gate.reason}>
        <CheckCircle2 size={10} /> {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-700 border border-red-200" title={gate.reason}>
      <XCircle size={10} /> {label}
    </span>
  );
}

// ── Draft helpers — user+org+site-scoped key + 24 h TTL ─────────────────────
//
// Stable IDs (user.id, user.organisation_id) come from the existing AuthContext
// current-user response — NOT from tokens, cookies, or browser-auth inspection.
// siteId is passed explicitly as a prop from the parent page's URL/session state.
//
// Fail-closed contract:
//  • If any of user.id / user.organisation_id / siteId is absent, all draft
//    operations (read, write, discard) are skipped — no silent anon fallback.
//  • This prevents cross-user and cross-site leakage while degrading gracefully
//    when the page loads without a fully resolved context.
//
// Draft key schema (v3):
//   psb_draft_v3_{userId}_{orgId}_{siteId}_{s1RunId}_{mc_id}
//
// Any old v1/v2 keys are left in storage and will be evicted by the 24 h TTL
// on their own open; they do not conflict because v3 keys never match them.

// Drafts older than 24 h are discarded silently on next open.
const _DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

// ── Governed New-Product Modal ────────────────────────────────────────────────
//
// Reachable from the owner evidence card (c_ammar tab) or ResolutionPanel.
// Per-row pre-fills are keyed to owner-confirmed decisions — see _OWNER_PREFILLS.
// Confirmed fields are displayed read-only with provenance; only genuinely
// unknown fields require owner input. Group 19 (S6C bridge) and PET TANGO
// (ic=120800) are hard-blocked.

// ── UOM unit-type options ─────────────────────────────────────────────────────
const PACK_UNIT_TYPES = [
  { value: "g",     label: "g (grams)" },
  { value: "ml",    label: "ml (millilitres)" },
  { value: "cl",    label: "cl (centilitres)" },
  { value: "each",  label: "each / unit" },
  { value: "other", label: "other — specify in label" },
];

// ── Per-row owner-confirmed pre-fills ─────────────────────────────────────────
// Only fields the owner has explicitly confirmed are populated here.
// Anything left null must be entered by the owner before submission.
// Do NOT infer pack size, yield, ratio, or price from size_raw or PLU data.
const _OWNER_PREFILLS = {
  "200500": {
    // MATCH POWDER = Matcha Powder — 250g pack, 20g per serve
    // Owner confirmed (2026): identity + pack unit + serving measure
    proposed_name:    "Matcha Powder",
    pack_qty:         "250",
    pack_type:        "g",
    pack_label:       "250g pack",
    pack_confirmed:   true,
    selling_qty:      "20",
    selling_type:     "g",
    selling_label:    "20g per serve",
    selling_confirmed: true,
    classification_hint: null,
    provisional:      false,
    confirmed_note:   "Owner confirmed (2026): Matcha Powder, 250g pack, 20g default per serve.",
  },
  "200200": {
    // FUNKIN PASSION — provisional post-mix syrup
    // Post-mix ratio and yield UNRESOLVED — to be defined in Product Master
    proposed_name:    "Funkin Passion Purée",
    pack_qty:         null,   // size_raw='LTR' — NOT confirmed as litres
    pack_type:        null,
    pack_label:       null,
    pack_confirmed:   false,
    selling_qty:      null,   // ratio/yield UNRESOLVED
    selling_type:     null,
    selling_label:    null,
    selling_confirmed: false,
    classification_hint: "post-mix",
    provisional:      true,
    confirmed_note:   "Owner advisory (2026): appears to be a post-mix syrup. Classification is provisional. Pack size and post-mix ratio/yield are UNRESOLVED — to be defined in Product Master after creation. Do not infer from raw size_raw='LTR'.",
  },
  "201000": {
    // MONIN STRAWBERRY — owner confirmed 700ml bottle, 20ml measure (2026)
    proposed_name:    "Monin Strawberry Syrup",
    pack_qty:         "700",
    pack_type:        "ml",
    pack_label:       "700ml bottle",
    pack_confirmed:   true,
    selling_qty:      "20",
    selling_type:     "ml",
    selling_label:    "20ml measure",
    selling_confirmed: true,
    classification_hint: "syrups / post-mix ingredient",
    // Taxonomy: resolves against the existing configured hierarchy.
    // "Spirits > Cocktail Ingredients" is the org's established path for
    // cocktail syrups/ingredients — confirmed by owner.  resolveAITaxonomy
    // matches these names against the live categoryOptions at accept time.
    category:         "Spirits",
    subcategory:      "Cocktail Ingredients",
    provisional:      false,
    confirmed_note:   "Owner confirmed (2026): 700ml bottle, 20ml default measure. Monin Strawberry flavoured syrup — post-mix cocktail ingredient. NOT a fountain post-mix concentrate; do NOT apply 1000ml bag-in-box rule.",
  },
  "200900": {
    // MONIN RASPBERRY — owner confirmed 700ml bottle, 20ml measure (2026)
    proposed_name:    "Monin Raspberry Syrup",
    pack_qty:         "700",
    pack_type:        "ml",
    pack_label:       "700ml bottle",
    pack_confirmed:   true,
    selling_qty:      "20",
    selling_type:     "ml",
    selling_label:    "20ml measure",
    selling_confirmed: true,
    classification_hint: "syrups / post-mix ingredient",
    // Taxonomy: Spirits > Cocktail Ingredients (owner-confirmed path)
    category:         "Spirits",
    subcategory:      "Cocktail Ingredients",
    provisional:      false,
    confirmed_note:   "Owner confirmed (2026): 700ml bottle, 20ml default measure. Monin Raspberry flavoured syrup — post-mix cocktail ingredient. NOT a fountain post-mix concentrate.",
  },
  "200600": {
    // MONIN GOMME — owner confirmed 700ml bottle, 20ml measure (2026)
    proposed_name:    "Monin Gomme Syrup",
    pack_qty:         "700",
    pack_type:        "ml",
    pack_label:       "700ml bottle",
    pack_confirmed:   true,
    selling_qty:      "20",
    selling_type:     "ml",
    selling_label:    "20ml measure",
    selling_confirmed: true,
    classification_hint: "syrups / post-mix ingredient",
    // Taxonomy: Spirits > Cocktail Ingredients (owner-confirmed path)
    category:         "Spirits",
    subcategory:      "Cocktail Ingredients",
    provisional:      false,
    confirmed_note:   "Owner confirmed (2026): 700ml bottle, 20ml default measure. Monin Gomme plain sugar syrup — post-mix cocktail ingredient. NOT a fountain post-mix concentrate.",
  },
};

// Small confirmed-field pill
function ConfirmedPill({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
      <CheckCircle2 size={9}/> <span className="text-emerald-500">{label}:</span> {value}
    </span>
  );
}
function UnknownPill({ label }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
      <AlertTriangle size={9}/> {label}: <span className="italic">not confirmed</span>
    </span>
  );
}

// Provenance badge for AI-suggested, owner-confirmed, master-derived, unknown
const PROVENANCE_STYLE = {
  "owner-confirmed": { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  "AI-suggested":    { cls: "bg-violet-100 text-violet-800 border-violet-200",    Icon: Sparkles },
  "master-derived":  { cls: "bg-blue-100 text-blue-800 border-blue-200",          Icon: Info },
  "unknown":         { cls: "bg-amber-100 text-amber-700 border-amber-200",        Icon: AlertTriangle },
};
function AIPrefillBadge({ provenance }) {
  const s = PROVENANCE_STYLE[provenance] || PROVENANCE_STYLE["unknown"];
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border ${s.cls}`}>
      <Icon size={8}/> {provenance}
    </span>
  );
}

// ── Pure taxonomy resolver — called BEFORE setForm so the active categoryOptions
// snapshot is baked in at call-time, eliminating any stale-closure risk inside
// the React state-updater function.
// Split a subcategory name/suggestion into significant tokens.
// Strips punctuation/digits; keeps only tokens ≥ 3 chars that are not pure
// numbers — avoids trivial matches on "0%", "n", "go", etc.
function _taxonomyTokens(str) {
  return str
    .toLowerCase()
    .split(/[\s/&\-,_.()%0-9]+/)
    .filter(t => t.length >= 3 && !/^\d+$/.test(t));
}

// Returns { categoryId, category, subcategoryId, subcategoryName,
//           aiSuggestedSubcategory } or null.
// aiSuggestedSubcategory is non-null when the AI named a subcategory that doesn't
// resolve to any catalogue entry — callers surface it as an "unresolved" hint.
// Exported for direct unit testing.
export function resolveAITaxonomy(pref, cats) {
  if (!pref || !pref.category || !cats || cats.length === 0) return null;
  const lcCat = pref.category.toLowerCase();

  // Pass 1: top-level category exact or partial match
  let matchedCat = cats.find(c => {
    const lc = c.label.toLowerCase();
    return lc === lcCat || lc.includes(lcCat) || lcCat.includes(lc);
  });
  let preselectedSub = null;

  // Pass 2: AI sometimes names a subcategory as the top-level category
  // (e.g. AI returns category="Liqueurs" → configured: Spirits / Liqueurs,
  //  or "Champagne/Sparkling" → Wine / Sparkling / Champagne).
  // Two matching strategies:
  //  a) substring containment (existing behaviour)
  //  b) token-level bidirectional — handles word-order swaps and different
  //     separator styles (e.g. "Champagne/Sparkling" ↔ "Sparkling / Champagne").
  //     Requires ≥2 AI tokens to avoid ambiguous single-word matches.
  if (!matchedCat) {
    for (const cat of cats) {
      const sub = (cat.subcategories || []).find(s => {
        const ls = s.name.toLowerCase();
        if (ls === lcCat || ls.includes(lcCat) || lcCat.includes(ls)) return true;
        const aiToks = _taxonomyTokens(lcCat);
        if (aiToks.length >= 2) {
          const subToks = _taxonomyTokens(ls);
          if (aiToks.every(t => subToks.includes(t))) return true;
        }
        return false;
      });
      if (sub) { matchedCat = cat; preselectedSub = sub; break; }
    }
  }

  if (!matchedCat) return null;

  let subcategoryId = null, subcategoryName = '';
  if (preselectedSub) {
    subcategoryId   = preselectedSub.id;
    subcategoryName = preselectedSub.name;
  } else if (pref.subcategory && matchedCat.subcategories?.length > 0) {
    const lcSub = pref.subcategory.toLowerCase();
    // Pass 1b: exact or bi-directional substring match (existing behaviour)
    const matchedSub = matchedCat.subcategories.find(s => {
      const ls = s.name.toLowerCase();
      return ls === lcSub || ls.includes(lcSub) || lcSub.includes(ls);
    });
    if (matchedSub) { subcategoryId = matchedSub.id; subcategoryName = matchedSub.name; }

    // Pass 3: Unique-token discriminating match.
    // A token from the AI suggestion that appears in exactly ONE catalogue
    // subcategory is a reliable, unambiguous signal (e.g. the token "sparkling"
    // appears only in "Sparkling / Champagne" across all Wine subcategories;
    // "vermouth" only in "Fortified / Vermouth").  Requires token length ≥ 3.
    if (!subcategoryId) {
      const aiToks = _taxonomyTokens(pref.subcategory);
      for (const tok of aiToks) {
        const hits = matchedCat.subcategories.filter(s =>
          _taxonomyTokens(s.name).includes(tok)
        );
        if (hits.length === 1) {
          subcategoryId   = hits[0].id;
          subcategoryName = hits[0].name;
          break;
        }
      }
    }

    // Pass 4: Same-name fallback.
    // When the category has a subcategory named identically to the category
    // itself (e.g. "Soft Drinks" category → "Soft Drinks" subcategory), use it
    // as a reliable generic bucket when the AI suggested a specific subtype that
    // isn't in the catalogue (e.g. AI says "Ginger Beer" under Soft Drinks →
    // falls back to "Soft Drinks" subcategory).  Only applied when AI provided
    // a subcategory suggestion that did not resolve via Passes 1b–3.
    if (!subcategoryId) {
      const catNm = (matchedCat.label || matchedCat.name || '').toLowerCase();
      const sameNameSub = matchedCat.subcategories.find(s => {
        const sn = s.name.toLowerCase();
        return sn === catNm || sn.includes(catNm) || catNm.includes(sn);
      });
      if (sameNameSub) {
        subcategoryId   = sameNameSub.id;
        subcategoryName = sameNameSub.name;
      }
    }
  }

  // Track what the AI suggested even when it didn't match — surfaces as a visible
  // "unresolved" hint in the form so the user can pick the right subcategory manually.
  const aiSuggestedSubcategory = (!subcategoryId && pref.subcategory) ? pref.subcategory : null;

  return {
    categoryId:              matchedCat.id,
    category:                matchedCat.slug || matchedCat.label,
    subcategoryId,
    subcategoryName,
    aiSuggestedSubcategory,  // null when resolved; non-null when AI named an unknown subcategory
  };
}

// Exported for integration testing (autoApply + manual Apply flows).
/**
 * Snaps an ml value to the best-matching catalog unit-type key.
 * Returns null when: no catalog entry has this exact ml, multiple entries exist
 * and none can be unambiguously chosen, or ml ≤ 0.
 *
 * Disambiguation when multiple candidates share the same ml:
 *  1. Single candidate → return it directly.
 *  2. Multiple → score each by token overlap with hintLabel (normalised AI label,
 *     e.g. "bottle", "can") and categorySlug (e.g. "wine", "beer").
 *     Return the unique top scorer; null on tie — leave blank for user.
 *
 * Caller must only invoke this for blank or non-catalog unit_type values;
 * valid catalog keys the user selected explicitly must not be replaced.
 */
function _snapUnitType(ml, hintLabel, categorySlug, dynamicUnitMlMap, dynamicUnitTypes) {
  if (!ml || ml <= 0) return null;
  const candidates = Object.entries(dynamicUnitMlMap)
    .filter(([k, v]) => v === ml && dynamicUnitTypes.includes(k))
    .map(([k]) => k);
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  // Multiple candidates — score by token overlap with label + category hints
  const hintToks = [
    ..._taxonomyTokens(hintLabel || ''),
    ...(categorySlug ? [categorySlug.toLowerCase()] : []),
  ].filter((t, i, a) => a.indexOf(t) === i); // dedupe
  if (hintToks.length === 0) return null;
  const scored = candidates.map(k => ({
    k,
    score: hintToks.filter(t => k.toLowerCase().includes(t)).length,
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored[0].score > 0 && scored[0].score > (scored[1]?.score ?? 0)
    ? scored[0].k
    : null; // tied — leave blank for user
}

export function GoverNedNewProductModal({ row, s1RunId, siteId, onSuccess, onCancel, aiPrefill, aiPrefillLoading, autoApplyAI, submitAdapter }) {
  const pf = _OWNER_PREFILLS[String(row.itemcode)] || {};
  const ai = aiPrefill?.prefill || {};

  // ── Stable user/org IDs from application AuthContext (no token/cookie inspection)
  const { user } = useAuth();

  // ── Standard form state (same shape as BeverageStockMasterPage) ─────────────
  const initPurchaseUnitType = pf.pack_type || ai.pack_label?.toLowerCase().replace(/\s+/g,'_') || "";
  const initPurchaseMl = pf.pack_qty || ai.pack_qty || "";
  const [form, setForm] = useState(() => ({
    ...emptyProductBase(),
    product_name: pf.proposed_name || ai.suggested_name || (row.source_description || "").trim(),
    // Pre-populate the purchase unit if owner-confirmed data is available
    units: [{
      ...emptyUnit(),
      unit_type: initPurchaseUnitType,
      conversion_to_base: String(initPurchaseMl),
      is_default_count_unit: true,
      is_default_purchase_unit: true,
    }],
    // PSB-specific
    source_evidence: [
      `Legacy code ${row.itemcode}: "${(row.source_description || "").trim()}"`,
      pf.confirmed_note || "",
      "Identity confirmed by: ",
    ].filter(Boolean).join(" — "),
    notes: "",
    reviewer_confirmation: false,
  }));

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  // Stage 1A — shared duplicate review (server returns candidates on 409)
  const [dupCandidates, setDupCandidates] = useState([]);
  const [dupReason, setDupReason] = useState('');
  useEffect(() => { setDupCandidates([]); }, [form.product_name, form.brand]);

  // ── Draft recovery (localStorage) ───────────────────────────────────────────
  // Preserves form state across navigation, page refresh, and normal
  // reauthentication (token refresh does NOT wipe localStorage).
  //
  // Design rules:
  //  • Draft key v3 = user/org/site/run/row — obtained from AuthContext user
  //    response (user.id, user.organisation_id) and from explicit siteId prop.
  //    No token, cookie, or browser-auth inspection occurs anywhere here.
  //  • Fail-closed: if user, user.id, user.organisation_id, or siteId is absent,
  //    draftEnabled is set to false and ALL localStorage operations are skipped.
  //    There is no 'anon' fallback — sharing a key across unknown users would
  //    risk cross-user leakage of uncommitted product data.
  //  • TTL of 24 h: drafts older than _DRAFT_TTL_MS are discarded on open.
  //  • Never auto-submit a recovered draft — user must explicitly Restore.
  //  • Clearly distinguish "unsaved draft" (localStorage) from
  //    "durable decision" (saved in DB via onSuccess).
  //  • Draft is cleared on: successful submit, explicit Cancel, or Discard.
  const draftEnabled = !!(user?.id && user?.organisation_id && siteId);
  const DRAFT_KEY = draftEnabled
    ? `psb_draft_v3_${user.id}_${user.organisation_id}_${siteId}_${s1RunId}_${row.mc_id}`
    : null;

  // Helper: returns true if the raw draft JSON is present and not expired.
  const _isDraftFresh = (raw) => {
    try {
      if (!raw) return false;
      const { savedAt } = JSON.parse(raw);
      return !!savedAt && (Date.now() - savedAt < _DRAFT_TTL_MS);
    } catch { return false; }
  };

  const [draftBannerOpen, setDraftBannerOpen] = useState(() => {
    try {
      if (!DRAFT_KEY) return false;          // fail closed: no scope context
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!_isDraftFresh(raw)) {
        // Silently discard stale / malformed drafts
        if (raw) { try { localStorage.removeItem(DRAFT_KEY); } catch {} }
        return false;
      }
      return true;
    } catch { return false; }
  });
  const [hasDraft, setHasDraft] = useState(() => {
    try {
      if (!DRAFT_KEY) return false;          // fail closed: no scope context
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!_isDraftFresh(raw)) {
        if (raw) { try { localStorage.removeItem(DRAFT_KEY); } catch {} }
        return false;
      }
      return true;
    } catch { return false; }
  });

  // Autosave to localStorage — state-based gate.
  // While draftBannerOpen is true the user has not yet resolved their Restore /
  // Discard choice.  Any form change firing during that window — including
  // deferred autoApplyAI updates triggered after API calls settle — must NOT
  // overwrite the preexisting manual draft payload.
  // The dep array intentionally omits draftBannerOpen: the effect fires only when
  // form actually changes, so closing the banner via Discard (which does not
  // mutate form) never re-creates the key — no silent resurrection after discard.
  useEffect(() => {
    if (!DRAFT_KEY) return;                   // fail closed — no scope
    if (draftBannerOpen) return;             // pending choice — do not write
    if (!hasDraft && !form.product_name && !form.units?.[0]?.unit_type) return;
    try {
      const snapshot = { form, savedAt: Date.now() };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(snapshot));
      setHasDraft(true);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const restoreDraft = () => {
    if (!DRAFT_KEY) return;                   // fail closed
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!_isDraftFresh(raw)) return;
      const { form: draftForm } = JSON.parse(raw);
      setForm(draftForm);
      setDraftBannerOpen(false);
    } catch {}
  };
  const discardDraft = () => {
    if (DRAFT_KEY) { try { localStorage.removeItem(DRAFT_KEY); } catch {} }
    setHasDraft(false);
    setDraftBannerOpen(false);
  };
  const clearDraftOnSuccess = () => {
    if (DRAFT_KEY) { try { localStorage.removeItem(DRAFT_KEY); } catch {} }
  };

  // ── Taxonomy categories (same call as BeverageStockMasterPage) ──────────────
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [dynamicUnitTypes, setDynamicUnitTypes] = useState(DEFAULT_UNIT_TYPES);
  const [dynamicUnitLabels, setDynamicUnitLabels] = useState(DEFAULT_UNIT_LABELS);
  const [dynamicUnitMlMap, setDynamicUnitMlMap] = useState({});

  // auto-apply guard — fires once when autoApplyAI=true + data + categories loaded
  const autoAppliedRef = useRef(false);
  // tracks whether measure-definitions API has settled (success or error)
  // so autoApply waits for full catalog data before firing (prevents "Custom bottle")
  const [measuresAttempted, setMeasuresAttempted] = useState(false);

  const isG19Hold    = String(row.group_code) === "19" && Math.abs(row.plu_value_inc_vat || row.s4_plu_revenue || 0) > 0;
  const isHardBlocked = isG19Hold;

  useEffect(() => {
    if (isHardBlocked) return;
    // Load taxonomy categories
    api.get('/product-taxonomy/categories')
    .then(r => {
      const cats = (r.data?.categories || []).filter(c => c.is_active).map(c => ({
        id: c.id, slug: c.slug, label: c.name, is_system: c.is_system,
        subcategories: (c.subcategories || []).filter(s => s.is_active),
      }));
      setCategoryOptions(cats);
    }).catch(() => {});
    // Load measure definitions for unit-type dropdown auto-populate
    api.get('/stocktake/measure-definitions').then(r => {
      const defs = r.data || [];
      if (defs.length > 0) {
        const sorted = [...defs].sort((a, b) => (a.name||'').localeCompare(b.name||'', undefined, {sensitivity:'base'}));
        const types = sorted.map(m => m.name.toLowerCase().replace(/\s+/g,'_'));
        const labels = {}, mlMap = {};
        sorted.forEach(m => {
          const key = m.name.toLowerCase().replace(/\s+/g,'_');
          labels[key] = m.name;
          if (m.default_ml != null && Number(m.default_ml) > 0) mlMap[key] = Number(m.default_ml);
        });
        setDynamicUnitTypes(types);
        setDynamicUnitLabels(labels);
        setDynamicUnitMlMap(mlMap);
      }
    }).catch(() => {}).finally(() => setMeasuresAttempted(true));
  }, [isHardBlocked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard against re-entry: button is disabled while loading, but an accidental
    // programmatic submit (e.g. Enter key, browser autofill) could bypass that.
    if (loading) return;
    if (!form.subcategory_id) {
      setError("Please select a subcategory."); return;
    }
    const purchaseUnit = form.units.find(u => u.is_default_purchase_unit && u.unit_type && u.conversion_to_base);
    if (!purchaseUnit) {
      setError("At least one unit must have the Purchase role with a type and ml/size value."); return;
    }
    if (dupCandidates.length > 0 && dupReason.trim().length < DUPLICATE_REASON_MIN) {
      setError("This may already exist — map the row to an existing product, or give a reason (at least 10 characters)."); return;
    }
    setError(null);
    setLoading(true);
    try {
      const purchaseBase = purchaseUnit.base_unit || "ml";
      const confirmedEvidence = [
        form.source_evidence.trim(),
        `Pack: ${purchaseUnit.conversion_to_base}${purchaseBase} ${purchaseUnit.unit_type}`,
        form.notes ? `Notes: ${form.notes.trim()}` : null,
      ].filter(Boolean).join(" | ");

      const salesUnit = form.units.find(u => (u.is_default_sales_unit || u.is_sales_unit) && u.conversion_to_base);

      const body = {
        mc_id:                 row.mc_id,
        proposed_name:         form.product_name.trim(),
        subcategory_id:        Number(form.subcategory_id),
        brand:                 form.brand?.trim() || null,
        purchase_unit_ml:      Number(purchaseUnit.conversion_to_base),
        purchase_unit_label:   `${purchaseUnit.unit_type} ${purchaseUnit.conversion_to_base}${purchaseBase}`,
        selling_unit_ml:       salesUnit ? Number(salesUnit.conversion_to_base) : null,
        selling_unit_label:    salesUnit ? salesUnit.unit_type : null,
        units:                 form.units,       // full units array for richer creation
        external_ref:          null,
        source_evidence:       confirmedEvidence,
        reviewer_confirmation: true,
        notes:                 form.notes?.trim() || null,
        internal_note:         form.notes?.trim() || null,
        ...(dupCandidates.length > 0 ? { force_create: true, duplicate_override_reason: dupReason.trim() } : {}),
      };
      const { data } = submitAdapter
        ? { data: await submitAdapter(body) }
        : await api.post(
            `/admin/psb-stage1b/${s1RunId}/create-product`, body
          );
      clearDraftOnSuccess();   // durable decision saved; remove unsaved draft
      onSuccess(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && Array.isArray(detail.candidates)) {
        setDupCandidates(detail.candidates);
        setError(detail.message);
      } else {
        setError(typeof detail === "string" ? detail : (detail?.message || (detail ? JSON.stringify(detail) : null) || err.message || "Unknown error"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Prefill badge for name field
  const namePrefillBadge = pf.proposed_name
    ? <AIPrefillBadge provenance="owner-confirmed"/>
    : ai.suggested_name ? <AIPrefillBadge provenance="AI-suggested"/> : null;

  // ── Accept AI suggestions — populates form fields, never saves/maps ─────────
  const handleAcceptAI = () => {
    const pref = aiPrefill?.prefill || {};
    // Resolve taxonomy BEFORE setForm — bakes in the current categoryOptions snapshot
    // so the setForm updater never closes over a stale array reference.
    // This is the AI-resolved path; owner-hint override is computed inside setForm.
    const resolvedTaxonomy = resolveAITaxonomy(pref, categoryOptions);

    setForm(f => {
      const updates = { ...f };
      if (!pf.proposed_name && pref.suggested_name) {
        // Apply AI suggested name if:
        //  (a) the field is empty, OR
        //  (b) it still holds the unchanged legacy description (auto-populated at open)
        // Actual user edits (any value that differs from the legacy default) are preserved.
        const legacyDefault = (row.source_description || "").trim();
        const nameIsDefaultOrEmpty = !f.product_name?.trim() || f.product_name.trim() === legacyDefault;
        if (nameIsDefaultOrEmpty) {
          updates.product_name = pref.suggested_name;
        }
      }

      // Brand: only if not already set
      if (pref.brand && !f.brand?.trim()) {
        updates.brand = pref.brand;
      }

      // Category / subcategory — safe merge logic to prevent loss across
      // AI acceptance, manual edits, and re-apply:
      //
      //  1. If user has NOT set category_id → apply both category + subcategory
      //     from AI/owner-confirmed taxonomy (normal first-apply).
      //  2. If user HAS set category_id AND the resolved taxonomy matches that
      //     same category → fill in subcategory only if it is still missing
      //     (e.g. user picked category manually but not subcategory yet).
      //  3. If user HAS set category_id AND resolved taxonomy is a DIFFERENT
      //     category → do NOT overwrite user's choice; their manual selection
      //     takes precedence over the AI suggestion.
      //
      // "owner-confirmed" taxonomy (pf.category from _OWNER_PREFILLS) is used
      // as primary source when present; falls back to AI-returned pref.category.
      const ownerHintPref = (pf.category)
        ? { ...pref, category: pf.category, subcategory: pf.subcategory || pref.subcategory }
        : pref;
      const resolvedTaxonomyFull = resolveAITaxonomy(ownerHintPref, categoryOptions);
      const useTaxonomy = resolvedTaxonomyFull || resolvedTaxonomy;

      if (useTaxonomy) {
        const userSetCategory = !!f.category_id;
        const taxonomyMatchesUser = useTaxonomy.categoryId === f.category_id;

        if (!userSetCategory) {
          // No category set yet — apply both
          updates.category_id    = useTaxonomy.categoryId;
          updates.category       = useTaxonomy.category;
          updates.subcategory    = useTaxonomy.subcategoryName;
          updates.subcategory_id = useTaxonomy.subcategoryId;
        } else if (taxonomyMatchesUser && !f.subcategory_id) {
          // Same category, subcategory missing → fill subcategory only
          updates.subcategory    = useTaxonomy.subcategoryName;
          updates.subcategory_id = useTaxonomy.subcategoryId;
        }
        // Else: different category or subcategory already set → no overwrite

        // When AI named a subcategory that doesn't exist in the catalogue,
        // store the raw suggestion as _aiSuggestedSubcategory so the form
        // can show an explicit "unresolved" hint to the user (regression:
        // Serenello 0% and FENT. GINGER BEER both showed empty subcategory
        // with no indication of what the AI had proposed).
        if (useTaxonomy.aiSuggestedSubcategory && !useTaxonomy.subcategoryId) {
          updates._aiSuggestedSubcategory = useTaxonomy.aiSuggestedSubcategory;
        } else {
          updates._aiSuggestedSubcategory = null; // resolved — clear any stale hint
        }
      }

      // Req 5: build reverse ml→standard-key map so we snap to an existing
      // catalog measure before falling back to a raw AI label (no duplicate UOMs).
      const mlToStdKey = {};
      Object.entries(dynamicUnitMlMap).forEach(([k, v]) => {
        if (!mlToStdKey[v]) mlToStdKey[v] = k;
      });

      // Citations and confidence: defined early so all guards below can use them.
      // Requirement: a citation must substantiate the specific measure/pack detail —
      // without evidence, don't silently commit the AI's assumption (e.g. "Can 125ml"
      // for a wine alternative, or "750ml bottle" for an uncited product).
      const citations = aiPrefill?.citations || pref.supporting_evidence || [];
      const bpcConf   = pref.confidence?.bottles_per_case ?? 0;

      // Purchase unit (first row): only if not already pre-filled by owner.
      // Require citations before auto-applying the AI's pack_ml so we don't
      // invent "750ml bottle" for a product with no substantiating evidence.
      let currentUnits = [...f.units];
      if (!pf.pack_confirmed && pref.pack_ml && currentUnits.length > 0 && !currentUnits[0].conversion_to_base
          && citations.length > 0) {
        // Snap to nearest standard bottle size if within 5 % (Wave 1 Item 7)
        const snappedPackMl = snapToStandardBottleMl(parseFloat(pref.pack_ml));
        // Set the measure (ml) only — never auto-apply the container type.
        // Citations substantiate the size (e.g. "125ml serving") but NOT the
        // container label (can vs glass vs bottle). User confirms type manually.
        currentUnits[0] = {
          ...currentUnits[0],
          conversion_to_base: String(snappedPackMl),
          // unit_type: intentionally not touched here — see above.
        };
      }

      // Task 7 (Wave 1): if AI identified a case/outer purchase unit (bottles_per_case > 1),
      // add a separate 'case' purchase unit with the correct total ml.
      //
      // Product unit policy (Ammar):
      //   - Beer / soft drinks / cider / mixers: case IS the standard purchase unit → default.
      //   - Spirits / wine / liqueurs: bottle is the normal purchase default; case is available
      //     in the list but must NOT be forced as the default.
      //   - Preserve any explicit user-selected override (is_default_purchase_unit already set).
      const _resolvedCatSlug = (useTaxonomy?.category || f.category || '').toLowerCase();
      const _rawCatLc        = (pref.category || '').toLowerCase();
      const _isNonCaseCategory = (
        ['spirits', 'wine'].includes(_resolvedCatSlug) ||
        _rawCatLc.includes('spirit') || _rawCatLc.includes('wine') ||
        _rawCatLc.includes('liqueur') || _rawCatLc.includes('whisky') ||
        _rawCatLc.includes('whiskey') || _rawCatLc.includes('gin') ||
        _rawCatLc.includes('vodka') || _rawCatLc.includes('rum') ||
        _rawCatLc.includes('brandy') || _rawCatLc.includes('cognac') ||
        _rawCatLc.includes('tequila') || _rawCatLc.includes('limoncello')
      );
      if (!pf.pack_confirmed && pref.pack_ml && pref.bottles_per_case && pref.bottles_per_case > 1
        && citations.length > 0 && bpcConf >= 0.8) {
        const bottleMl    = snapToStandardBottleMl(parseFloat(pref.pack_ml));
        const caseTotalMl = bottleMl * pref.bottles_per_case;
        const packLabel   = (pref.pack_type || pref.pack_label || 'bottle').toLowerCase().replace(/\s+/g,'_');
        // Req 5: snap bottle unit to catalog standard if available
        const stdBottleKey = mlToStdKey[bottleMl] || packLabel;
        // Ensure non-case bottle rows lose their purchase flag and get a
        // normalised unit_type. Case/outer units are EXCLUDED from the map
        // so a re-Apply (e.g. after editing the product name) cannot
        // corrupt their unit_type or clear their is_default_purchase_unit.
        // (reapply-regression fix: wave1-phaseb-candidate+1)
        currentUnits = currentUnits.map(u => {
          if (u.unit_type === 'case' || u.unit_type === 'outer') return u;
          // Selling-only rows must not have their unit_type snapped to a purchase-bottle
          // catalog key — that produces "Bottle 700ml" labels on a 25ml shot row.
          if (u.is_default_sales_unit || u.is_sales_unit) return u;
          return {
            ...u,
            // Spirits/wine/liqueurs: bottle is the purchase default — preserve the
            // user's explicit choice.  Beer/mixers/cider: case takes the default,
            // so clear the bottle's flag (case row will have it set below).
            is_default_purchase_unit: _isNonCaseCategory ? u.is_default_purchase_unit : false,
            unit_type: (u.unit_type && dynamicUnitTypes.includes(u.unit_type))
              ? u.unit_type                      // already a valid catalog key → keep
              : (stdBottleKey || u.unit_type),   // snap to catalog; raw AI label as last resort
            conversion_to_base: !u.conversion_to_base && pref.pack_ml ? String(bottleMl) : u.conversion_to_base,
          };
        });
        // Add a case unit if none already exists; restore its purchase flag
        // if one was already present (re-Apply path).
        const existingCaseIdx = currentUnits.findIndex(
          u => u.unit_type === 'case' || u.unit_type === 'outer'
        );
        if (existingCaseIdx === -1) {
          // First-apply: add Case ONLY for packaged beers, soft drinks, ciders,
          // and mixers (i.e. NOT spirits / wine / liqueurs).
          //
          // Business rule (Ammar 16 Oct): spirits are not normally classified as
          // cases — no Case measure is auto-populated for spirits/wine/liqueurs.
          // Case volume = units_per_case × individual_ml (supported values only).
          //
          // Packaged beers / soft drinks / ciders / mixers:
          //   is_default_purchase_unit = true  (case IS the purchase default)
          //   is_default_sales_unit    = false (individual can/bottle stays sales default)
          //
          // Spirits / wine / liqueurs: no auto-Case.  A user-manually-added Case
          // row is preserved through the existingCaseIdx !== -1 path below.
          if (!_isNonCaseCategory) {
            currentUnits.push({
              ...emptyUnit(),
              unit_type:                'case',
              conversion_to_base:       String(caseTotalMl),
              base_unit:                'ml',
              is_default_purchase_unit: true,   // case is purchase default for beer/soft drinks
              is_default_sales_unit:    false,  // individual can/bottle remains sales/count default
              _units_per_case:          String(pref.bottles_per_case),
              _indiv_ml:                String(pref.pack_ml),
            });
          }
          // Spirits/wine/liqueurs: no case row auto-added (Ammar 16 Oct).
        } else {
          // Re-apply — preserve user-edited helper fields; recompute the total
          // from whatever the user has set, so 6×700=4200 and 12×700=8400.
          // Do NOT force-set is_default_purchase_unit (preserve purchase choice).
          const existingCase = currentUnits[existingCaseIdx];
          const retainedUpc = (existingCase._units_per_case && existingCase._units_per_case !== '')
            ? existingCase._units_per_case
            : String(pref.bottles_per_case);
          const retainedMl  = (existingCase._indiv_ml && existingCase._indiv_ml !== '')
            ? existingCase._indiv_ml
            : String(pref.pack_ml);
          const retainedTotal = String(Math.round(Number(retainedUpc) * Number(retainedMl)));
          currentUnits[existingCaseIdx] = {
            ...existingCase,
            conversion_to_base: retainedTotal,  // recomputed from retained factors
            _units_per_case:    retainedUpc,    // preserved (not overwritten by AI)
            _indiv_ml:          retainedMl,     // preserved (not overwritten by AI)
            // is_default_purchase_unit: intentionally not touched — preserve user choice
          };
        }
      }

      // Pass P5 — Snap purchase unit type to catalog key.
      // When the AI's pack_label is a generic label ("bottle", "can") not in
      // the org's named-measure catalog ("Bottle 200ml", "Can 558ml" etc.),
      // the unit_type initialised from pack_label shows as Custom after measures
      // load.  Fix: find the catalog key for the already-established ml value.
      // No citations guard — we label an already-determined size, not invent one.
      // Only replaces non-catalog or blank types; preserves explicit user edits.
      //
      // Also: when pack_ml is known but the ml hasn't been applied yet (e.g. the
      // citations guard on lines above prevented it), set it here so the snap can
      // fire. Setting a known ml is labelling — it does not invent a new size.
      if (currentUnits.length > 0 && !pf.pack_confirmed) {
        const pu      = currentUnits[0];
        let   puMl    = parseFloat(pu.conversion_to_base);
        // If ml not yet applied but AI pack_ml is known, seed it now (snap pass only).
        if ((!puMl || puMl <= 0) && pref.pack_ml) {
          puMl = snapToStandardBottleMl(parseFloat(pref.pack_ml));
          currentUnits[0] = { ...currentUnits[0], conversion_to_base: String(puMl) };
        }
        const notInCatalog = currentUnits[0].unit_type && !dynamicUnitTypes.includes(currentUnits[0].unit_type);
        if ((notInCatalog || !currentUnits[0].unit_type) && puMl > 0) {
          const packHint = (pref.pack_type || pref.pack_label || '').toLowerCase();
          const snapped  = _snapUnitType(puMl, packHint, _resolvedCatSlug, dynamicUnitMlMap, dynamicUnitTypes);
          if (snapped) currentUnits[0] = { ...currentUnits[0], unit_type: snapped };
        }
      }

      // Selling unit: add if AI suggests a UK measure and none exists yet.
      // De-duplicate: if an existing non-case unit already has the SAME ml as the
      // proposed selling measure (e.g. individual bottle 330ml = selling unit 330ml),
      // add Sales roles to that row instead of creating a second "One Bottle 330ml".
      if (!pf.selling_confirmed && pref.uk_selling_measure_ml) {
        const hasSalesUnit = currentUnits.some(u => u.is_default_sales_unit || u.is_sales_unit);
        if (!hasSalesUnit) {
          const sellMl = parseFloat(pref.uk_selling_measure_ml);
          // Find an existing non-case unit whose ml matches the selling measure (within 1ml).
          const matchIdx = currentUnits.findIndex(
            u => u.conversion_to_base
              && Math.abs(parseFloat(u.conversion_to_base) - sellMl) < 1
              && u.unit_type !== 'case'
              && u.unit_type !== 'outer'
              && !u.is_default_sales_unit
              && !u.is_sales_unit
          );
          if (matchIdx !== -1) {
            // Merge: add Sales roles onto the existing row (e.g. bottle 330ml
            // carries Count + Purchase + Sales + DefaultSales — no duplicate row).
            currentUnits[matchIdx] = {
              ...currentUnits[matchIdx],
              is_sales_unit:         true,
              is_default_sales_unit: true,
            };
          } else {
            // Add a separate selling unit row (ml differs from purchase unit).
            const sellLabel = (pref.uk_selling_measure_label || '').toLowerCase().replace(/\s+/g,'_') || 'measure';
            // Snap to catalog: try to find the correct named measure using the AI's
            // sell label and the resolved category as disambiguation hints
            // (e.g. "wine" prefers wine_125ml over can_125ml for a 125ml wine serving).
            // Falls back to blank + amber hint when ambiguous — never silently guesses.
            // Pack/serve assumptions remain uncertain; case size is never auto-chosen.
            const snappedSellType = _snapUnitType(
              sellMl, sellLabel, _resolvedCatSlug, dynamicUnitMlMap, dynamicUnitTypes
            );
            currentUnits.push({
              ...emptyUnit(),
              unit_type:              snappedSellType || '',
              _ai_unit_label:         snappedSellType ? null : (sellLabel || null),
              conversion_to_base:     String(pref.uk_selling_measure_ml),
              is_sales_unit:          true,
              is_default_sales_unit:  true,
            });
          }
        }
      }
      updates.units = currentUnits;

      // Append supporting evidence / citations to source_evidence
      // (citations already defined above for the bottles_per_case guard)
      if (citations.length > 0) {
        const citText = `AI citations: ${citations.join('; ')}`;
        if (!f.source_evidence.includes(citText)) {
          updates.source_evidence = f.source_evidence
            ? `${f.source_evidence} | ${citText}`
            : citText;
        }
      }

      return updates;
    });
  };

  // Auto-apply AI when modal was opened via 1-click "AI complete product form" button
  // Fires once when: autoApplyAI=true + prefill data ready + categories loaded + measures settled.
  // Waiting for measuresAttempted ensures dynamicUnitMlMap is populated (or has failed) so that
  // unit-type snapping works correctly (Bottle 700ml / Case / Shot 25ml — no Custom bottle).
  // resolveAITaxonomy is called OUTSIDE setForm — taxonomy resolution baked in at call-time.
  // autoAppliedRef guards against double-firing when any dependency changes again.
  useEffect(() => {
    if (autoApplyAI || autoAppliedRef.current || isHardBlocked) return;
    if (!pf.category) return;         // no owner-confirmed taxonomy for this item
    if (categoryOptions.length === 0) return;  // taxonomy not loaded yet
    // Apply owner-confirmed taxonomy from _OWNER_PREFILLS without AI
    setForm(f => {
      if (f.category_id) return f;    // user already set — don't overwrite
      const ownerTax = resolveAITaxonomy(
        { category: pf.category, subcategory: pf.subcategory || '' },
        categoryOptions
      );
      if (!ownerTax) return f;
      return {
        ...f,
        category_id:    ownerTax.categoryId,
        category:       ownerTax.category,
        subcategory_id: ownerTax.subcategoryId,
        subcategory:    ownerTax.subcategoryName,
      };
    });
  }, [categoryOptions, autoApplyAI, isHardBlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoApplyAI || autoAppliedRef.current || isHardBlocked) return;
    if (!aiPrefill?.prefill) return;
    if (categoryOptions.length === 0) return;  // wait for taxonomy to load
    if (!measuresAttempted) return;             // wait for measure-definitions (success or error)
    autoAppliedRef.current = true;
    handleAcceptAI();
  }, [autoApplyAI, aiPrefill, categoryOptions, measuresAttempted]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-product-modal" data-form-category-id={form.category_id || ''} data-form-subcategory-id={form.subcategory_id || ''}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">

        {/* ── Header ── */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Add product</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              ic={row.itemcode} · {row.source_description} · Group {row.group_code}
              {pf.classification_hint && <span className="ml-1 text-indigo-500">· {pf.classification_hint}</span>}
              {pf.provisional && <span className="ml-1 text-amber-600 font-medium">· provisional</span>}
              <span className="ml-1 text-slate-400">· Preview only</span>
            </p>
          </div>
          <button
            onClick={() => { discardDraft(); onCancel(); }}
            className="text-slate-400 hover:text-slate-700"
            data-testid="new-product-close-btn"
          >
            <XCircle size={18}/>
          </button>
        </div>

        {/* ── AI loading / prefill banner ── */}
        {aiPrefillLoading && (
          <div className="mx-5 mt-3 flex items-center gap-2 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800" data-testid="ai-prefill-loading">
            <Sparkles size={12} className="animate-pulse shrink-0"/> Fetching AI field suggestions…
          </div>
        )}
        {!aiPrefillLoading && aiPrefill && !aiPrefill.enabled && (
          <div className="mx-5 mt-3 flex items-center gap-1.5 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="ai-prefill-disabled">
            <Sparkles size={11}/> AI assistance disabled (PSB_AI_SUGGESTIONS not set)
          </div>
        )}

        {/* ── Draft recovery banner ──────────────────────────────────────── */}
        {/* Shown when an unsaved form draft exists in localStorage for this
            mc_id + run.  User must explicitly click Restore — never auto-
            populated.  "Unsaved draft" is clearly distinct from a durable
            DB decision (produced by successful submit). */}
        {draftBannerOpen && (
          <div className="mx-5 mt-3 flex items-center justify-between gap-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs"
               data-testid="draft-recovery-banner">
            <span className="text-amber-800 font-medium flex items-center gap-1.5">
              <AlertTriangle size={11} className="flex-shrink-0"/>
              Unsaved draft found for this row
              <span className="text-amber-600 font-normal">— not yet saved to the database</span>
            </span>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={restoreDraft}
                className="px-2.5 py-1 bg-amber-600 text-white rounded text-xs hover:bg-amber-700"
                data-testid="draft-restore-btn"
              >
                Restore
              </button>
              <button
                onClick={discardDraft}
                className="px-2.5 py-1 border border-amber-300 text-amber-700 rounded text-xs hover:bg-amber-100"
                data-testid="draft-discard-btn"
              >
                Discard
              </button>
            </div>
          </div>
        )}
        {!aiPrefillLoading && aiPrefill?.enabled && !(pf.pack_confirmed && pf.selling_confirmed) && (
          <div className="mx-5 mt-3 rounded border border-violet-200 bg-violet-50 px-3 py-2.5 text-xs" data-testid="ai-prefill-advisory">
            {/* Header */}
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5 font-semibold text-violet-700">
                <Sparkles size={11}/> AI SOP profile <AIPrefillBadge provenance="AI-suggested"/>
                {autoApplyAI && autoAppliedRef.current && (
                  <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-semibold">auto-applied</span>
                )}
              </div>
              <button
                type="button"
                onClick={handleAcceptAI}
                data-testid="ai-accept-suggestions-btn"
                disabled={categoryOptions.length === 0 || !measuresAttempted}
                className="px-2.5 py-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded text-xs font-semibold flex items-center gap-1"
              >
                <CheckCircle2 size={11}/> Apply AI suggestions
              </button>
            </div>
            {/* Per-field suggestions — full SOP profile */}
            {aiPrefill.prefill && (() => {
              const pref = aiPrefill.prefill;
              const conf = pref.confidence || {};
              const confColor = (c) => c === 'high' ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : c === 'medium' ? 'text-amber-700 bg-amber-50 border-amber-200' : c === 'low' ? 'text-red-700 bg-red-50 border-red-200' : '';
              const confBadge = (c) => c ? (
                <span className={`ml-1 px-1 py-0.5 rounded border text-[10px] font-semibold ${confColor(c)}`}>{c}</span>
              ) : null;
              const fields = [
                pref.suggested_name           && { label: 'Name',           value: pref.suggested_name,                              conf: conf.suggested_name },
                pref.brand                    && { label: 'Brand',          value: pref.brand,                                        conf: conf.brand },
                pref.is_alcoholic !== undefined && pref.is_alcoholic !== null && {
                  label: 'Alcoholic',
                  value: pref.is_alcoholic ? 'Yes' : 'No — non-alcoholic',
                  conf:  conf.is_alcoholic,
                },
                pref.category                 && { label: 'Category',       value: pref.subcategory ? `${pref.category} → ${pref.subcategory}` : pref.category, conf: conf.category },
                pref.pack_ml                  && { label: 'Pack volume',    value: `${pref.pack_ml}ml${pref.pack_type ? ` (${pref.pack_type})` : ''}`, conf: conf.pack_ml },
                pref.bottles_per_case         && { label: 'Bottles/case',   value: String(pref.bottles_per_case),                     conf: conf.bottles_per_case },
                pref.uk_selling_measure_ml    && { label: 'UK selling',     value: `${pref.uk_selling_measure_ml}ml${pref.uk_selling_measure_label ? ` — ${pref.uk_selling_measure_label}` : ''}`, conf: conf.uk_selling_measure_ml },
                pref.yield_servings           && { label: 'Yield/servings', value: String(pref.yield_servings),                       conf: conf.yield_servings },
              ].filter(Boolean);
              return (
                <div className="space-y-1 mb-2">
                  {fields.map((fld, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-28 shrink-0 text-violet-600">{fld.label}</span>
                      <span className="font-medium text-violet-900">{fld.value}</span>
                      {confBadge(fld.conf)}
                    </div>
                  ))}
                </div>
              );
            })()}
            {/* Advisory text */}
            {aiPrefill.advisory && <p className="text-violet-800 mb-1.5">{aiPrefill.advisory}</p>}
            {/* Supporting evidence / citations */}
            {(aiPrefill.citations?.length > 0 || aiPrefill.prefill?.supporting_evidence?.length > 0) && (
              <div className="border-t border-violet-200 pt-1.5 mt-1.5">
                <div className="font-medium text-violet-600 mb-0.5">Supporting evidence</div>
                <ul className="space-y-0.5">
                  {(aiPrefill.citations?.length > 0 ? aiPrefill.citations : aiPrefill.prefill.supporting_evidence).map((c, i) => (
                    <li key={i} className="text-violet-700 flex items-start gap-1">
                      <span className="opacity-50 shrink-0">·</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Uncertainty notes */}
            {aiPrefill.prefill?.uncertainty_notes && Object.values(aiPrefill.prefill.uncertainty_notes).some(Boolean) && (
              <div className="border-t border-violet-200 pt-1.5 mt-1.5 space-y-0.5">
                {aiPrefill.prefill.uncertainty_notes.taxonomy && (
                  <p className="text-amber-700 text-[11px]"><span className="font-semibold">Taxonomy:</span> {aiPrefill.prefill.uncertainty_notes.taxonomy}</p>
                )}
                {aiPrefill.prefill.uncertainty_notes.pack && (
                  <p className="text-amber-700 text-[11px]"><span className="font-semibold">Pack:</span> {aiPrefill.prefill.uncertainty_notes.pack}</p>
                )}
                {aiPrefill.prefill.uncertainty_notes.serving && (
                  <p className="text-amber-700 text-[11px]"><span className="font-semibold">Serving:</span> {aiPrefill.prefill.uncertainty_notes.serving}</p>
                )}
              </div>
            )}
            <p className="mt-1.5 text-violet-500 italic">Advisory only — "Apply" populates the form for your review. Does not save or map anything.</p>
          </div>
        )}

        {/* ── Taxonomy uncertainty warning (critical: non-alcoholic mis-classification guard) ── */}
        {!aiPrefillLoading && aiPrefill?.prefill?.taxonomy_uncertainty && (
          <div className="mx-5 mt-2 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900" data-testid="taxonomy-uncertainty-warning">
            <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-600"/>
            <div>
              <div className="font-semibold mb-0.5">Taxonomy uncertainty</div>
              <p>{aiPrefill.prefill.taxonomy_uncertainty}</p>
            </div>
          </div>
        )}

        {/* ── Hard-block notices ── */}
        {isG19Hold && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded border border-orange-300 bg-orange-50 px-3 py-2.5 text-xs text-orange-900">
            <AlertTriangle size={13} className="shrink-0 mt-0.5 text-orange-600"/>
            <span><strong>Stage 6C bridge hold (Group 19):</strong> S4 PLU revenue = £{row.s4_plu_revenue?.toFixed(2) ?? "?"}.
            Cannot create a new product master until the ALLOFILE bridge is confirmed.</span>
          </div>
        )}

        {!isHardBlocked && (
          <form
            onSubmit={handleSubmit}
            // Only an explicit Create click may persist — block implicit Enter-to-submit from inputs.
            onKeyDown={e => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}
            className="px-5 py-4 space-y-5"
          >

            {/* ── Provisional classification notice ── */}
            {pf.provisional && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 flex items-start gap-2" data-testid="provisional-notice">
                <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-600"/>
                <span>
                  <strong>Provisional classification.</strong> Pack size and post-mix ratio/yield are
                  <strong> UNRESOLVED</strong> — define in Product Master after creation.
                  Do not infer from EPOS raw size (<code>{row.size_raw}</code>).
                </span>
              </div>
            )}

            {/* ── Source row provenance strip ── */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs space-y-2" data-testid="new-product-provenance">
              <div className="flex items-center gap-1.5 text-slate-500 font-medium mb-0.5">
                <Info size={11}/> Source row — pre-filled evidence
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600">
                <span><span className="text-slate-400">Legacy code</span> <span className="font-mono font-semibold">{row.itemcode}</span></span>
                <span><span className="text-slate-400">EPOS name</span> <span className="font-medium">{row.source_description}</span></span>
                <span><span className="text-slate-400">Group</span> {row.group_code}{row.group_name ? ` — ${row.group_name}` : ""}</span>
                <span>
                  <span className="text-slate-400">Raw size</span>{" "}
                  <span className={`font-mono ${!pf.pack_confirmed ? "text-amber-700 font-semibold" : "text-slate-700"}`}>{row.size_raw || "—"}</span>
                  {row.size_raw && !pf.pack_confirmed && <span className="text-amber-600 italic ml-1">— unit unconfirmed, use Units table below</span>}
                  {pf.pack_confirmed && <span className="text-emerald-600 italic ml-1">— see Units table (pre-filled)</span>}
                </span>
              </div>
              {pf.confirmed_note && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {pf.pack_confirmed && (
                    <ConfirmedPill label="Pack" value={`${pf.pack_qty}${pf.pack_type} (${pf.pack_label})`} />
                  )}
                  {pf.selling_confirmed && (
                    <ConfirmedPill label="Serving" value={pf.selling_label} />
                  )}
                  {!pf.pack_confirmed && <UnknownPill label="Pack size" />}
                  {!pf.selling_confirmed && !pf.provisional && <UnknownPill label="Serving/yield" />}
                  {pf.provisional && <UnknownPill label="Post-mix ratio/yield" />}
                </div>
              )}
            </div>

            {/* ── Standard BeverageStockMaster form fields ── */}
            {/* ── Measures guidance — visible inline note ───────────────────── */}
            {/* Unit types and measure sizes are organisation-wide. To configure  */}
            {/* a site-specific default serving measure after product creation,    */}
            {/* use Admin → Product Master → Site Bar Prices.                      */}
            <div
              className="flex items-start gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800"
              data-testid="measures-guidance-note"
            >
              <Info size={11} className="shrink-0 mt-0.5 text-blue-500"/>
              <span>
                Unit types and measure sizes here are <strong>global to the organisation</strong> (Admin &rarr; Measures).
                To set a different default serving measure for a specific site after this product is created,
                visit{' '}
                <a
                  href="/stocktaking/site-bar-prices"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium hover:text-blue-600"
                  data-testid="measures-site-bar-prices-link"
                >
                  Site Prices &rarr;
                </a>
              </span>
            </div>
            <AddProductFormBody
              form={form}
              setForm={setForm}
              categoryOptions={categoryOptions}
              dynamicUnitTypes={dynamicUnitTypes}
              dynamicUnitLabels={dynamicUnitLabels}
              dynamicUnitMlMap={dynamicUnitMlMap}
              namePrefillBadge={namePrefillBadge}
              showCaseSize={true}
              isCreate={true}
              showInternalNote={false}
              duplicateReview={
                <DuplicateReviewPanel
                  candidates={dupCandidates}
                  reason={dupReason}
                  onReasonChange={setDupReason}
                  onOpen={(c) => window.open(`/settings/beverage-stock-master?search=${encodeURIComponent(c.product_name)}`, '_blank', 'noopener')}
                  useHint="To use an existing product instead, cancel and map this row to it with Master Search."
                />
              }
            />

            {/* ── Identity evidence (PSB-specific, optional — source code/name are pre-filled) ── */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Identity evidence
                <span className="text-slate-400 font-normal ml-1">(optional — source code and EPOS name are pre-filled as the audit record)</span>
              </label>
              <textarea
                value={form.source_evidence} rows={3}
                onChange={e => setForm(f => ({ ...f, source_evidence: e.target.value }))}
                data-testid="new-product-source-evidence"
                className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm resize-none"
                placeholder="Optionally add how you confirmed identity — e.g. 'physical label inspection', 'supplier spec INV-2456'"
              />
              <p className="text-[11px] text-slate-400 mt-0.5">
                Actor, timestamp, legacy code, and EPOS name are always audit-recorded. Commentary is not required.
              </p>
            </div>

            {/* ── Notes ── */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Internal note (optional)</label>
              <input
                type="text"
                maxLength={500}
                value={form.notes || ""}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                data-testid="new-product-notes"
                className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm"
                placeholder="e.g. 'confirmed with supplier', 'verify bottle size with purchasing'"
              />
              <p className="text-[11px] text-slate-400 mt-0.5">Saved on the product for editors only. Never shown on reports or exports.</p>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="new-product-error">
                <AlertTriangle size={12} className="mt-0.5 shrink-0"/> {error}
              </div>
            )}

            {/* ── Owner sign-off box removed: audit actor/timestamp captured automatically ── */}

            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={loading}
                data-testid="new-product-submit"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <RefreshCw size={14} className="animate-spin"/> : <Plus size={14}/>}
                {loading ? "Creating…" : "Create Product Master"}
              </button>
              <button type="button" onClick={onCancel}
                className="px-4 py-2 border border-slate-300 rounded text-sm text-slate-600 hover:bg-slate-50"
                data-testid="new-product-cancel">
                Cancel
              </button>
            </div>
          </form>
        )}

        {isHardBlocked && (
          <div className="px-5 py-4 text-center">
            <button onClick={onCancel} className="px-4 py-2 rounded text-sm border border-slate-300 hover:bg-slate-50 text-slate-600">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Summary panel ─────────────────────────────────────────────────────────────

function SummaryPanel({ coverage, onTileClick, activeTile }) {
  if (!coverage) return null;
  const bms = coverage.by_match_status || {};
  const needsReview =
    (bms.strong_suggestion?.pending || 0) +
    (bms.review_required?.pending   || 0);
  const blocked     = bms.blocked?.pending      || 0;
  const matched     = coverage.approved_count   || 0;
  const proposed    = bms.proposed_new?.pending || 0;
  const total       = coverage.total_candidates || 0;
  const cards = [
    { label: "Need a decision",   value: needsReview, sub: "awaiting confirmation", color: "border-amber-400 bg-amber-50", textColor: "text-amber-800", activeColor: "ring-2 ring-amber-500", icon: AlertTriangle, key: "needs_review" },
    { label: "Matched",           value: matched,      sub: `of ${total} total`,     color: "border-emerald-400 bg-emerald-50", textColor: "text-emerald-800", activeColor: "ring-2 ring-emerald-500", icon: CheckCircle2, key: "matched" },
    { label: "Cannot match yet",  value: blocked,      sub: "hard-gate conflicts",   color: "border-red-400 bg-red-50",         textColor: "text-red-800",     activeColor: "ring-2 ring-red-500",     icon: Lock,         key: "blocked" },
    { label: "Create a product",  value: proposed,     sub: "no catalogue match",    color: "border-purple-400 bg-purple-50",   textColor: "text-purple-800",  activeColor: "ring-2 ring-purple-500",  icon: Zap,          key: "proposed_new" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="summary-panel">
      {cards.map(c => {
        const Icon = c.icon;
        const isActive = activeTile === c.key;
        return (
          <div
            key={c.label}
            onClick={() => onTileClick && onTileClick(c.key)}
            className={`border-l-4 rounded-lg p-3 ${c.color} ${onTileClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''} ${isActive ? c.activeColor : ''}`}
            data-testid={`summary-tile-${c.key}`}
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <Icon size={13} className={c.textColor} />
              <span className={`text-xs font-semibold ${c.textColor}`}>{c.label}</span>
            </div>
            <div className={`text-2xl font-bold ${c.textColor}`}>{c.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.sub}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Non-stock items review panel ──────────────────────────────────────────────
// Classification values are stored in DB as-is; only the display labels change.
const BRIDGE_PRE_CLASSIFICATIONS = [
  { value: "",                        label: "— select a type —" },
  { value: "management_allowance",    label: "Management allowance" },
  { value: "stock_cost_adjustment",   label: "Stock cost adjustment" },
  { value: "plu_retail_line",         label: "Retail line" },
  { value: "hybrid",                  label: "Mixed management / cost" },
  { value: "other",                   label: "Other — describe below" },
];
const BRIDGE_PRE_CLASS_LABELS = Object.fromEntries(
  BRIDGE_PRE_CLASSIFICATIONS.slice(1).map(c => [c.value, c.label])
);

// Route A = Group 19 source lines (inherited non-stock from import file).
// Route B = Reviewer-classified non-stock (explicitly routed, any group).
// reception = Reception adjustment lines (shown neutrally).
function _itemRoute(item) {
  if (item.source_instrument === "s6c_reception") return "reception";
  if (String(item.group_code) === "19") return "a";
  return "b";
}

function BridgePreviewPanel({ s1RunId }) {
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [collapsed,   setCollapsed]   = useState(false);
  const [forms,       setForms]       = useState({});
  const [openDetails, setOpenDetails] = useState({});

  const loadPreview = useCallback(async () => {
    if (!s1RunId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/psb-stage1b/${s1RunId}/bridge-preview`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load non-stock items");
    } finally {
      setLoading(false);
    }
  }, [s1RunId]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const initForm = (id) => setForms(f => ({
    ...f, [id]: f[id] || { classification: "", treatment: "", submitting: false, error: null },
  }));
  const setField = (id, field, val) => setForms(f => ({
    ...f, [id]: { ...f[id], [field]: val },
  }));

  const handleDeclare = async (item) => {
    const form = forms[item.id] || {};
    const cls  = form.classification || "";
    const txt  = (form.treatment || "").trim();
    if (!cls)            { setField(item.id, "error", "Select a type before saving."); return; }
    if (txt.length < 10) { setField(item.id, "error", "Description must be at least 10 characters."); return; }
    const treatment = `${BRIDGE_PRE_CLASS_LABELS[cls] || cls}: ${txt}`;
    setField(item.id, "submitting", true);
    setField(item.id, "error", null);
    try {
      await api.post(`/admin/psb-stage1b/${s1RunId}/bridge-preview/declare`, {
        source_identifier: item.source_identifier,
        source_instrument: item.source_instrument,
        declared_treatment: treatment,
      });
      toast.success(`Saved: ${item.description}`);
      await loadPreview();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Save failed";
      setField(item.id, "error", typeof msg === "object" ? JSON.stringify(msg) : msg);
    } finally {
      setField(item.id, "submitting", false);
    }
  };

  if (!s1RunId) return null;

  // Route A items never need reviewer action; only B/reception with requires_disposition count.
  const routeBPendingCount = data
    ? data.items.filter(i => _itemRoute(i) !== "a" && i.requires_disposition).length
    : null;
  const allReviewed = data
    ? data.items.every(i => _itemRoute(i) === "a" || !i.requires_disposition)
    : false;

  return (
    <div
      className={`rounded-xl border-2 my-4 p-4
        ${allReviewed ? "border-emerald-300 bg-emerald-50/30" : "border-amber-300 bg-amber-50/20"}`}
      data-testid="s1b-bridge-preview-panel"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer mb-1"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Shield size={15} className={allReviewed ? "text-emerald-600" : "text-amber-600"} />
          <span className={`text-sm font-bold ${allReviewed ? "text-emerald-800" : "text-amber-800"}`}>
            Review non-stock items
          </span>
          {data && (data.source_name || data.source_house) && (
            <span className="text-xs font-medium text-slate-500">
              {`${data.source_house || ""} ${data.source_name || ""}`.trim()}
            </span>
          )}
          {routeBPendingCount !== null && (
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-full border
                ${routeBPendingCount === 0
                  ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                  : "bg-amber-100 text-amber-700 border-amber-200"}`}
              data-testid="bridge-preview-pending-count"
            >
              {routeBPendingCount === 0 ? "All reviewed" : `${routeBPendingCount} to review`}
            </span>
          )}
        </div>
        {collapsed
          ? <ChevronDown size={14} className="text-slate-400 shrink-0" />
          : <ChevronUp   size={14} className="text-slate-400 shrink-0" />}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-1 ml-5"
          data-testid="bridge-preview-loading">
          <RefreshCw size={11} className="animate-spin" /> Loading...
        </div>
      )}

      {!loading && !collapsed && data && (
        <div className="space-y-3 ml-1 mt-2" data-testid="bridge-preview-item-list">
          {data.items.length === 0 && (
            <p className="text-xs text-slate-500 italic ml-4">
              No non-stock items found for this run.
            </p>
          )}

          {data.items.map(item => {
            const route      = _itemRoute(item);
            const detailOpen = openDetails[item.id] ?? false;

            const routeLabel =
              route === "a"   ? "Reference only — inherited non-stock from import file" :
              route === "b"   ? "Non-stock — awaiting or completed treatment record"    :
                                "Reception adjustment";

            const whatNext =
              route === "a"
                ? "Reference only — this item came from the import file as a non-stock line. It is not included in opening stock and no action is required here."
                : route === "b"
                  ? (needsAction
                      ? "This item has been classified as non-stock. It will not enter opening stock — please record the treatment type and description below to complete this review."
                      : "Treatment recorded. This item will be kept in the audit record for reference and will not form part of opening stock.")
                  : "This adjustment will be reflected in the audit record.";

            const hasDeclaration = !item.requires_disposition && item.declared_treatment;
            const needsAction    = route !== "a" && item.requires_disposition;

            return (
              <div
                key={item.id}
                className={`rounded-lg border bg-white
                  ${needsAction ? "border-amber-200" : "border-slate-200"}`}
                data-testid={`bridge-preview-item-${item.source_identifier}`}
              >
                {/* Identity */}
                <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 leading-tight">
                      {item.description}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{routeLabel}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded border
                    ${needsAction
                      ? "bg-amber-100 text-amber-700 border-amber-200"
                      : "bg-emerald-100 text-emerald-700 border-emerald-200"}`}>
                    {needsAction
                      ? "Needs review"
                      : route === "a" ? "No action needed" : "Reviewed"}
                  </span>
                </div>

                {/* What happens next */}
                <div className="mx-3 mb-2 px-2.5 py-2 bg-slate-50 rounded text-[11px] text-slate-600 leading-snug">
                  {whatNext}
                </div>

                {/* Existing declaration */}
                {hasDeclaration && (
                  <div
                    className="mx-3 mb-2 rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1.5"
                    data-testid={`bridge-declared-${item.source_identifier}`}
                  >
                    <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-0.5">
                      Treatment recorded
                    </p>
                    <p className="text-xs text-slate-700">{item.declared_treatment}</p>
                    {item.declared_at && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {new Date(item.declared_at).toLocaleString("en-GB")}
                      </p>
                    )}
                  </div>
                )}

                {/* Treatment form — Route B / reception, pending only */}
                {needsAction && (
                  <div
                    className="mx-3 mb-2 rounded border border-amber-200 bg-amber-50/40 px-3 py-2.5 space-y-2"
                    data-testid={`bridge-declare-form-${item.source_identifier}`}
                    onClick={() => initForm(item.id)}
                  >
                    <p className="text-[11px] font-semibold text-amber-800">
                      Record how this item should be treated
                    </p>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-1">
                        Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={forms[item.id]?.classification || ""}
                        onChange={e => { initForm(item.id); setField(item.id, "classification", e.target.value); }}
                        className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs bg-white focus:ring-1 focus:ring-amber-400"
                        data-testid={`bridge-cls-select-${item.source_identifier}`}
                      >
                        {BRIDGE_PRE_CLASSIFICATIONS.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-1">
                        Description <span className="text-red-500">*</span>
                        <span className="font-normal text-slate-400 ml-1">— briefly describe how this item should be handled</span>
                      </label>
                      <textarea
                        value={forms[item.id]?.treatment || ""}
                        onChange={e => { initForm(item.id); setField(item.id, "treatment", e.target.value); }}
                        rows={2}
                        placeholder="e.g. Confirmed as a management allowance — does not require a separate stock adjustment."
                        className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs font-mono bg-white focus:ring-1 focus:ring-amber-400"
                        data-testid={`bridge-treatment-${item.source_identifier}`}
                      />
                    </div>
                    {forms[item.id]?.error && (
                      <p className="text-xs text-red-700" data-testid={`bridge-pre-error-${item.source_identifier}`}>
                        {forms[item.id].error}
                      </p>
                    )}
                    <button
                      onClick={() => handleDeclare(item)}
                      disabled={forms[item.id]?.submitting}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                      data-testid={`bridge-pre-submit-${item.source_identifier}`}
                    >
                      {forms[item.id]?.submitting
                        ? <><RefreshCw size={11} className="animate-spin" /> Saving...</>
                        : "Save"}
                    </button>
                  </div>
                )}

                {/* Source details — collapsed by default */}
                <div className="border-t border-slate-100">
                  <button
                    onClick={() => setOpenDetails(d => ({ ...d, [item.id]: !d[item.id] }))}
                    className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-slate-400 hover:text-slate-600"
                    data-testid={`bridge-source-details-toggle-${item.source_identifier}`}
                  >
                    {detailOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                    {detailOpen ? "Hide source details" : "Source details"}
                  </button>
                  {detailOpen && (
                    <div className="px-3 pb-3 space-y-2" data-testid={`bridge-source-details-${item.source_identifier}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400">Source code</span>
                        <span className="text-[10px] font-mono text-slate-600">{item.source_identifier}</span>
                      </div>
                      {(item.unit_cost_gbp != null || item.bar_price_gbp != null) && (
                        <div className="grid grid-cols-2 gap-2">
                          {item.unit_cost_gbp != null && (
                            <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                              <p className="text-[10px] text-slate-400 leading-tight">Cost per unit</p>
                              <p
                                className={`text-xs font-mono font-semibold mt-0.5
                                  ${item.unit_cost_gbp < 0 ? "text-red-700" : "text-slate-700"}`}
                                data-testid={`bridge-unit-cost-${item.source_identifier}`}
                              >
                                £{Number(item.unit_cost_gbp).toFixed(4)}
                              </p>
                              <p className="text-[9px] text-slate-400 mt-0.5">from import file</p>
                            </div>
                          )}
                          {item.bar_price_gbp != null && (
                            <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                              <p className="text-[10px] text-slate-400 leading-tight">Retail price per unit</p>
                              <p
                                className={`text-xs font-mono font-semibold mt-0.5
                                  ${item.bar_price_gbp < 0 ? "text-red-700" : "text-indigo-700"}`}
                                data-testid={`bridge-bar-price-${item.source_identifier}`}
                              >
                                £{Number(item.bar_price_gbp).toFixed(2)}
                              </p>
                              <p className="text-[9px] text-slate-400 mt-0.5">from import file</p>
                            </div>
                          )}
                        </div>
                      )}
                      {item.s6c_value_gbp != null && (
                        <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                          <p className="text-[10px] text-slate-400 leading-tight">Adjustment value</p>
                          <p
                            className={`text-xs font-mono font-semibold mt-0.5
                              ${item.s6c_value_gbp < 0 ? "text-red-700" : "text-slate-700"}`}
                            data-testid={`bridge-s6c-value-${item.source_identifier}`}
                          >
                            £{Number(item.s6c_value_gbp).toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      {!collapsed && !loading && (
        <div className="mt-3 ml-1 flex items-center justify-between">
          <button
            onClick={loadPreview}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="bridge-preview-refresh"
          >
            <RefreshCw size={11} /> Refresh
          </button>
          {allReviewed && data && data.items.length > 0 && (
            <span className="text-xs font-semibold text-emerald-700">
              All {data.items.length} non-stock item{data.items.length !== 1 ? "s" : ""} reviewed
            </span>
          )}
        </div>
      )}
    </div>
  );
}


// ── Override reason presets (reuses the same codebase vocabulary) ─────────────
const OVERRIDE_REASON_PRESETS = [
  { value: "naming_convention", label: "Naming convention difference only — variant token in name, not a real variant" },
  { value: "category_label",    label: "Category labelling difference — confirmed same product" },
  { value: "brand_abbrev",      label: "Brand abbreviation / format difference — confirmed same product" },
  { value: "confirmed_match",   label: "Owner-confirmed physical match — description discrepancy only" },
  { value: "size_format",       label: "Size/volume notation (e.g. 70cl vs 700ml) — same product" },
  { value: "other",             label: "Other — describe in the text field below" },
];

// ── Per-row resolution panel ──────────────────────────────────────────────────

function ResolutionPanel({ row, detail, loading, onResolve, onAIAssist, onResearch, onExclude, s1RunId, siteId, onNewProductCreated, overrideState, onResolveWithOverride, onClearOverride, onRequestOverride }) {
  const [showCandidates, setShowCandidates] = useState(false);
  // Auto-expand the research section for rows that need a decision — reduces the
  // row-to-Research path from 4 clicks to 3 (no need to click "Details & research" separately).
  const [showAdvancedResearch, setShowAdvancedResearch] = useState(
    () => row?.stage1_status === 'review_required' || row?.stage1_status === 'exception_needs_decision'
  );
  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [aiLoading, setAILoading] = useState(false);
  const [researchBarcode, setResearchBarcode] = useState("");
  const [researchLoading, setResearchLoading] = useState(false);
  // Item 2: joined result from the unified research-product endpoint
  const [unifiedResearchResult, setUnifiedResearchResult] = useState(null);

  // Admin override reason — only relevant when overrideState is active for this row
  const [overridePreset, setOverridePreset] = useState("");  // quick preset
  const [overrideReason, setOverrideReason] = useState("");  // optional free-text
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  // AI prefill state — cached per open; never re-fetches unless cache is cleared
  const [aiPrefillData, setAiPrefillData] = useState(null);    // null = not fetched yet
  const [aiPrefillLoading, setAiPrefillLoading] = useState(false);
  // 1-click "AI complete product form" state
  const [aiCompleteLoading, setAiCompleteLoading] = useState(false);
  const [aiAutoApply, setAiAutoApply] = useState(false);
  // ── Route to AZ Allowances — inline confirmation before commit ──────────────
  const [routeAllowancesLoading, setRouteAllowancesLoading] = useState(false);
  const [showRouteConfirm,       setShowRouteConfirm]       = useState(false);

  const fetchAIPrefill = async () => {
    if (aiPrefillData !== null || aiPrefillLoading) return;  // already fetched or in flight
    setAiPrefillLoading(true);
    try {
      const res = await api.get(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/ai-prefill`
      );
      setAiPrefillData(res.data);
    } catch (e) {
      setAiPrefillData({ enabled: false, prefill: null, advisory: null, error: e.message });
    } finally {
      setAiPrefillLoading(false);
    }
  };

  // ── 1-click: fetch AI, then open form with all suggestions auto-applied ──────
  const handleAICompleteForm = async () => {
    setAiCompleteLoading(true);
    try {
      const res = await api.get(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/ai-prefill`
      );
      setAiPrefillData(res.data);
      setAiAutoApply(true);
      setShowNewProductModal(true);
    } catch (e) {
      setAiPrefillData({ enabled: false, prefill: null, advisory: null, error: e.message });
      setAiAutoApply(false);
      setShowNewProductModal(true);
    } finally {
      setAiCompleteLoading(false);
    }
  };

  // ── Route to AZ Allowances: confirm step then commit ────────────────────────
  const handleRouteToAllowances = async () => {
    setShowRouteConfirm(false);
    setRouteAllowancesLoading(true);
    try {
      const res = await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/route-to-allowances`
      );
      const data = res.data;
      toast.success(`Routed to AZ Allowances: ${data.description || data.itemcode} (ic=${data.itemcode})`);
      onNewProductCreated && onNewProductCreated(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRouteAllowancesLoading(false);
    }
  };

  const handleOpenNewProductModal = () => {
    setAiAutoApply(false);
    setShowNewProductModal(true);
    fetchAIPrefill();  // auto-fire; cached on first call
  };

  // ── Master search widget ──────────────────────────────────────────────────
  const [showMasterSearch, setShowMasterSearch] = useState(false);
  const [masterSearchQ, setMasterSearchQ] = useState("");
  const [masterSearchResults, setMasterSearchResults] = useState(null);
  const [masterSearchLoading, setMasterSearchLoading] = useState(false);
  const [masterSearchError, setMasterSearchError] = useState(null);
  // gateCheckState: keyed by bp_id  →  { loading, result, error }
  const [gateCheckState, setGateCheckState] = useState({});

  // ── Derived candidates — must be declared before the auto-gate-check effects ─
  const ranked     = detail?.ranked_candidates || [];
  const src        = detail?.source || {};
  const alias      = detail?.alias_registry;
  const crossSite  = detail?.cross_site;
  const research   = detail?.research_cache || [];

  // ── Auto gate-check refs — prevent duplicate fires ────────────────────────
  const autoRankedGateRef  = useRef(false);  // true once we've fired for current ranked list
  const autoSearchGateKey  = useRef(null);   // stores query string of last auto-checked search
  const autoDetailGateRef  = useRef(false);  // true once gate checks fired on detail load

  // Auto-fire gate checks as soon as detail/ranked candidates load (Wave 1 Item 4).
  // This means all five gate results are available immediately when the row is
  // expanded — the reviewer does not have to click "Review candidates" first.
  // Run automatic gate-checks for ALL top-5 ranked candidates (including blocked
  // ones).  A blocked status reflects the match engine's initial classification;
  // the gate-check result and the inline override button are what actually govern
  // whether the user can override that block.
  useEffect(() => {
    if (!detail || loading || ranked.length === 0) return;
    if (autoDetailGateRef.current) return;
    autoDetailGateRef.current = true;
    ranked.slice(0, 5).forEach(c => {
      setGateCheckState(s => {
        if (s[c.id]) return s;
        return { ...s, [c.id]: { loading: true, result: null, error: null } };
      });
      api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/gate-check`,
        { az_bp_id: c.id }
      ).then(r => {
        setGateCheckState(s => ({ ...s, [c.id]: { loading: false, result: r.data, error: null } }));
      }).catch(e => {
        setGateCheckState(s => ({ ...s, [c.id]: { loading: false, result: null, error: e.message } }));
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCandidates, loading, ranked.length]);

  // Auto-fire gate checks when master-search results appear
  useEffect(() => {
    if (!masterSearchResults?.results?.length) return;
    const qkey = masterSearchResults.query || '';
    if (autoSearchGateKey.current === qkey) return;
    autoSearchGateKey.current = qkey;
    masterSearchResults.results.filter(r => !r.inactive_warning).forEach(r => {
      setGateCheckState(s => {
        if (s[r.id]) return s;
        return { ...s, [r.id]: { loading: true, result: null, error: null } };
      });
      api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/gate-check`,
        { az_bp_id: r.id }
      ).then(res => {
        setGateCheckState(s => ({ ...s, [r.id]: { loading: false, result: res.data, error: null } }));
      }).catch(e => {
        setGateCheckState(s => ({ ...s, [r.id]: { loading: false, result: null, error: e.message } }));
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterSearchResults]);

  // ── "Use site size" — writes site_purchase_case_ml, then re-runs gate check ──
  const [usingSiteSize, setUsingSiteSize] = useState({});  // { bp_id: true|false }

  const handleUseSiteSize = async (bpId, sitePackMl) => {
    setUsingSiteSize(s => ({ ...s, [bpId]: true }));
    try {
      // 1. Write the site override (axios throws on non-2xx)
      const writeRes = await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/use-site-size`,
        { az_bp_id: bpId, site_pack_ml: sitePackMl }
      );
      const audit = writeRes.data;

      // 2. Re-run gate check with the new site effective pack (validateStatus → never throws)
      setGateCheckState(s => ({ ...s, [bpId]: { loading: true, result: null, error: null } }));
      const gcRes = await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/gate-check`,
        { az_bp_id: bpId, site_pack_ml_override: sitePackMl },
        { validateStatus: () => true }
      );
      const gcData = gcRes.data;
      setGateCheckState(s => ({ ...s, [bpId]: { loading: false, result: { ...gcData, _site_size_audit: audit }, error: null } }));
    } catch (e) {
      setGateCheckState(s => ({ ...s, [bpId]: { loading: false, result: null, error: e.message } }));
    } finally {
      setUsingSiteSize(s => ({ ...s, [bpId]: false }));
    }
  };

  const handleMasterSearch = async () => {
    if (!masterSearchQ.trim()) return;
    setMasterSearchLoading(true);
    setMasterSearchError(null);
    setMasterSearchResults(null);
    setGateCheckState({});
    try {
      const res = await api.get(
        `/admin/psb-stage1b/${s1RunId}/master-search?q=${encodeURIComponent(masterSearchQ.trim())}&limit=15`,
        { timeout: 20000 },  // 20 s — surfaces a clear error instead of spinning forever
      );
      setMasterSearchResults(res.data);
    } catch (e) {
      const msg = e.code === 'ECONNABORTED'
        ? 'Search timed out — the query may be too broad, or the server is under load. Try a more specific term.'
        : (e.response?.data?.detail || e.message || 'Search failed');
      setMasterSearchError(msg);
    } finally {
      setMasterSearchLoading(false);
    }
  };

  const handleGateCheck = async (bpId) => {
    setGateCheckState(s => ({ ...s, [bpId]: { loading: true, result: null, error: null } }));
    try {
      // validateStatus: always resolve (gate-check non-2xx = pre-gate block, not a throw)
      const res = await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/gate-check`,
        { az_bp_id: bpId },
        { validateStatus: () => true }
      );
      const data = res.data;
      const isOk = res.status >= 200 && res.status < 300;
      const result = isOk ? data : (data.detail || data);
      setGateCheckState(s => ({ ...s, [bpId]: { loading: false, result, error: isOk ? null : (result?.message || "Gate check failed") } }));
    } catch (e) {
      setGateCheckState(s => ({ ...s, [bpId]: { loading: false, result: null, error: e.message } }));
    }
  };

  const [aiConfirmPending, setAIConfirmPending] = useState(false);

  const handleAI = async () => {
    // Show pre-flight confirmation — research is NOT called automatically on expand.
    setAIConfirmPending(true);
  };
  const handleAIConfirmed = async () => {
    // Unified research action: one call → joined AI + online sections.
    // research-product already writes ai_suggestion to the DB, so we do NOT call
    // onAIAssist() here — that would make a second paid AI call and trigger
    // loadExceptions(), which can collapse this panel when the row moves pages.
    setAIConfirmPending(false);
    setAILoading(true);
    setResearchLoading(true);
    try {
      const { data } = await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/research-product`,
        {},
        { timeout: 30000 }
      );
      setUnifiedResearchResult(data.sections || []);
      // Update detailData directly from the AI section so the suggestion badge
      // reflects the new result without reloading the full exceptions list.
      const aiSec = (data.sections || []).find(s => s.source === 'AI');
      if (aiSec?.available && aiSec?.content) {
        setDetailData(prev => {
          const existing = prev[row.mc_id] || {};
          return {
            ...prev,
            [row.mc_id]: {
              ...existing,
              source: { ...(existing.source || {}), ai_suggestion: aiSec.content },
            },
          };
        });
      }
    } catch (e) {
      toast.error("Research failed: " + (e.response?.data?.detail || e.message));
      // Panel stays open — detail preserved, error shown as toast
    } finally {
      setAILoading(false);
      setResearchLoading(false);
    }
  };
  const handleAICancelled = () => setAIConfirmPending(false);

  // Issue 2: Inline override form — rendered directly under the gate-check result
  // for the specific candidate rather than in a disconnected top-of-panel window.
  const renderInlineOverride = (candidateId) => {
    if (!overrideState || overrideState.azBpId !== candidateId) return null;
    return (
      <div className="mt-2 rounded-lg border-2 border-amber-400 bg-amber-50 p-3 space-y-2.5"
        data-testid={`admin-override-panel-${row.mc_id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-600 shrink-0" />
            <span className="text-xs font-bold text-amber-800">
              {overrideState.isProposedNew && overrideState.failedGates?.length === 0
                ? "Manual assignment — confirm identity"
                : "Gate override required"}
            </span>
          </div>
          <button
            onClick={() => { onClearOverride && onClearOverride(); setOverridePreset(""); setOverrideReason(""); }}
            className="text-amber-500 hover:text-amber-700 text-xs underline shrink-0"
            data-testid={`override-dismiss-${row.mc_id}`}
          >
            dismiss
          </button>
        </div>

        <div className="text-xs text-amber-900">
          {overrideState.isProposedNew && overrideState.failedGates?.length === 0 ? (
            <p className="mb-1 text-purple-800">
              All identity gates <strong>passed</strong> for this manual selection.
              The system did not find this candidate automatically (proposed_new status).
              {" "}A system note will be recorded — you may add your own reason below for additional clarity, or leave it blank to use the auto-generated note.
            </p>
          ) : (
            <p className="mb-1">{overrideState.errorMessage}</p>
          )}
          <div className="flex flex-wrap gap-1 mt-1">
            {overrideState.failedGates.map(g => (
              <span key={g}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 border border-red-200 font-medium">
                <XCircle size={10} /> {g}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded border border-amber-300 bg-white p-2 text-[11px] text-slate-600 space-y-0.5">
          <p className="font-semibold text-slate-700">Override scope</p>
          <p>This override confirms identity only — it records that this source item maps to the selected master product. It does <strong>not</strong> establish a quantity conversion, alter pack-size values, or change the Product Master.</p>
          <p className="text-amber-700">Override is audited. Actor, timestamp, target product and failed gates are always recorded.</p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Admin override reason <span className="text-red-500">*</span>
            <span className="text-slate-400 font-normal ml-1">(required — actor, target, timestamp and failed gates are always audit-recorded)</span>
          </label>
          <select
            value={overridePreset}
            onChange={e => { setOverridePreset(e.target.value); }}
            className="w-full border border-amber-300 rounded px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 mb-2"
            data-testid={`override-preset-select-${row.mc_id}`}
          >
            <option value="">— Select a reason (required) —</option>
            {OVERRIDE_REASON_PRESETS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <textarea
            value={overrideReason}
            onChange={e => setOverrideReason(e.target.value)}
            rows={2}
            placeholder="Optional — add specific detail (e.g. 'Bells 70cl = Bell's Original 700ml — pack confirmed identical')"
            className="w-full border border-amber-300 rounded px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
            data-testid={`override-reason-textarea-${row.mc_id}`}
          />
          {(overridePreset || overrideReason.trim()) && (
            <div className="mt-1 px-2 py-1 rounded bg-amber-100 text-[10px] text-amber-800 font-mono break-all">
              Audit record: {overridePreset
                ? `${OVERRIDE_REASON_PRESETS.find(p => p.value === overridePreset)?.label || overridePreset}${overrideReason.trim() ? ' — ' + overrideReason.trim() : ''}`
                : overrideReason.trim()}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            disabled={overrideSubmitting || (
              !(overrideState.isProposedNew && overrideState.failedGates?.length === 0)
              && (!overridePreset && !overrideReason.trim())
            )}
            onClick={async () => {
              const combinedReason = overridePreset
                ? `${OVERRIDE_REASON_PRESETS.find(p => p.value === overridePreset)?.label || overridePreset}${overrideReason.trim() ? ' — ' + overrideReason.trim() : ''}`
                : overrideReason.trim() || null;
              setOverrideSubmitting(true);
              try {
                const success = await onResolveWithOverride(overrideState.action, overrideState.azBpId, combinedReason);
                if (success) { setOverridePreset(""); setOverrideReason(""); }
              } finally {
                setOverrideSubmitting(false);
              }
            }}
            data-testid={`override-submit-${row.mc_id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {overrideSubmitting ? <RefreshCw size={12} className="animate-spin" /> : <Shield size={12} />}
            {overrideState.isProposedNew && overrideState.failedGates?.length === 0
              ? "Confirm Manual Assignment"
              : "Submit with Admin Override"
            }
          </button>
          <button
            onClick={() => { onClearOverride && onClearOverride(); setOverridePreset(""); setOverrideReason(""); }}
            className="px-3 py-1.5 border border-amber-300 rounded text-xs text-amber-700 hover:bg-amber-100"
            data-testid={`override-cancel-${row.mc_id}`}
          >
            Cancel — choose different master
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-slate-50 border-t border-slate-200 px-5 py-4 space-y-4">

      {/* ── Proposed-new manual-assignment notice ─────────────────────────── */}
      {row.match_status === "proposed_new" && !overrideState && (
        <div className="rounded-lg border border-purple-300 bg-purple-50 px-3 py-2.5 flex items-start gap-2 text-xs text-purple-900"
          data-testid={`proposed-new-notice-${row.mc_id}`}>
          <Zap size={13} className="text-purple-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Automated matching did not find a candidate above threshold.</span>{" "}
            If an existing master product is the correct match, use <strong>Master Search</strong> below to locate it and select it.
            Gate results will be shown before you confirm. An override reason is required if any gate flags — system notes are generated automatically if all gates pass.
          </div>
        </div>
      )}

      {/* ── Admin gate-override panel (Issue 2: now rendered inline with gate checks) ── */}

      {/* ── Structural / bridge notices (derived from row data, no write) ──────── */}
      {row.match_status === "blocked" && row.group_code !== "14" && (() => {
        const isG19WithPLU = String(row.group_code) === "19" && Math.abs(row.plu_value_inc_vat || row.s4_plu_revenue || 0) > 0;
        if (isG19WithPLU) return (
          <div className="flex items-start gap-2 rounded border border-purple-300 bg-purple-50 px-3 py-2 text-xs text-purple-900" data-testid={`s6c-bridge-notice-${row.mc_id}`}>
            <AlertTriangle size={13} className="shrink-0 mt-0.5 text-purple-600" />
            <span>
              <span className="font-semibold">Group 19 — Non-product AZ Adjustment</span> — PLU revenue = {fmt(row.plu_value_inc_vat || row.s4_plu_revenue)}.
              This is a Management Allowance, not a physical product. Use the "Route to AZ Allowances" button to resolve without creating or mapping a product.
            </span>
          </div>
        );
        return (
          <div className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900" data-testid={`blocked-identity-notice-${row.mc_id}`}>
            <AlertTriangle size={13} className="shrink-0 mt-0.5 text-rose-500" />
            <span>
              <span className="font-semibold">Blocked identity</span> — all scored candidates fail identity gates.
              Options: search for an alternate master below, exclude with a documented reason, or hold pending a new-product investigation.
            </span>
          </div>
        );
      })()}

      {/* ── Source evidence — compact single line ──────────────────────────────── */}
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
        <span><span className="text-slate-400">Code</span> <span className="font-mono font-semibold">{row.itemcode}</span></span>
        <span><span className="text-slate-400">Group</span> <span className="font-medium">{row.group_code} — {row.group_name}</span></span>
        {row.size_raw && <span><span className="text-slate-400">Size</span> {row.size_raw}</span>}
        {src.yield_raw && <span><span className="text-slate-400">Yield</span> {src.yield_raw} sv</span>}
        <span><span className="text-slate-400">PLU</span> <strong>{fmt(row.plu_value_inc_vat)}</strong></span>
        <span><span className="text-slate-400">Open cost</span> {fmt(row.open_cost)}</span>
        {row.source_barprice && <span><span className="text-slate-400">Bar</span> {fmt(row.source_barprice)}</span>}
        {alias && (
          <span className="text-indigo-700 font-medium flex items-center gap-1">
            <CheckCircle2 size={11}/> {alias.az_product_name || `bp_id=${alias.az_product_id}`}
          </span>
        )}
        {crossSite && !crossSite.ambiguous && crossSite.az_product_id && (
          <span className="text-purple-700 font-medium flex items-center gap-1" data-testid={`cross-site-badge-${row.mc_id}`} title={`Confirmed at: ${(crossSite.source_sites || []).join(', ')}`}>
            <CheckCircle2 size={11}/>
            {crossSite.cross_site_match === 'exact_hash' ? 'Learned — ' : 'Seen at — '}
            {crossSite.az_product_name || `bp #${crossSite.az_product_id}`}
            {(crossSite.source_sites || []).length > 0 && (
              <span className="text-[10px] font-normal text-purple-500">
                ({crossSite.source_sites[0]}{crossSite.source_sites.length > 1 ? ` +${crossSite.source_sites.length - 1}` : ''})
              </span>
            )}
          </span>
        )}
        {crossSite?.ambiguous && (
          <span className="text-amber-600 text-[11px] flex items-center gap-1" data-testid={`cross-site-ambiguous-${row.mc_id}`}>
            <AlertTriangle size={11}/> Cross-site evidence ambiguous — requires manual review
          </span>
        )}
      </div>

      {/* AI suggestion */}
      {row.ai_suggestion && (
        <div className="bg-indigo-50 border border-indigo-200 rounded px-3 py-2 text-xs text-indigo-800">
          <span className="font-semibold">AI advisory (read-only):</span> {row.ai_suggestion}
          <div className="text-indigo-500 mt-0.5 italic">Suggestions are advisory only — requires human approval + gate check.</div>
        </div>
      )}

      {/* Task 4: Stored gate summary — shown immediately on expand, no extra API call needed */}
      {row.gate_summary && row.matched_bp_id && (
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 space-y-2" data-testid={`gate-summary-${row.mc_id}`}>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
            <Shield size={11} /> Last gate check — {row.matched_bp_name || `bp #${row.matched_bp_id}`}
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(row.gate_summary).map(([gate, g]) => {
              const label = gate === 'pack_size' ? 'Pack' : gate.charAt(0).toUpperCase() + gate.slice(1);
              return (
                <span key={gate}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${
                    g.passed
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}
                  title={g.reason || ''}
                  data-testid={`gate-badge-${row.mc_id}-${gate}`}
                >
                  {g.passed ? <Check size={10} /> : <X size={10} />} {label}
                </span>
              );
            })}
          </div>
          {Object.entries(row.gate_summary).some(([, g]) => !g.passed) && (
            <div className="space-y-0.5">
              {Object.entries(row.gate_summary).filter(([, g]) => !g.passed).map(([gate, g]) => (
                <div key={gate} className="text-[11px] text-red-600 flex items-start gap-1">
                  <XCircle size={10} className="shrink-0 mt-0.5" />
                  <span><strong className="capitalize">{gate}:</strong> {g.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Req 4: Decision-first — top candidate gate result shown immediately on expand */}
      {row.decision !== "approved" && row.decision !== "excluded" && row.decision !== "az_adjustment"
        && ranked.length > 0 && !showCandidates && (() => {
          const topC = ranked[0];
          const gcEntry = gateCheckState[topC.id];
          if (!gcEntry) return null; // gate check not yet started
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 space-y-1.5"
              data-testid={`top-candidate-block-${row.mc_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-700 truncate flex-1">
                  {topC.product_name}
                  {topC.product_code && <span className="text-slate-400 font-mono ml-1 font-normal text-[11px]">{topC.product_code}</span>}
                </span>
                <span className="text-[10px] text-slate-400 shrink-0">Top candidate</span>
              </div>
              {gcEntry.loading ? (
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <RefreshCw size={11} className="animate-spin" /> Gate check running…
                </div>
              ) : gcEntry.result ? (() => {
                const gc = gcEntry.result;
                const gates = gc.gates || {};
                const allPass = gc.all_pass;
                const preBlock = gc.pre_gate_block;
                const sizeOnly = gc.size_only_conflict;
                return (
                  <div className="space-y-1">
                    {preBlock ? (
                      <p className="text-xs text-orange-700">{preBlock.message}</p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(gates).map(([gate, g]) => (
                          <span key={gate}
                            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium border ${
                              g.passed ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                       : 'bg-red-50 border-red-200 text-red-700'
                            }`}
                            title={g.reason || ''}
                            data-testid={`decision-gate-${row.mc_id}-${gate}`}
                          >
                            {g.passed ? <Check size={9} /> : <X size={9} />}
                            {gate === 'pack_size' ? 'Pack' : gate.charAt(0).toUpperCase() + gate.slice(1)}
                          </span>
                        ))}
                      </div>
                    )}
                    {allPass && !preBlock && (
                      <button
                        onClick={() => onResolve("map_to", topC.id)}
                        data-testid={`decision-first-confirm-${row.mc_id}`}
                        className="mt-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold"
                      >
                        Confirm Map to {topC.product_name}
                      </button>
                    )}
                    {!allPass && !preBlock && !sizeOnly && !gc.blocked_override_unavailable && (
                      <p className="text-[11px] text-amber-700">Gate conflict — open candidates to review or use Admin Override</p>
                    )}
                  </div>
                );
              })() : null}
            </div>
          );
        })()
      }

      {/* Primary action buttons */}
      {row.decision !== "approved" && row.decision !== "excluded" && row.decision !== "az_adjustment" && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCandidates(c => !c)}
            data-testid={`show-candidates-${row.mc_id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded text-xs hover:bg-white"
          >
            <Eye size={13} /> {showCandidates ? "Hide" : "Review"} candidates ({ranked.length})
          </button>
          {/* Single "Add product" entry — opens form immediately; uses cached AI suggestions
              when available; manual entry always available; never blocks on new AI fetch;
              "Apply AI suggestions" button inside the form is explicit and user-driven. */}
          {String(row.group_code) !== "19" && row.itemcode !== "120800" && (
            <button
              onClick={handleOpenNewProductModal}
              data-testid={`add-product-btn-${row.mc_id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-indigo-300 text-indigo-700 rounded text-xs hover:bg-indigo-50 font-medium"
              title="Open the product entry form — AI suggestions load in background and can be applied or ignored"
            >
              <Plus size={13} /> Add product
              {aiPrefillLoading && <span className="text-[10px] text-violet-500 ml-1">· AI loading…</span>}
            </button>
          )}
          {/* Route to AZ Allowances — available for any pending row; confirm before commit */}
          {row.itemcode !== "120800" && (
            <button
              onClick={() => setShowRouteConfirm(c => !c)}
              disabled={routeAllowancesLoading}
              data-testid={`route-allowances-btn-${row.mc_id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-purple-400 bg-purple-50 text-purple-800 rounded text-xs hover:bg-purple-100 disabled:opacity-50 font-medium"
              title="Route this row to Management > Allowances — preserves source values, no product created"
            >
              <ArrowRight size={13}/>
              {routeAllowancesLoading ? "Routing…" : "Route to AZ Allowances"}
            </button>
          )}
          {/* Details & research toggle — also contains the advanced Exclude action */}
          <button
            onClick={() => setShowAdvancedResearch(r => !r)}
            data-testid={`advanced-research-toggle-${row.mc_id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-500 rounded text-xs hover:bg-slate-100"
            title="Source links, detailed evidence, research tools, and advanced actions"
          >
            <Settings size={12} />
            {showAdvancedResearch ? "Hide" : "Details & research"}
            {showAdvancedResearch ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
          </button>
        </div>
      )}

      {/* AZ Allowances — inline confirmation panel */}
      {showRouteConfirm && row.decision !== "az_adjustment" && (
        <div className="rounded border border-purple-300 bg-purple-50 px-3 py-2.5 text-xs text-purple-900 space-y-2" data-testid={`route-allowances-confirm-${row.mc_id}`}>
          <p className="font-semibold">Confirm: Route to AZ Allowances</p>
          <p>
            <span className="font-medium">Group {row.group_code}{row.group_name ? ` — ${row.group_name}` : ""}</span> · ic={row.itemcode} · {row.source_description}
          </p>
          <p>
            Source values preserved for audit — bar price: <span className="font-mono">{row.source_barprice != null ? `£${Number(row.source_barprice).toFixed(2)}` : "n/a"}</span>, cost: <span className="font-mono">{row.open_cost != null ? `£${Number(row.open_cost).toFixed(2)}` : "n/a"}</span>.
          </p>
          <p className="text-purple-700">
            This row will be recorded as a non-stock AZ adjustment and removed from product-matching pending. It will not enter stock calculations or create a product. This decision is final once confirmed and cannot be re-routed.
          </p>
          <div className="flex gap-2 mt-1">
            <button
              onClick={handleRouteToAllowances}
              disabled={routeAllowancesLoading}
              data-testid={`route-allowances-confirm-btn-${row.mc_id}`}
              className="px-3 py-1 bg-purple-700 text-white rounded text-xs font-semibold hover:bg-purple-800 disabled:opacity-50"
            >
              {routeAllowancesLoading ? "Routing…" : "Confirm — Route to AZ Allowances"}
            </button>
            <button
              onClick={() => setShowRouteConfirm(false)}
              data-testid={`route-allowances-cancel-btn-${row.mc_id}`}
              className="px-3 py-1 border border-purple-300 text-purple-700 rounded text-xs hover:bg-purple-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* AZ Adjustment confirmation — shown when row is routed (any group) */}
      {row.decision === "az_adjustment" && (
        <div className="flex items-start gap-2 rounded border border-purple-300 bg-purple-50 px-3 py-2.5 text-xs text-purple-900" data-testid={`az-adjustment-confirmed-${row.mc_id}`}>
          <CheckCircle2 size={13} className="shrink-0 mt-0.5 text-purple-600"/>
          <span>
            <span className="font-semibold">Routed to Management &gt; Allowances</span> — ic={row.itemcode}
            {row.group_name ? ` (${row.group_name})` : ""} recorded as non-product AZ adjustment.
            Source values preserved. No product created or mapped.
          </span>
        </div>
      )}

      {/* Site Settings panel — shown when there is a confirmed or pending match candidate.
          Appears for approved rows AND for rows where a gate check result is available.
          Allows editing site-specific pack count, unit size (ml) without touching Product Master. */}
      {row.matched_bp_id && row.decision !== "excluded" && row.decision !== "az_adjustment" && (
        <SiteSettingsPanel
          row={row}
          azBpId={row.matched_bp_id}
          s1RunId={s1RunId}
          siteId={siteId}
          srcYield={src.yield_raw}
          srcPackMl={src.pack_ml}
        />
      )}

      {/* Governed new-product modal */}
      {showNewProductModal && (
        <GoverNedNewProductModal
          row={row}
          s1RunId={s1RunId}
          siteId={siteId}
          aiPrefill={aiPrefillData}
          aiPrefillLoading={aiPrefillLoading}
          autoApplyAI={aiAutoApply}
          onSuccess={(data) => {
            setShowNewProductModal(false);
            setAiAutoApply(false);
            onNewProductCreated && onNewProductCreated(data);
            toast.success(
              `New product created: "${data.product_name}" (code: ${data.product_code || "—"})` +
              (data.created_product_id ? ` — row approved (bp_id=${data.created_product_id})` : "")
            );
          }}
          onCancel={() => { setShowNewProductModal(false); setAiAutoApply(false); }}
        />
      )}

      {/* Advanced Research — collapsed by default */}
      {showAdvancedResearch && row.decision !== "approved" && row.decision !== "excluded" && (
        <div className="border border-slate-200 rounded-lg p-3 bg-white space-y-3" data-testid={`advanced-research-panel-${row.mc_id}`}>
          <div className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1">
            <Settings size={12}/> Advanced Research
            <span className="font-normal text-slate-400 ml-1">— advisory only, never auto-maps</span>
          </div>
          {/* ── Unified Research (Item 2): one button → joined AI + online evidence ─── */}
          {/* Barcode is ONLY shown here when user explicitly pastes one (admin view) */}
          {aiConfirmPending && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-2" data-testid={`ai-confirm-dialog-${row.mc_id}`}>
              <div className="font-semibold">Confirm research request</div>
              <p>This calls the advisory AI service and an online evidence source in one pass. Results are read-only and cannot approve or map any row. Proceed?</p>
              <div className="flex gap-2">
                <button onClick={handleAIConfirmed} className="px-3 py-1 bg-amber-700 text-white rounded text-xs font-medium hover:bg-amber-800" data-testid={`ai-confirm-yes-${row.mc_id}`}>
                  Research this product
                </button>
                <button onClick={handleAICancelled} className="px-3 py-1 border border-amber-300 rounded text-xs hover:bg-amber-100" data-testid={`ai-confirm-no-${row.mc_id}`}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {!aiConfirmPending && (
            <button
              onClick={() => {
                // Unified action: trigger the joined AI + online research endpoint
                handleAI();
              }}
              disabled={aiLoading || researchLoading}
              data-testid={`research-product-btn-${row.mc_id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-600 rounded text-xs hover:bg-slate-50 disabled:opacity-50"
              title="Advisory only. Calls AI + online evidence in one step. Never auto-maps."
            >
              <FlaskConical size={13} /> {(aiLoading || researchLoading) ? "Researching…" : "Research this product"}
            </button>
          )}
          {/* Barcode field — hidden from normal review; shown only in advanced/admin context */}
          {showAdvancedResearch && researchBarcode && (
            <div className="flex items-center gap-2 mt-1">
              <input
                value={researchBarcode}
                onChange={e => setResearchBarcode(e.target.value)}
                data-testid={`research-barcode-${row.mc_id}`}
                placeholder="Barcode / GTIN"
                className="border border-slate-300 rounded px-2 py-1 text-xs w-44"
              />
              <span className="text-xs text-slate-400">Barcode search</span>
            </div>
          )}

          {/* Joined research result — source-labelled sections (Item 2) */}
          {unifiedResearchResult && unifiedResearchResult.length > 0 && (
            <div className="space-y-2 mt-2" data-testid={`unified-research-result-${row.mc_id}`}>
              {unifiedResearchResult.map((section, i) => (
                <div key={i} className={`rounded border px-3 py-2 text-xs ${section.available ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50'}`}
                  data-testid={`research-section-${section.source.toLowerCase()}-${row.mc_id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-slate-700 uppercase tracking-wide text-[10px]">
                      {section.source}
                    </span>
                    {!section.available && (
                      <span className="text-slate-400 font-normal text-[10px]">not available</span>
                    )}
                    {section.is_authoritative === false && (
                      <span className="text-amber-600 text-[10px]">community-sourced</span>
                    )}
                    {section.from_cache && (
                      <span className="text-slate-400 text-[10px]">cached</span>
                    )}
                  </div>
                  {section.available && section.content ? (
                    <div className="text-slate-600 break-words whitespace-pre-wrap">
                      {(() => {
                        const cnt = section.content;
                        // Online provider returned a "not found" result — show a
                        // user-friendly message instead of the raw error JSON.
                        if (cnt && typeof cnt === 'object' && cnt.found === false) {
                          const errStr = (cnt.error || '').toString();
                          const isServiceErr = errStr.includes('503') || errStr.includes('Service') || errStr.includes('Unavailable');
                          return (
                            <span className="text-slate-400 italic">
                              {isServiceErr
                                ? 'Provider temporarily unavailable — try again shortly.'
                                : cnt.error
                                  ? `No record found: ${errStr.slice(0, 100)}`
                                  : 'No product record found in this database.'}
                            </span>
                          );
                        }
                        return typeof cnt === 'string' ? cnt : JSON.stringify(cnt, null, 2).slice(0, 600);
                      })()}
                    </div>
                  ) : (
                    <div className="text-slate-400 italic">{section.note}</div>
                  )}
                </div>
              ))}
              <p className="text-[10px] text-slate-400 italic">
                All sections are advisory. No auto-mapping or auto-creation occurs.
              </p>
            </div>
          )}

          {/* ── Exclude — exceptional advanced action ─────────────────────────────────
              Legitimate uses: demonstrably invalid, non-product, duplicate, or corrupt rows.
              Zero stock movement alone does not qualify.
              Effect: decision=excluded; row not counted in matched coverage; does not flow to
              S2 or later stages. Run-scoped or persistent. Mandatory reason required.
              Saved exclusions and persistent governance rules are preserved as-is.          */}
          <div className="border-t border-slate-100 pt-3 mt-1 space-y-1.5" data-testid={`exclude-advanced-section-${row.mc_id}`}>
            <p className="text-[10px] text-slate-500 leading-snug">
              <span className="font-semibold text-orange-700">Exclude</span> is an admin-only exceptional action.
              Use only when a row is demonstrably invalid, non-product, a duplicate entry, or corrupt data.
              Low or zero stock movement alone does not qualify.
              Excluded rows are recorded as workflow-resolved but are <span className="font-medium">not counted in matched coverage</span> and do not enter stock calculations.
            </p>
            <button
              onClick={onExclude}
              data-testid={`exclude-btn-${row.mc_id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-orange-300 text-orange-700 rounded text-xs hover:bg-orange-50"
              title="Admin-only: exclude only for demonstrably invalid/non-product/duplicate/corrupt rows. Requires a reason."
            >
              <Ban size={13} /> Exclude (admin only)
            </button>
          </div>
        </div>
      )}

      {row.decision === "excluded" && (
        <div className="bg-orange-50 border border-orange-200 rounded px-3 py-2 text-xs text-orange-800">
          <span className="font-semibold">Excluded:</span> {row.exclusion_reason || "No reason recorded"}
          {row.exclusion_scope === "persistent" && <span className="ml-2 font-bold">(Persistent governance rule)</span>}
        </div>
      )}

      {/* Master search — find a master by ID/code/name when absent from scored list */}
      {row.decision !== "approved" && row.decision !== "excluded" && (
        <div className="border border-slate-200 rounded-lg bg-white" data-testid={`master-search-section-${row.mc_id}`}>
          <button
            onClick={() => setShowMasterSearch(s => !s)}
            data-testid={`master-search-toggle-${row.mc_id}`}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg"
          >
            <span className="flex items-center gap-1.5">
              <Search size={12} /> Search master by ID / code / name
              <span className="text-slate-400 font-normal">(use when correct master is absent from scored list)</span>
            </span>
            {showMasterSearch ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
          </button>

          {showMasterSearch && (
            <div className="border-t border-slate-100 px-3 pb-3 pt-2 space-y-3">
              {/* Search input */}
              <div className="flex gap-2">
                <input
                  value={masterSearchQ}
                  onChange={e => setMasterSearchQ(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleMasterSearch()}
                  placeholder="e.g. Pernod Pastis  or  330  or  80170"
                  className="border border-slate-300 rounded px-2 py-1.5 text-xs flex-1 font-mono"
                  data-testid={`master-search-input-${row.mc_id}`}
                />
                <button
                  onClick={handleMasterSearch}
                  disabled={masterSearchLoading || !masterSearchQ.trim()}
                  data-testid={`master-search-submit-${row.mc_id}`}
                  className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-800 text-white rounded text-xs disabled:opacity-50"
                >
                  <Search size={11} /> {masterSearchLoading ? "Searching…" : "Search"}
                </button>
              </div>

              {masterSearchError && (
                <p className="text-xs text-red-600" data-testid={`master-search-error-${row.mc_id}`}>{masterSearchError}</p>
              )}

              {/* Results */}
              {masterSearchResults && (
                <div data-testid={`master-search-results-${row.mc_id}`}>
                  {masterSearchResults.count === 0 ? (
                    <p className="text-xs text-slate-400 italic">No ACTIVE masters found for "{masterSearchResults.query}".</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-xs text-slate-500">{masterSearchResults.count} result{masterSearchResults.count !== 1 ? "s" : ""} — gate check runs automatically</div>
                      {masterSearchResults.results.map(r => (
                        <div
                          key={r.id}
                          data-testid={`master-search-result-${row.mc_id}-${r.id}`}
                          className={`border rounded p-2.5 text-xs ${r.inactive_warning ? "border-orange-300 bg-orange-50" : "border-slate-200 bg-slate-50"}`}
                        >
                          {/* Identity row */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-slate-800">{r.product_name}</span>
                              {r.brand && <span className="text-slate-500 ml-1">· {r.brand}</span>}
                              <span className="ml-1.5 font-mono text-slate-400">#{r.id}</span>
                              {r.product_code && <span className="ml-1 text-slate-400">code={r.product_code}</span>}
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span className={`px-1.5 py-0.5 rounded font-medium ${r.inactive_warning ? "bg-orange-200 text-orange-800" : "bg-emerald-100 text-emerald-700"}`}>
                                {r.status}
                              </span>
                              {!r.inactive_warning && !gateCheckState[r.id]?.result && !gateCheckState[r.id]?.loading && (
                                <button
                                  onClick={() => handleGateCheck(r.id)}
                                  data-testid={`gate-check-btn-${row.mc_id}-${r.id}`}
                                  className="px-2 py-1 bg-slate-600 hover:bg-slate-700 text-white rounded text-xs font-medium"
                                >
                                  Validate gates
                                </button>
                              )}
                              {!r.inactive_warning && gateCheckState[r.id]?.loading && (
                                <span className="text-[10px] text-slate-400 animate-pulse">Checking…</span>
                              )}
                            </div>
                          </div>
                          {/* Subcat / category */}
                          <div className="text-slate-500 mt-0.5">{r.subcategory_name} · {r.category_slug}{r.is_post_mix ? " · postmix" : ""}</div>
                          {/* UOM table */}
                          {r.uoms.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {r.uoms.map((u, ui) => (
                                <span
                                  key={ui}
                                  className={`px-1.5 py-0.5 rounded border text-xs font-mono
                                    ${u.role === "sales_default" ? "border-blue-300 bg-blue-50 text-blue-700" :
                                      u.role === "sales"         ? "border-blue-200 bg-blue-50 text-blue-600" :
                                      u.role === "purchase_default" ? "border-amber-300 bg-amber-50 text-amber-700" :
                                      u.role === "stock"         ? "border-slate-300 bg-white text-slate-600" :
                                      "border-slate-200 bg-white text-slate-500"}`}
                                  title={`role: ${u.role}`}
                                >
                                  {u.unit_type} {u.ml ? `= ${u.ml}ml` : ""} <span className="opacity-60">[{u.role}]</span>
                                </span>
                              ))}
                            </div>
                          )}
                          {r.inactive_warning && (
                            <p className="text-orange-700 mt-1 font-medium">Inactive — cannot be selected. Gate check will reject this target.</p>
                          )}

                          {/* ── Gate check result panel ─────────────────────── */}
                          {gateCheckState[r.id]?.error && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700" data-testid={`gate-check-error-${row.mc_id}-${r.id}`}>
                              {gateCheckState[r.id].error}
                            </div>
                          )}
                          {gateCheckState[r.id]?.result && (() => {
                            const gc = gateCheckState[r.id].result;
                            const gates = gc.gates || {};
                            const allPass = gc.all_pass;
                            const softOnly = gc.soft_conflict_only;
                            const sizeOnly = gc.size_only_conflict;
                            const preBlock = gc.pre_gate_block;
                            const alreadyApproved = gc.mc_already_approved;
                            const audit = gc._site_size_audit;
                            return (
                              <div className="mt-2 border border-slate-200 rounded bg-white p-2" data-testid={`gate-check-result-${row.mc_id}-${r.id}`}>
                                  <>
                                    {!allPass && !preBlock && (
                                      <div className="text-xs text-slate-500 mb-1.5 flex items-center gap-1">
                                        <AlertTriangle size={11} className="shrink-0 text-amber-500" />
                                        Possible candidate — identity gates have not all passed. Review the gate detail below and use Admin Override to confirm.
                                      </div>
                                    )}
                                    <div className="text-xs font-semibold text-slate-700 mb-1.5">Gate check result</div>
                                    {preBlock ? (
                                      <p className="text-xs text-orange-700 font-medium">{preBlock.message}</p>
                                    ) : (
                                      <div className="space-y-1 mb-2">
                                        {Object.entries(gates).map(([name, g]) => (
                                          <div key={name} className="flex items-start gap-1.5 text-xs">
                                            <span className={`mt-0.5 shrink-0 w-3 h-3 rounded-full ${g.passed ? "bg-emerald-400" : "bg-red-400"}`} />
                                            <span className="font-mono text-slate-500 w-20 shrink-0">{name}</span>
                                            <span className={g.passed ? "text-slate-600" : "text-red-700"}>{g.reason}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {/* UOMs from gate-check result */}
                                    {gc.uoms?.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mb-2">
                                        {gc.uoms.map((u, ui) => (
                                          <span key={ui} className={`px-1.5 py-0.5 rounded border text-xs font-mono
                                            ${u.role === "sales_default" ? "border-blue-300 bg-blue-50 text-blue-700" :
                                              u.role === "sales"         ? "border-blue-200 bg-blue-50 text-blue-600" :
                                              u.role === "purchase_default" ? "border-amber-300 bg-amber-50 text-amber-700" :
                                              "border-slate-200 bg-white text-slate-500"}`}>
                                            {u.unit_type} {u.ml ? `=${u.ml}ml` : ""} [{u.role}]
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {alreadyApproved && (
                                      <div className="mt-1 space-y-1">
                                        <p className="text-xs text-amber-700 font-medium">This row is already approved. An override note is optional — use the panel below to remap.</p>
                                        <button
                                          onClick={() => onRequestOverride(
                                            r.id,
                                            Object.keys(gates).filter(k => !gates[k].passed),
                                            `Row already approved — admin gate override to change mapping`
                                          )}
                                          data-testid={`request-override-remap-${row.mc_id}-${r.id}`}
                                          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold inline-flex items-center gap-1"
                                        >
                                          <Shield size={12} /> Override to Remap
                                        </button>
                                      </div>
                                    )}
                                    {allPass && !alreadyApproved && !preBlock && (
                                      <button
                                        onClick={() => onResolve("map_to", r.id)}
                                        data-testid={`confirm-map-btn-${row.mc_id}-${r.id}`}
                                        className="mt-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold"
                                      >
                                        Confirm Map to {gc.product_name}
                                      </button>
                                    )}
                                    {/* Site-size conflict */}
                                    {sizeOnly && !preBlock && !alreadyApproved && gc.source_pack_ml && (
                                      <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                        <div className="text-xs text-blue-800 mb-1.5">
                                          <strong>Size-only difference</strong> — this site buys {gc.source_pack_ml}ml; master records {gc.candidate_pack_ml || '?'}ml.
                                          Product Master stays unchanged.
                                        </div>
                                        {audit && (
                                          <div className="text-[10px] text-blue-600 mb-1.5 font-mono bg-blue-100 rounded px-2 py-1" data-testid={`site-size-audit-search-${row.mc_id}-${r.id}`}>
                                            Audit — recorded {audit.recorded_at?.split('T')[0]} by {audit.actor_email} | old={audit.old_pack_ml != null ? `${audit.old_pack_ml}ml` : 'none'} → new={audit.new_pack_ml}ml
                                          </div>
                                        )}
                                        {!audit && (
                                          <button
                                            onClick={() => handleUseSiteSize(r.id, gc.source_pack_ml)}
                                            disabled={usingSiteSize[r.id]}
                                            data-testid={`use-site-size-search-${row.mc_id}-${r.id}`}
                                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                                          >
                                            {usingSiteSize[r.id] ? 'Saving…' : `Use for this site (${gc.source_pack_ml}ml)`}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                    {!allPass && !gc.blocked_override_unavailable && !preBlock && !alreadyApproved && (
                                      <button
                                        onClick={() => onRequestOverride(
                                          r.id,
                                          Object.keys(gates).filter(k => !gates[k].passed),
                                          `Gate conflict on "${gc.product_name || r.product_name}" — admin gate override`,
                                          gc.is_proposed_new_manual
                                        )}
                                        data-testid={`request-override-btn-${row.mc_id}-${r.id}`}
                                        className="mt-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold inline-flex items-center gap-1"
                                      >
                                        <Shield size={12} /> Admin Override — submit with reason
                                      </button>
                                    )}
                                    {!allPass && gc.blocked_override_unavailable && !preBlock && !sizeOnly && (
                                      <span
                                        className="mt-1 inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200"
                                        data-testid={`override-unavailable-${row.mc_id}-${r.id}`}
                                      >
                                        <Lock size={11} /> Override not permitted — hard physical conflict
                                      </span>
                                    )}
                                    <button
                                      onClick={() => setGateCheckState(s => { const n={...s}; delete n[r.id]; return n; })}
                                      data-testid={`gate-check-dismiss-${row.mc_id}-${r.id}`}
                                      className="ml-2 text-xs text-slate-400 hover:text-slate-600 underline"
                                    >
                                      dismiss
                                    </button>
                                  </>
                              </div>
                            );
                          })()}
                        {/* Issue 2: inline override form for this search result */}
                        {renderInlineOverride(r.id)}
                        </div>
                      ))}
                      <p className="text-xs text-slate-400 italic">Gate check runs automatically on search. Server result is authoritative.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Ranked candidates */}
      {loading && (
        <div className="text-xs text-slate-400 flex items-center gap-1">
          <RefreshCw size={12} className="animate-spin" /> Loading candidates…
        </div>
      )}

      {/* ── Alias Registry pinned card — shown when a prior owner-approved mapping exists ── */}
      {showCandidates && !loading && alias && row.decision !== "approved" && row.decision !== "excluded" && (
        <div className="border-2 border-indigo-300 rounded overflow-hidden" data-testid={`alias-pinned-card-${row.mc_id}`}>
          <div className="bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-800 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-indigo-600" />
              Prior Approval — Alias Registry
            </span>
            <span className="text-indigo-500 font-normal">Gate-check still required — not auto-approved</span>
          </div>
          <div className="px-3 py-2 text-xs bg-indigo-50/40">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-indigo-900">{alias.az_product_name || `Product #${alias.az_product_id}`}</div>
                {alias.az_product_code && <div className="text-indigo-600 font-mono">code {alias.az_product_code}</div>}
                <div className="text-indigo-500 mt-0.5">
                  Approved{alias.approved_at ? ` ${new Date(alias.approved_at).toLocaleDateString('en-GB')}` : ""}
                  {alias.notes ? ` · ${alias.notes}` : ""}
                </div>
                <p className="mt-1 text-indigo-700 italic">
                  This mapping was owner-approved in a prior session. It is surfaced as a strong suggestion only — all 5 server gates must pass before confirming.
                </p>
              </div>
              {!gateCheckState[alias.az_product_id]?.result && (
                <button
                  onClick={() => handleGateCheck(alias.az_product_id)}
                  disabled={gateCheckState[alias.az_product_id]?.loading}
                  data-testid={`gate-check-alias-${row.mc_id}`}
                  className="shrink-0 px-2.5 py-1.5 bg-indigo-700 hover:bg-indigo-800 text-white rounded text-xs font-semibold disabled:opacity-50"
                >
                  {gateCheckState[alias.az_product_id]?.loading ? "Checking…" : "Validate & Map"}
                </button>
              )}
            </div>
            {/* Gate-check error */}
            {gateCheckState[alias.az_product_id]?.error && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs" data-testid={`gate-check-alias-error-${row.mc_id}`}>
                {gateCheckState[alias.az_product_id].error}
              </div>
            )}
            {/* Gate-check result — reuses same pattern as ranked/master-search */}
            {gateCheckState[alias.az_product_id]?.result && (() => {
              const gc  = gateCheckState[alias.az_product_id].result;
              const gates = gc.gates || {};
              const allPass = gc.all_pass;
              const softOnly = gc.soft_conflict_only;
              const sizeOnly = gc.size_only_conflict;
              const preBlock = gc.pre_gate_block;
              const alreadyApproved = gc.mc_already_approved;
              const audit = gc._site_size_audit;
              return (
                <div className="mt-2 border border-indigo-200 rounded bg-white p-2" data-testid={`gate-check-alias-result-${row.mc_id}`}>
                  <>
                    {!allPass && !preBlock && (
                      <div className="text-xs text-slate-500 mb-1.5 flex items-center gap-1">
                        <AlertTriangle size={11} className="shrink-0 text-amber-500" />
                        Possible candidate — identity gates have not all passed. Review the gate detail below and use Admin Override to confirm.
                      </div>
                    )}
                    <div className="text-xs font-semibold text-indigo-700 mb-1.5">Server gate check result — Prior Approval</div>
                      {preBlock ? (
                        <p className="text-xs text-orange-700 font-medium">{preBlock.message}</p>
                      ) : (
                        <div className="space-y-1 mb-2">
                          {Object.entries(gates).map(([name, g]) => (
                            <div key={name} className="flex items-start gap-1.5 text-xs">
                              <span className={`mt-0.5 shrink-0 w-3 h-3 rounded-full ${g.passed ? "bg-emerald-400" : "bg-red-400"}`} />
                              <span className="font-mono text-slate-500 w-20 shrink-0">{name}</span>
                              <span className={g.passed ? "text-slate-600" : "text-red-700"}>{g.reason}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {alreadyApproved && (
                        <div className="mt-1 space-y-1">
                          <p className="text-xs text-amber-700 font-medium">This row is already approved. An override note is optional — use the panel below to remap.</p>
                          <button
                            onClick={() => onRequestOverride(
                              alias.az_product_id,
                              Object.keys(gates).filter(k => !gates[k].passed),
                              `Row already approved — admin gate override to change mapping`
                            )}
                            data-testid={`request-override-remap-alias-${row.mc_id}`}
                            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold inline-flex items-center gap-1"
                          >
                            <Shield size={12} /> Override to Remap
                          </button>
                        </div>
                      )}
                      {allPass && !alreadyApproved && !preBlock && (
                        <button
                          onClick={() => gc.is_proposed_new_manual
                            ? onRequestOverride(alias.az_product_id, [], null, true)
                            : onResolve("map_to", alias.az_product_id)
                          }
                          data-testid={`confirm-map-alias-${row.mc_id}`}
                          className="mt-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold"
                        >
                          {gc.is_proposed_new_manual ? "Confirm Manual Assignment" : `Confirm Map to ${gc.product_name || alias.az_product_name}`}
                        </button>
                      )}
                      {/* Site-size conflict */}
                      {sizeOnly && !preBlock && !alreadyApproved && gc.source_pack_ml && (
                        <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                          <div className="text-xs text-blue-800 mb-1.5">
                            <strong>Size-only difference</strong> — this site buys {gc.source_pack_ml}ml; master records {gc.candidate_pack_ml || '?'}ml.
                            Product Master stays unchanged.
                          </div>
                          {audit && (
                            <div className="text-[10px] text-blue-600 mb-1.5 font-mono bg-blue-100 rounded px-2 py-1" data-testid={`site-size-audit-alias-${row.mc_id}`}>
                              Audit — recorded {audit.recorded_at?.split('T')[0]} by {audit.actor_email} | old={audit.old_pack_ml != null ? `${audit.old_pack_ml}ml` : 'none'} → new={audit.new_pack_ml}ml
                            </div>
                          )}
                          {!audit && (
                            <button
                              onClick={() => handleUseSiteSize(alias.az_product_id, gc.source_pack_ml)}
                              disabled={usingSiteSize[alias.az_product_id]}
                              data-testid={`use-site-size-alias-${row.mc_id}`}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                            >
                              {usingSiteSize[alias.az_product_id] ? 'Saving…' : `Use for this site (${gc.source_pack_ml}ml)`}
                            </button>
                          )}
                        </div>
                      )}
                      {!allPass && !gc.blocked_override_unavailable && !preBlock && !alreadyApproved && (
                        <button
                          onClick={() => onRequestOverride(
                            alias.az_product_id,
                            Object.keys(gates).filter(k => !gates[k].passed),
                            `Gate conflict on prior-approval "${gc.product_name || alias.az_product_name}" — admin gate override`,
                            gc.is_proposed_new_manual
                          )}
                          data-testid={`request-override-alias-${row.mc_id}`}
                          className="mt-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold inline-flex items-center gap-1"
                        >
                          <Shield size={12} /> Admin Override — submit with reason
                        </button>
                      )}
                      {!allPass && gc.blocked_override_unavailable && !preBlock && !sizeOnly && (
                        <span
                          className="mt-1 inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200"
                          data-testid={`override-unavailable-alias-${row.mc_id}`}
                        >
                          <Lock size={11} /> Override not permitted — hard physical conflict
                        </span>
                      )}
                      <button
                        onClick={() => setGateCheckState(s => { const n={...s}; delete n[alias.az_product_id]; return n; })}
                        data-testid={`gate-check-alias-dismiss-${row.mc_id}`}
                        className="ml-2 text-xs text-slate-400 hover:text-slate-600 underline"
                      >
                        dismiss
                      </button>
                    </>
                </div>
              );
            })()}
          {/* Issue 2: inline override form for this alias */}
          {renderInlineOverride(alias.az_product_id)}
          </div>
        </div>
      )}

      {showCandidates && !loading && ranked.length > 0 && (
        <div className="border border-slate-200 rounded overflow-hidden">
          <div className="bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 flex items-center justify-between">
            <span>Ranked Candidates (top {ranked.length})</span>
            <span className="text-slate-500">Score · Pre-scored gates · Validate to confirm</span>
          </div>
          <div className="divide-y divide-slate-100">
            {ranked.map((c, i) => (
              <div key={c.id} className={`px-3 py-2 text-xs ${c.is_blocked ? "bg-red-50 opacity-75" : i === 0 ? "bg-amber-50" : ""}`}>
                {/* Top row: score + identity + pre-scored gates + action button */}
                <div className="flex items-start gap-3">
                  <div className="w-12 text-right shrink-0">
                    <span className={`font-mono font-bold ${c.score >= 0.86 ? "text-emerald-700" : c.score >= 0.55 ? "text-amber-700" : "text-red-600"}`}>
                      {Math.round(c.score * 100)}%
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate">{c.product_name}</div>
                    <div className="text-slate-500 truncate">{c.subcategory_name} · code {c.product_code}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.gates && Object.entries(c.gates).map(([name, g]) => (
                        <GateBadge key={name} name={name} gate={g} />
                      ))}
                    </div>
                    {c.is_blocked && <div className="text-red-600 mt-0.5">{c.gates_summary}</div>}
                  </div>
                  {/* Validate & Map — shown for any candidate not yet gate-checked,
                      including blocked ones whose block may be overridable */}
                  {row.decision !== "approved" && row.decision !== "excluded" && !gateCheckState[c.id]?.result && !gateCheckState[c.id]?.loading && (
                    <button
                      onClick={() => handleGateCheck(c.id)}
                      data-testid={`gate-check-ranked-${row.mc_id}-${c.id}`}
                      className="shrink-0 px-2 py-1 bg-slate-700 hover:bg-slate-800 text-white rounded text-xs disabled:opacity-50"
                    >
                      Validate &amp; Map
                    </button>
                  )}
                  {gateCheckState[c.id]?.loading && (
                    <span className="shrink-0 text-[10px] text-slate-400 animate-pulse">Checking…</span>
                  )}
                </div>

                {/* Inline gate check result — same pattern as master search */}
                {gateCheckState[c.id]?.error && (
                  <div className="mt-2 ml-15 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs" data-testid={`gate-check-ranked-error-${row.mc_id}-${c.id}`}>
                    {gateCheckState[c.id].error}
                  </div>
                )}
                {gateCheckState[c.id]?.result && (() => {
                  const gc = gateCheckState[c.id].result;
                  const gates = gc.gates || {};
                  const allPass = gc.all_pass;
                  const softOnly = gc.soft_conflict_only;
                  const sizeOnly = gc.size_only_conflict;
                  const preBlock = gc.pre_gate_block;
                  const alreadyApproved = gc.mc_already_approved;
                  const audit = gc._site_size_audit;
                  return (
                    <div className="mt-2 border border-slate-200 rounded bg-white p-2" data-testid={`gate-check-ranked-result-${row.mc_id}-${c.id}`}>
                      <>
                        {!allPass && !preBlock && (
                          <div className="text-xs text-slate-500 mb-1.5 flex items-center gap-1">
                            <AlertTriangle size={11} className="shrink-0 text-amber-500" />
                            Possible candidate — identity gates have not all passed. Review the gate detail below and use Admin Override to confirm.
                          </div>
                        )}
                        <div className="text-xs font-semibold text-slate-700 mb-1.5">Server gate check result</div>
                          {preBlock ? (
                            <p className="text-xs text-orange-700 font-medium">{preBlock.message}</p>
                          ) : (
                            <div className="space-y-1 mb-2">
                              {Object.entries(gates).map(([name, g]) => (
                                <div key={name} className="flex items-start gap-1.5 text-xs">
                                  <span className={`mt-0.5 shrink-0 w-3 h-3 rounded-full ${g.passed ? "bg-emerald-400" : "bg-red-400"}`} />
                                  <span className="font-mono text-slate-500 w-20 shrink-0">{name}</span>
                                  <span className={g.passed ? "text-slate-600" : "text-red-700"}>{g.reason}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {gc.uoms?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {gc.uoms.map((u, ui) => (
                                <span key={ui} className={`px-1.5 py-0.5 rounded border text-xs font-mono
                                  ${u.role === "sales_default" ? "border-blue-300 bg-blue-50 text-blue-700" :
                                    u.role === "purchase_default" ? "border-amber-300 bg-amber-50 text-amber-700" :
                                    "border-slate-200 bg-white text-slate-500"}`}>
                                  {u.unit_type} {u.ml ? `=${u.ml}ml` : ""} [{u.role}]
                                </span>
                              ))}
                            </div>
                          )}
                          {/* ── Site-size conflict: show "Use site size" action ── */}
                          {sizeOnly && !preBlock && !alreadyApproved && gc.source_pack_ml && (
                            <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                              <div className="text-xs text-blue-800 mb-1.5">
                                <strong>Size-only difference</strong> — this site buys {gc.source_pack_ml}ml; master records {gc.candidate_pack_ml || '?'}ml.
                                Product Master stays unchanged.
                              </div>
                              {audit && (
                                <div className="text-[10px] text-blue-600 mb-1.5 font-mono bg-blue-100 rounded px-2 py-1" data-testid={`site-size-audit-ranked-${row.mc_id}-${c.id}`}>
                                  Audit — recorded {audit.recorded_at?.split('T')[0]} by {audit.actor_email} | old={audit.old_pack_ml != null ? `${audit.old_pack_ml}ml` : 'none'} → new={audit.new_pack_ml}ml
                                </div>
                              )}
                              {!audit && (
                                <button
                                  onClick={() => handleUseSiteSize(c.id, gc.source_pack_ml)}
                                  disabled={usingSiteSize[c.id]}
                                  data-testid={`use-site-size-ranked-${row.mc_id}-${c.id}`}
                                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                                >
                                  {usingSiteSize[c.id] ? 'Saving…' : `Use for this site (${gc.source_pack_ml}ml)`}
                                </button>
                              )}
                            </div>
                          )}
                          {alreadyApproved && (
                            <div className="mt-1 space-y-1">
                              <p className="text-xs text-amber-700 font-medium">This row is already approved. An override note is optional — use the panel below to remap.</p>
                              <button
                                onClick={() => onRequestOverride(
                                  c.id,
                                  Object.keys(gates).filter(k => !gates[k].passed),
                                  `Row already approved — admin gate override to change mapping`
                                )}
                                data-testid={`request-override-remap-ranked-${row.mc_id}-${c.id}`}
                                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold inline-flex items-center gap-1"
                              >
                                <Shield size={12} /> Override to Remap
                              </button>
                            </div>
                          )}
                          {allPass && !alreadyApproved && !preBlock && (
                            <button
                              onClick={() => gc.is_proposed_new_manual
                                ? onRequestOverride(c.id, [], null, true)
                                : onResolve("map_to", c.id)
                              }
                              data-testid={`confirm-map-ranked-${row.mc_id}-${c.id}`}
                              className="mt-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold"
                            >
                              {gc.is_proposed_new_manual ? "Confirm Manual Assignment" : `Confirm Map to ${gc.product_name}`}
                            </button>
                          )}
                          {!allPass && !gc.blocked_override_unavailable && !preBlock && !alreadyApproved && (
                            <button
                              onClick={() => onRequestOverride(
                                c.id,
                                Object.keys(gates).filter(k => !gates[k].passed),
                                `Gate conflict on "${gc.product_name || c.product_name}" — admin gate override`,
                                gc.is_proposed_new_manual
                              )}
                              data-testid={`request-override-ranked-${row.mc_id}-${c.id}`}
                              className="mt-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold inline-flex items-center gap-1"
                            >
                              <Shield size={12} /> Admin Override — submit with reason
                            </button>
                          )}
                          {!allPass && gc.blocked_override_unavailable && !preBlock && !sizeOnly && (
                            <span
                              className="mt-1 inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200"
                              data-testid={`override-unavailable-ranked-${row.mc_id}-${c.id}`}
                            >
                              <Lock size={11} /> Override not permitted — hard physical conflict
                            </span>
                          )}
                          <button
                            onClick={() => setGateCheckState(s => { const n={...s}; delete n[c.id]; return n; })}
                            data-testid={`gate-check-ranked-dismiss-${row.mc_id}-${c.id}`}
                            className="ml-2 text-xs text-slate-400 hover:text-slate-600 underline"
                          >
                            dismiss
                          </button>
                        </>
                    </div>
                  );
                })()}
                {/* Issue 2: inline override form for this ranked candidate */}
                {renderInlineOverride(c.id)}
              </div>
            ))}
          </div>
          <p className="px-3 py-2 text-xs text-slate-400 italic border-t border-slate-100">Gate badges above are pre-scored — server result (auto-checked on expand) is authoritative.</p>
        </div>
      )}

      {showCandidates && !loading && ranked.length === 0 && (
        <div className="rounded border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-500" data-testid={`no-candidates-${row.mc_id}`}>
          <span className="font-medium text-slate-700">No scored candidates.</span>{" "}
          {row.match_status === "proposed_new"
            ? "Automated matching did not find a candidate above threshold. If you believe an existing master product is the correct match, search by name below and select it — you will need to confirm the gate results and provide an override reason if any gates flag."
            : "Search by barcode or product name (Master Search below) to locate an existing master. If no existing match is found, use 'Add product' to propose a new entry — requires authorised admin review."
          }
        </div>
      )}

      {/* Research results that were already loaded */}
      {research.length > 0 && (
        <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`research-results-${row.mc_id}`}>
          <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
            <FlaskConical size={12}/> Research results — advisory only
          </div>
          <div className="space-y-1">
            {research.map(r => (
              <div key={r.id} className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.is_manual ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>
                    {r.is_manual ? "Manual citation" : r.provider}
                  </span>
                  {r.source_url && (
                    <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline flex items-center gap-0.5">
                      Source <ExternalLink size={10} />
                    </a>
                  )}
                </div>
                {r.evidence?.product_name && <div className="mt-0.5 font-medium">{r.evidence.product_name}</div>}
                {r.is_manual && <div className="text-slate-600 mt-0.5">{r.manual_citation}</div>}
                <div className="text-slate-400 mt-0.5 italic">Corroborative only — all suggestions require human approval + gate check</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Exclude dialog ─────────────────────────────────────────────────────────────

function ExcludeDialog({ row, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  const [scope, setScope] = useState("run");
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" role="dialog" aria-modal="true" data-testid="exclude-dialog">
      <div className="bg-white rounded-lg shadow-xl p-6 w-[500px] max-w-full">
        <h3 className="font-semibold text-slate-800 mb-1">Exclude Row from Import</h3>

        {/* Req 8: Admin-only semantic warning */}
        <div className="mb-3 flex items-start gap-2 rounded border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-900">
          <span className="shrink-0 mt-0.5"><Ban size={12} className="text-orange-600" /></span>
          <span>
            <strong>Admin-only exceptional action.</strong> Use only for rows that are demonstrably
            invalid, non-product, duplicate, or corrupt. Every valid legacy product must be
            resolved or imported — do not exclude to avoid a difficult match.
            Source and audit evidence is always preserved.
          </span>
        </div>

        <p className="text-xs text-slate-500 mb-4">
          <strong>{row.itemcode}</strong> — {row.source_description}
          <br/>
          <span className="font-medium text-slate-700">S2 stock cost:</span>{" "}
          <span className="font-mono">{fmt(row.open_cost)}</span>
          {" "}&nbsp;·&nbsp;{" "}
          <span className="font-medium text-slate-700">S4 PLU retail inc-VAT:</span>{" "}
          <span className="font-mono">{fmt(row.plu_value_inc_vat)}</span>
          <br/>
          <span className="text-amber-700">These are separate financial domains and must not be netted.</span>{" "}
          Excluded rows are workflow-resolved but NOT counted in matched coverage.
        </p>
        <label className="block text-xs font-medium text-slate-700 mb-1">
          Reason <span className="text-red-500">*</span>
          <span className="text-slate-400 font-normal ml-1">(e.g. "Duplicate of ic=12345" / "Non-product code — system flag" / "Corrupt data — confirmed by admin")</span>
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder="Mandatory — state why this row is demonstrably invalid/non-product/duplicate/corrupt…"
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm mb-3"
          data-testid="exclude-reason-input"
        />
        <div className="mb-4">
          <label className="block text-xs font-medium text-slate-700 mb-1">Scope</label>
          <select value={scope} onChange={e => setScope(e.target.value)} className="border border-slate-300 rounded px-3 py-1.5 text-sm">
            <option value="run">This import run only (default)</option>
            <option value="persistent">Persistent governance rule (Admin only)</option>
          </select>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50">Cancel</button>
          <button
            onClick={() => {
              if (!reason.trim()) { toast.error("Exclusion reason is required"); return; }
              onConfirm(reason.trim(), scope);
            }}
            disabled={!reason.trim()}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded text-sm disabled:opacity-50"
            data-testid="exclude-confirm-btn"
          >
            Confirm Exclusion
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Disposition Queue Panel ───────────────────────────────────────────────────

const DISP_META = {
  wrong_variant_hold: { color: "bg-red-50 border-red-200 text-red-800",          dot: "bg-red-500",     action: "Hold — variant conflict, do not approve", actionColor: "text-red-700"  },
  existing_match:     { color: "bg-emerald-50 border-emerald-200 text-emerald-800", dot: "bg-emerald-500", action: "Review identity — candidate only",         actionColor: "text-emerald-700" },
  alternate_existing: { color: "bg-amber-50 border-amber-200 text-amber-800",      dot: "bg-amber-500",   action: "Choose candidate — not decided",            actionColor: "text-amber-700"   },
  unresolved_no_match:{ color: "bg-blue-50 border-blue-200 text-blue-800",         dot: "bg-blue-500",    action: "Investigate — search failure ≠ new product", actionColor: "text-blue-700"   },
  incomplete_data:    { color: "bg-orange-50 border-orange-200 text-orange-800",   dot: "bg-orange-400",  action: "Hold — missing data or route to 1C",         actionColor: "text-orange-700"  },
  treatment_exception:{ color: "bg-slate-50 border-slate-200 text-slate-600",      dot: "bg-slate-400",   action: "Needs ALLOFILE/S6C bridge — not exclusion",  actionColor: "text-slate-600"   },
};

// ── A/B/C/D Action Ledger ─────────────────────────────────────────────────────

const ABCD_META = {
  A: {
    letter: "A", bg: "bg-emerald-50", border: "border-emerald-400",
    textColor: "text-emerald-800", badgeColor: "bg-emerald-600",
    action: "Bulk Approve", actionColor: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  B: {
    letter: "B", bg: "bg-amber-50", border: "border-amber-400",
    textColor: "text-amber-800", badgeColor: "bg-amber-500",
    action: "Batch Review", actionColor: "text-amber-700",
    dot: "bg-amber-500",
  },
  C: {
    letter: "C", bg: "bg-blue-50", border: "border-blue-400",
    textColor: "text-blue-800", badgeColor: "bg-blue-600",
    action: "Owner Decision", actionColor: "text-blue-700",
    dot: "bg-blue-500",
  },
  D: {
    letter: "D", bg: "bg-slate-50", border: "border-slate-400",
    textColor: "text-slate-700", badgeColor: "bg-slate-500",
    action: "Exclude (with reason)", actionColor: "text-slate-600",
    dot: "bg-slate-400",
  },
};

function AbcdLedgerPanel({ s1RunId, onOpenBatchReview, onOpenDecisionQueue }) {
  const [open, setOpen]       = useState(true);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!s1RunId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/admin/psb-stage1b/${s1RunId}/abcd-ledger`
      );
      setData(d);
      // Auto-expand non-empty buckets
      const ex = {};
      Object.entries(d.buckets || {}).forEach(([k, v]) => { if (v.totals?.count > 0) ex[k] = false; });
      setExpanded(ex);
    } catch { /* silently ignore — ledger is supplementary */ }
    finally { setLoading(false); }
  }, [s1RunId]);

  useEffect(() => { load(); }, [load]);

  if (!data && !loading) return null;

  const total = data?.total_pending ?? 0;
  const cc    = data?.count_check   ?? {};

  return (
    <div className="mb-5 border border-slate-200 rounded-lg bg-white shadow-sm" data-testid="abcd-ledger-panel">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-50 transition-colors rounded-t-lg"
        onClick={() => setOpen(o => !o)}
        data-testid="abcd-ledger-toggle"
      >
        <div className="flex items-center gap-3">
          <Shield size={16} className="text-slate-500" />
          <span className="font-semibold text-slate-800 text-sm">A/B/C/D Action Ledger</span>
          {data && (
            <span className="text-xs text-slate-500">
              {total} pending identities &nbsp;·&nbsp; A={cc.A} &nbsp;B={cc.B} &nbsp;C={cc.C} &nbsp;D={cc.D}
            </span>
          )}

        </div>
        <div className="flex items-center gap-2">
          {loading && <RefreshCw size={13} className="animate-spin text-slate-400" />}
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-200">
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
              <RefreshCw size={14} className="animate-spin mr-2" /> Building ledger…
            </div>
          )}

          {data && (
            <>
              {/* ── Summary strip ── */}
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex flex-wrap gap-3 items-center">
                {["A","B","C","D"].map(letter => {
                  const m   = ABCD_META[letter];
                  const bkt = data.buckets[letter];
                  return (
                    <div key={letter} className={`flex items-center gap-2 px-3 py-1.5 rounded-md border ${m.border} ${m.bg}`}>
                      <span className={`w-5 h-5 rounded text-xs font-bold text-white flex items-center justify-center ${m.badgeColor}`}>
                        {letter}
                      </span>
                      <span className={`text-sm font-bold ${m.textColor}`}>{bkt.totals.count}</span>
                      <span className={`text-xs ${m.textColor} opacity-75`}>{bkt.label}</span>
                    </div>
                  );
                })}
                <div className="ml-auto text-xs text-slate-400 italic">
                  {cc.A}+{cc.B}+{cc.C}+{cc.D} = {cc.sum} pending
                </div>
              </div>

              {/* ── Per-bucket detail cards ── */}
              <div className="divide-y divide-slate-100">
                {["A","B","C","D"].map(letter => {
                  const m   = ABCD_META[letter];
                  const bkt = data.buckets[letter];
                  const isEx = expanded[letter];
                  const count = bkt.totals.count;
                  if (count === 0 && letter === "A") {
                    // A=0 is expected — show as a small note rather than full card
                    return (
                      <div key={letter} className="px-5 py-2 flex items-center gap-3 text-xs text-slate-400">
                        <span className={`w-4 h-4 rounded text-xs font-bold text-white flex items-center justify-center ${m.badgeColor}`}>
                          {letter}
                        </span>
                        <span className="italic">No exact matches pending — all deterministic rows already approved.</span>
                      </div>
                    );
                  }
                  return (
                    <div key={letter} data-testid={`abcd-bucket-${letter}`}>
                      {/* Bucket header */}
                      <button
                        className="w-full flex items-center justify-between px-5 py-2.5 text-left hover:bg-slate-50 transition-colors"
                        onClick={() => setExpanded(e => ({ ...e, [letter]: !e[letter] }))}
                        data-testid={`abcd-bucket-toggle-${letter}`}
                      >
                        <div className="flex items-center gap-2.5">
                          <span className={`w-5 h-5 rounded text-xs font-bold text-white flex items-center justify-center ${m.badgeColor}`}>
                            {letter}
                          </span>
                          <span className={`text-sm font-semibold ${m.textColor}`}>{bkt.label}</span>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${m.border} ${m.bg} ${m.textColor}`}>
                            {count} row{count !== 1 ? "s" : ""}
                          </span>
                          <span className="text-xs text-slate-400 hidden sm:inline">{bkt.description}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                          <span title="Stock cost: S2+S3+S6a" className="tabular-nums">
                            cost {fmt(bkt.totals.cost_priority_total)}
                          </span>
                          <span title="S4 PLU revenue — retail domain, separate" className="text-amber-600 tabular-nums">
                            PLU {fmt(bkt.totals.s4_plu_revenue)}
                          </span>
                          {isEx ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                        </div>
                      </button>

                      {/* Bucket body */}
                      {isEx && (
                        <div className={`border-t border-slate-100 ${m.bg} px-5 py-3`}>
                          {/* Financial breakdown */}
                          <div className="flex flex-wrap gap-4 text-xs mb-3 text-slate-600">
                            <span>S2 open: <b className="tabular-nums">{fmt(bkt.totals.s2_open_cost)}</b></span>
                            <span>S3 deliv: <b className="tabular-nums">{fmt(bkt.totals.s3_deliv_cost)}</b></span>
                            <span>S3 credit: <b className="tabular-nums">{fmt(bkt.totals.s3_credit_cost)}</b></span>
                            <span>S6a close: <b className="tabular-nums">{fmt(bkt.totals.s6a_close_cost)}</b></span>
                            <span className="text-amber-700">S4 PLU rev (retail): <b className="tabular-nums">{fmt(bkt.totals.s4_plu_revenue)}</b></span>
                          </div>

                          {/* Action button */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {letter === "B" && count > 0 && (
                              <button
                                onClick={onOpenBatchReview}
                                data-testid="abcd-b-batch-review-btn"
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-medium"
                              >
                                <CheckSquare size={13}/> Open Batch Review Panel
                              </button>
                            )}
                            {letter === "C" && count > 0 && (
                              <button
                                onClick={onOpenDecisionQueue}
                                data-testid="abcd-c-decision-queue-btn"
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium"
                              >
                                <Eye size={13}/> Open Owner Decision Queue ({count})
                              </button>
                            )}
                            {letter === "D" && (
                              <span className="text-xs text-slate-500 italic py-1">
                                Structural exclusions — document reason per row before excluding.
                              </span>
                            )}
                          </div>

                          {/* Row table */}
                          <div className="overflow-x-auto rounded border border-slate-200">
                            <table className="w-full text-xs border-collapse bg-white">
                              <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500 w-20">PLU</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500">Description</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500 w-14">Grp</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500 w-14">Pack</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500">Candidate</th>
                                  <th className="px-2 py-1.5 text-right font-medium text-slate-500 w-14">Conf</th>
                                  <th className="px-2 py-1.5 text-right font-medium text-slate-500 w-20" title="Stock cost priority (S2+S3+S6a)">Cost</th>
                                  <th className="px-2 py-1.5 text-right font-medium text-amber-600 w-20" title="S4 PLU revenue — retail domain">PLU Rev</th>
                                </tr>
                              </thead>
                              <tbody>
                                {bkt.rows.map((row, i) => (
                                  <tr
                                    key={row.mc_id}
                                    className={`border-t border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/50"}`}
                                    data-testid={`abcd-row-${row.mc_id}`}
                                  >
                                    <td className="px-2 py-1.5 font-mono text-slate-600">{row.itemcode}</td>
                                    <td className="px-2 py-1.5 font-medium text-slate-800">{row.source_description}</td>
                                    <td className="px-2 py-1.5 text-slate-500">{row.group_code}</td>
                                    <td className="px-2 py-1.5 text-slate-500">{row.size_raw || "—"}</td>
                                    <td className="px-2 py-1.5 text-slate-600">
                                      {row.matched_bp_name
                                        ? <span>{row.matched_bp_name} <span className="text-slate-400">bp={row.matched_bp_id}</span></span>
                                        : <span className="text-slate-400 italic">no match</span>
                                      }
                                    </td>
                                    <td className="px-2 py-1.5 text-right"><ConfBadge conf={row.confidence}/></td>
                                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{fmt(row.review_priority_score)}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums text-amber-700">{fmt(row.s4_plu_revenue)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="border-t border-slate-200 bg-slate-50">
                                <tr>
                                  <td colSpan={6} className="px-2 py-1 text-right text-xs text-slate-400 font-medium">
                                    {count} row{count !== 1 ? "s" : ""} · totals:
                                  </td>
                                  <td className="px-2 py-1 text-right text-xs font-bold text-slate-700 tabular-nums">{fmt(bkt.totals.cost_priority_total)}</td>
                                  <td className="px-2 py-1 text-right text-xs font-bold text-amber-700 tabular-nums">{fmt(bkt.totals.s4_plu_revenue)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="px-5 py-2.5 bg-amber-50 rounded-b-lg flex items-center justify-between border-t border-amber-100">
                <p className="text-xs text-amber-700 font-medium">
                  Zero-write pass — no approvals or exclusions made here.
                  Stock cost (S2+S3+S6a) and retail revenue (S4 PLU) are separate dimensions.
                </p>
                <button onClick={load} className="ml-4 flex-shrink-0 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                  <RefreshCw size={11}/> Refresh
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// ── Group 14 Owner Exclusion Card ─────────────────────────────────────────────
// Renders inside the d_structural tab for `exclusion_type === 'food_and_snacks'`.
// Shows separate cost/retail evidence, required reason, run-scoped vs persistent
// scope choice, submit, and audit trail for already-excluded rows.

function Group14ExclusionCard({ row, s1RunId, onDone }) {
  const [scope,       setScope]      = useState("run");
  const [reason,      setReason]     = useState(row.exclusion_reason_template || "");
  const [submitting,  setSubmitting] = useState(false);
  const [apiError,    setApiError]   = useState(null);

  const costTotal  = (row.s2_open_cost || 0) + (row.s3_deliv_cost || 0) + (row.s3_credit_cost || 0) + (row.s6a_close_cost || 0);
  const retailRev  = row.s4_plu_revenue || 0;
  const fmt = v => v == null ? "—" : `£${Number(v).toFixed(2)}`;

  const canSubmit = reason.trim().length >= 10 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setApiError(null);
    try {
      await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/resolve`,
        { action: "exclude", exclusion_reason: reason.trim(), exclusion_scope: scope }
      );
      onDone({ mc_id: row.mc_id, scope, reason: reason.trim() });
    } catch (err) {
      setApiError(err?.response?.data?.detail || "Server error — action not saved");
      setSubmitting(false);
    }
  };

  return (
    <div className="p-3 space-y-3" data-testid={`g14-excl-card-${row.mc_id}`}>
      {/* Evidence: separate cost domain / retail domain */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="rounded border border-slate-200 bg-white p-2 space-y-1">
          <p className="font-semibold text-slate-600 uppercase tracking-wide">Cost domain (S2+S3+S6a)</p>
          <div className="flex justify-between"><span className="text-slate-500">Opening stock</span><span className="tabular-nums font-medium">{fmt(row.s2_open_cost)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">S3 deliveries</span><span className="tabular-nums font-medium text-green-700">{fmt(row.s3_deliv_cost)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">S3 credits</span><span className="tabular-nums font-medium text-red-600">{fmt(row.s3_credit_cost)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Closing stock</span><span className="tabular-nums font-medium">{fmt(row.s6a_close_cost)}</span></div>
          <div className="flex justify-between border-t pt-1 mt-1"><span className="font-semibold text-slate-700">Cost exposure</span><span className="tabular-nums font-bold text-slate-800">{fmt(costTotal)}</span></div>
        </div>
        <div className="rounded border border-amber-200 bg-amber-50/40 p-2 space-y-1">
          <p className="font-semibold text-amber-700 uppercase tracking-wide">Retail domain (S4 PLU) — separate</p>
          <div className="flex justify-between"><span className="text-slate-500">PLU revenue</span><span className="tabular-nums font-medium text-amber-800">{fmt(retailRev)}</span></div>
          <p className="text-[10px] text-amber-600 italic mt-1">Retail is not additive to cost — two independent domains.</p>
        </div>
      </div>

      {/* Exclusion scope — Persistent locked until site/source-identity scope and future-file behaviour are proven */}
      <div className="flex gap-4 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="radio" name={`scope-${row.mc_id}`} value="run" checked={scope === "run"} onChange={() => setScope("run")} />
          <span><b>Run-scoped</b> — exclude from this import only; re-evaluate next run</span>
        </label>
        <label
          className="flex items-center gap-1.5 cursor-not-allowed opacity-40 select-none"
          title="Persistent exclusion is locked for Group 14 rows until site/source-identity scope and future-file behaviour are confirmed by an authorised owner."
          data-testid={`g14-persistent-locked-${row.mc_id}`}
        >
          <input type="radio" name={`scope-${row.mc_id}`} value="persistent" disabled checked={false} readOnly />
          <span><b>Persistent</b> — locked (scope not yet proven for Group 14)</span>
        </label>
      </div>

      {/* Required reason */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-700">
          Exclusion reason <span className="text-red-500">*</span>
          <span className="text-slate-400 font-normal"> (min 10 chars, will be recorded in audit trail)</span>
        </label>
        <textarea
          data-testid={`g14-excl-reason-${row.mc_id}`}
          rows={2}
          value={reason}
          onChange={e => setReason(e.target.value)}
          className="w-full rounded border border-slate-300 text-xs px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400 resize-none"
          placeholder="Required — state why this item is excluded…"
        />
        <p className="text-[10px] text-slate-400">{reason.trim().length} / 10 minimum chars</p>
      </div>

      {apiError && (
        <p className="text-xs text-red-600 font-medium" data-testid={`g14-excl-error-${row.mc_id}`}>{apiError}</p>
      )}

      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-400 italic">
          Audit trail: decision recorded with timestamp, actor ID, reason and scope.
        </span>
        <button
          data-testid={`g14-excl-submit-${row.mc_id}`}
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold"
        >
          {submitting ? "Saving…" : `Confirm ${scope === "persistent" ? "Persistent" : "Run-Scoped"} Exclusion`}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Site Settings Panel — compact expandable panel beside a chosen/confirmed match
// ─────────────────────────────────────────────────────────────────────────────

function SiteSettingsPanel({ row, azBpId, s1RunId, siteId, srcYield, srcPackMl }) {
  const [open,      setOpen]     = useState(false);
  const [invData,   setInvData]  = useState(null);
  const [loadingInv, setLoadingInv] = useState(false);
  const [saving,    setSaving]   = useState(false);
  const [saved,     setSaved]    = useState(false);
  const [err,       setErr]      = useState(null);

  // Pack form state
  const [formCount, setFormCount] = useState('');
  const [formMl,    setFormMl]    = useState('');
  const [formNote,  setFormNote]  = useState('');

  // Bar-price form state
  const [formPrice,    setFormPrice]    = useState('');
  const [formMeasure,  setFormMeasure]  = useState('');
  const [formEffDate,  setFormEffDate]  = useState('');
  const [savingPrice,  setSavingPrice]  = useState(false);
  const [savedPrice,   setSavedPrice]   = useState(false);
  const [priceErr,     setPriceErr]     = useState(null);

  const fetchInv = useCallback(async () => {
    if (!siteId || !azBpId) return;
    setLoadingInv(true);
    try {
      const res = await api.get(`/sites/${siteId}/beverage-inventory-item?product_id=${azBpId}`);
      const inv = res.data;
      setInvData(inv);
      // Pre-fill pack form with SAVED site values (not proposed PS values)
      setFormCount(inv?.site_purchase_case_count != null ? String(inv.site_purchase_case_count) : '');
      setFormMl(inv?.site_purchase_case_ml        != null ? String(inv.site_purchase_case_ml)    : '');
      // Pre-fill price form with saved bar price
      setFormPrice(inv?.bar_price          != null ? String(inv.bar_price)          : '');
      setFormMeasure(inv?.bar_price_measure_ml != null ? String(inv.bar_price_measure_ml) : '');
      setFormEffDate(inv?.bar_price_effective_from || '');
    } catch {
      setInvData(null);
    } finally {
      setLoadingInv(false);
    }
  }, [siteId, azBpId]);

  const handleOpen = () => {
    setOpen(o => {
      if (!o) fetchInv();
      return !o;
    });
  };

  // ── Pack save ────────────────────────────────────────────────────────────
  const handleSavePack = async () => {
    if (!siteId || !azBpId) return;
    const newCount = formCount !== '' ? Number(formCount) : null;
    const newMl    = formMl    !== '' ? Number(formMl)    : null;
    const curCount = invData?.site_purchase_case_count;
    const curMl    = invData?.site_purchase_case_ml;
    // Unchanged save — no write
    if (newCount === curCount && newMl === curMl) { setSaved(true); setTimeout(() => setSaved(false), 1500); return; }
    setSaving(true); setErr(null);
    try {
      await api.patch(`/sites/${siteId}/beverage-inventory/${azBpId}/case-override`, {
        site_purchase_case_count: newCount,
        site_purchase_case_ml:    newMl,
        reason: formNote.trim() || `PSB ${s1RunId?.slice(0,8)}… — explicit site pack`,
        clear: false,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await fetchInv();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleCancelPack = () => {
    // Revert form to last-loaded DB values — no write
    setFormCount(invData?.site_purchase_case_count != null ? String(invData.site_purchase_case_count) : '');
    setFormMl(invData?.site_purchase_case_ml        != null ? String(invData.site_purchase_case_ml)    : '');
    setFormNote('');
    setErr(null);
  };

  // ── Bar price save ───────────────────────────────────────────────────────
  const handleSavePrice = async () => {
    if (!siteId || !azBpId || !formPrice) return;
    const newP = Number(formPrice);
    const newM = formMeasure ? Number(formMeasure) : invData?.bar_price_measure_ml;
    if (!newM) { setPriceErr('Measure (ml) required'); return; }
    const curP = invData?.bar_price;
    const curM = invData?.bar_price_measure_ml;
    const curE = invData?.bar_price_effective_from;
    if (newP === curP && newM === curM && (formEffDate === curE || !formEffDate)) {
      setSavedPrice(true); setTimeout(() => setSavedPrice(false), 1500); return;
    }
    setSavingPrice(true); setPriceErr(null);
    try {
      await api.put(`/sites/${siteId}/product-price?product_id=${azBpId}`, {
        bar_price:      newP,
        measure_ml:     newM,
        effective_from: formEffDate || undefined,
      });
      setSavedPrice(true);
      setTimeout(() => setSavedPrice(false), 2000);
      await fetchInv();
    } catch (e) {
      setPriceErr(e?.response?.data?.detail || 'Price save failed');
    } finally { setSavingPrice(false); }
  };

  const handleCancelPrice = () => {
    setFormPrice(invData?.bar_price     != null ? String(invData.bar_price)         : '');
    setFormMeasure(invData?.bar_price_measure_ml != null ? String(invData.bar_price_measure_ml) : '');
    setFormEffDate(invData?.bar_price_effective_from || '');
    setPriceErr(null);
  };

  // ── Derived display values ───────────────────────────────────────────────
  // PS source proposed (informational — not written on identity approval)
  const propCount = srcYield  ? Math.round(srcYield)  : null;
  const propMl    = srcPackMl ? Math.round(srcPackMl) : null;
  const propTotal = (propCount && propMl) ? propCount * propMl : null;

  // Saved site values
  const curCount = invData?.site_purchase_case_count;
  const curMl    = invData?.site_purchase_case_ml;
  const curTotal = (curCount && curMl) ? curCount * curMl : null;

  // Master defaults
  const masterCount = invData?.master_case_size;
  const masterMl    = invData?.default_unit_ml;

  // Form computed total
  const fmtCount = formCount ? Number(formCount) : null;
  const fmtMl    = formMl    ? Number(formMl)    : null;
  const fmtTotal = (fmtCount && fmtMl) ? fmtCount * fmtMl : null;

  const packChanged = (
    (formCount !== '' && fmtCount !== curCount) ||
    (formMl    !== '' && fmtMl    !== curMl)
  );
  const priceChanged = (
    (formPrice   !== '' && Number(formPrice)   !== invData?.bar_price) ||
    (formMeasure !== '' && Number(formMeasure) !== invData?.bar_price_measure_ml) ||
    (formEffDate !== '' && formEffDate         !== invData?.bar_price_effective_from)
  );

  return (
    <div className="mt-2 border border-slate-200 rounded-lg bg-slate-50 text-xs" data-testid={`site-settings-panel-${row.mc_id}`}>
      <button
        className="w-full flex items-center justify-between px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        onClick={handleOpen}
        data-testid={`site-settings-toggle-${row.mc_id}`}
        type="button"
      >
        <span className="font-medium flex items-center gap-1.5">
          <Settings size={12} /> Settings for this site
          {curCount && <span className="text-slate-400 font-normal">
            {curCount}×{curMl ? `${curMl}ml` : '?'}{curTotal ? ` = ${curTotal}ml/case` : ''}
          </span>}
          {invData?.bar_price && <span className="text-slate-400 font-normal ml-1">· £{invData.bar_price.toFixed(2)}</span>}
        </span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-4">
          {loadingInv && <p className="text-slate-400 italic text-[11px]">Loading site data…</p>}
          {!siteId && <p className="text-amber-600 text-[11px]">Site not resolved — settings unavailable until run site is linked.</p>}

          {!loadingInv && siteId && (
            <>
              {/* ── Pack / unit size section ── */}
              <div>
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Purchase pack &amp; unit size</p>
                {/* Three-column comparison */}
                <div className="grid grid-cols-3 gap-1.5 mb-2 text-[11px]">
                  <div className="rounded border border-blue-100 bg-blue-50 p-1.5">
                    <p className="text-[9px] font-semibold text-blue-500 uppercase mb-0.5">PS source suggests</p>
                    {propCount && propMl
                      ? <span className="text-blue-700">{propCount} × {propMl}ml = {propTotal}ml</span>
                      : <span className="text-slate-400">—</span>}
                    {row.source_size_raw && <p className="text-[9px] text-slate-400 mt-0.5">Raw: {row.source_size_raw}</p>}
                    <p className="text-[9px] text-slate-400 italic mt-0.5">Not applied automatically</p>
                  </div>
                  <div className="rounded border border-slate-200 bg-white p-1.5">
                    <p className="text-[9px] font-semibold text-slate-500 uppercase mb-0.5">Master default</p>
                    {masterCount || masterMl
                      ? <span className="text-slate-600">{masterCount ?? '?'} × {masterMl ? `${masterMl}ml` : '?'}</span>
                      : <span className="text-slate-400">—</span>}
                  </div>
                  <div className="rounded border border-emerald-100 bg-emerald-50 p-1.5">
                    <p className="text-[9px] font-semibold text-emerald-600 uppercase mb-0.5">Saved site value</p>
                    {curCount && curMl
                      ? <span className="text-emerald-700 font-medium">{curCount} × {curMl}ml = {curTotal}ml</span>
                      : curCount
                        ? <span className="text-slate-600">{curCount} units (no ml set)</span>
                        : <span className="text-slate-400 italic">Master default applies</span>}
                    {invData?.case_override_reason && (
                      <p className="text-[9px] text-slate-400 truncate mt-0.5" title={invData.case_override_reason}>
                        {invData.case_override_reason.slice(0, 40)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Pack edit form */}
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Units/case</label>
                    <input type="number" min="1" step="1"
                      className="h-7 w-14 border border-slate-300 rounded text-xs px-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      placeholder={curCount ?? propCount ?? '—'}
                      value={formCount} onChange={e => setFormCount(e.target.value)}
                      data-testid={`site-settings-count-${row.mc_id}`}
                    />
                  </div>
                  <span className="text-slate-400 pb-0.5">×</span>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Unit size (ml)</label>
                    <input type="number" min="1" step="1"
                      className="h-7 w-20 border border-slate-300 rounded text-xs px-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      placeholder={curMl ?? propMl ?? '—'}
                      value={formMl} onChange={e => setFormMl(e.target.value)}
                      data-testid={`site-settings-ml-${row.mc_id}`}
                    />
                  </div>
                  {fmtTotal && <span className="text-[11px] text-slate-500 pb-0.5 font-mono">= {fmtTotal}ml/case</span>}
                  <div className="flex-1 min-w-[120px]">
                    <label className="text-[10px] text-slate-500 block mb-0.5">Note (optional)</label>
                    <input type="text"
                      className="h-7 w-full border border-slate-300 rounded text-xs px-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      placeholder="Reason…"
                      value={formNote} onChange={e => setFormNote(e.target.value)}
                      data-testid={`site-settings-note-${row.mc_id}`}
                    />
                  </div>
                  <div className="flex gap-1">
                    <button onClick={handleCancelPack} type="button"
                      className="h-7 px-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                      disabled={!packChanged}
                      data-testid={`site-settings-cancel-${row.mc_id}`}
                    >Cancel</button>
                    <button onClick={handleSavePack} disabled={saving}
                      className="h-7 px-2.5 rounded bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-40"
                      data-testid={`site-settings-save-${row.mc_id}`}
                    >
                      {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save'}
                    </button>
                  </div>
                </div>
                {err && <p className="text-red-600 text-[11px] mt-1">{err}</p>}
              </div>

              {/* ── Bar price section ── */}
              <div>
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Bar price &amp; measure</p>
                <div className="grid grid-cols-2 gap-1.5 mb-2 text-[11px]">
                  <div className="rounded border border-slate-200 bg-white p-1.5">
                    <p className="text-[9px] font-semibold text-slate-500 uppercase mb-0.5">PS source price</p>
                    {row.source_barprice
                      ? <span className="text-slate-700">£{Number(row.source_barprice).toFixed(2)}</span>
                      : <span className="text-slate-400">—</span>}
                    <p className="text-[9px] text-slate-400 italic mt-0.5">Not applied automatically</p>
                  </div>
                  <div className="rounded border border-emerald-100 bg-emerald-50 p-1.5">
                    <p className="text-[9px] font-semibold text-emerald-600 uppercase mb-0.5">Saved dated price</p>
                    {invData?.bar_price != null
                      ? <>
                          <span className="text-emerald-700 font-medium">£{invData.bar_price.toFixed(2)}</span>
                          {invData.bar_price_measure_ml && <span className="text-slate-500"> / {invData.bar_price_measure_ml}ml</span>}
                          {invData.bar_price_effective_from && <p className="text-[9px] text-slate-400 mt-0.5">Effective: {invData.bar_price_effective_from}</p>}
                        </>
                      : <span className="text-slate-400 italic">No site price on record</span>}
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Bar price (£)</label>
                    <input type="number" min="0" step="0.01"
                      className="h-7 w-20 border border-slate-300 rounded text-xs px-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      placeholder={invData?.bar_price != null ? String(invData.bar_price) : '0.00'}
                      value={formPrice} onChange={e => setFormPrice(e.target.value)}
                      data-testid={`site-settings-price-${row.mc_id}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Measure (ml)</label>
                    <input type="number" min="1" step="1"
                      className="h-7 w-16 border border-slate-300 rounded text-xs px-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      placeholder={invData?.bar_price_measure_ml != null ? String(invData.bar_price_measure_ml) : '500'}
                      value={formMeasure} onChange={e => setFormMeasure(e.target.value)}
                      data-testid={`site-settings-measure-${row.mc_id}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Effective from</label>
                    <input type="date"
                      className="h-7 border border-slate-300 rounded text-xs px-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      value={formEffDate} onChange={e => setFormEffDate(e.target.value)}
                      data-testid={`site-settings-effdate-${row.mc_id}`}
                    />
                  </div>
                  <div className="flex gap-1">
                    <button onClick={handleCancelPrice} type="button"
                      className="h-7 px-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                      disabled={!priceChanged}
                      data-testid={`site-settings-price-cancel-${row.mc_id}`}
                    >Cancel</button>
                    <button onClick={handleSavePrice} disabled={savingPrice || !formPrice}
                      className="h-7 px-2.5 rounded bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-40"
                      data-testid={`site-settings-price-save-${row.mc_id}`}
                    >
                      {savingPrice ? 'Saving…' : savedPrice ? 'Saved ✓' : 'Save price'}
                    </button>
                  </div>
                </div>
                {priceErr && <p className="text-red-600 text-[11px] mt-1">{priceErr}</p>}
              </div>

              {/* Serving measures (display-only) */}
              {invData?.serving_measures?.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Serving measures</p>
                  <ul className="text-[11px] text-slate-600 space-y-0.5">
                    {invData.serving_measures.map((m, i) => (
                      <li key={i}>{m.description} — {m.quantity_ml}ml</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────


const SQUAD_META = {
  B:           { label: "B · Strong Suggestion",      badge: "bg-amber-500",   ring: "border-amber-300", text: "text-amber-800" },
  c_staff:     { label: "C·staff",                    badge: "bg-blue-600",    ring: "border-blue-200",  text: "text-blue-800"  },
  c_ammar:     { label: "C·owner",                    badge: "bg-rose-600",    ring: "border-rose-300",  text: "text-rose-800"  },
  d_structural:{ label: "D·structural",               badge: "bg-slate-500",   ring: "border-slate-200", text: "text-slate-700" },
  d_new:       { label: "D·new proposal",             badge: "bg-violet-600",  ring: "border-violet-200",text: "text-violet-800"},
};

const SQUAD_TABS = [
  { key: "c_staff",      short: "Staff Queue" },
  { key: "c_ammar",      short: "Owner Queue" },
  { key: "d_structural", short: "Exclusions"  },
  { key: "B",            short: "Batch B"     },
];

function StaffCorrectionPanel({ s1RunId, siteId, onOpenBatchReview, onSelectRow }) {
  const [open, setOpen]   = useState(true);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab]     = useState("c_staff");
  const [exclDone, setExclDone] = useState({});
  // Persistent "Add to Product Master" modal state — keyed by mc_id
  const [newProductRow, setNewProductRow] = useState(null);
  // AI prefill state for the evidence-card modal (fetched per-row on open)
  const [newProductAIPrefill, setNewProductAIPrefill] = useState(null);
  const [newProductAIPrefillLoading, setNewProductAIPrefillLoading] = useState(false);

  const handleOpenNewProductFromEvidenceCard = useCallback(async (row) => {
    setNewProductRow(row);
    setNewProductAIPrefill(null);           // reset for new row
    setNewProductAIPrefillLoading(true);
    try {
      const res = await api.get(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${row.mc_id}/ai-prefill`
      );
      setNewProductAIPrefill(res.data);
    } catch (e) {
      setNewProductAIPrefill({ enabled: false, prefill: null, advisory: null, error: String(e) });
    } finally {
      setNewProductAIPrefillLoading(false);
    }
  }, [s1RunId]);
  // Group 14 exclusion form state: {[mc_id]: {done: bool, scope, reason} | null}

  const handleExclDone = useCallback(({ mc_id, scope, reason }) => {
    setExclDone(prev => ({ ...prev, [mc_id]: { scope, reason } }));
    setTimeout(() => loadRef.current?.(), 800);
  }, []);

  const loadRef = useRef(null);

  const load = useCallback(async () => {
    if (!s1RunId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/admin/psb-stage1b/${s1RunId}/staff-queue`
      );
      setData(d);
    } catch { /* supplementary panel — silent fail */ }
    finally { setLoading(false); }
  }, [s1RunId]);

  // Keep ref to latest load so handleExclDone can call it without stale closure
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => { load(); }, [load]);

  if (!data && !loading) return null;

  const cc = data?.count_check ?? {};
  const total = data?.total_pending ?? 0;

  // Flat cost-priority-sorted list for "all C+D" view
  const allActionable = data
    ? [
        ...(data.sub_buckets.c_staff?.rows     || []),
        ...(data.sub_buckets.c_ammar?.rows     || []),
        ...(data.sub_buckets.d_structural?.rows|| []),
        ...(data.sub_buckets.B?.rows           || []),
      ].sort((a, b) => (b.cost_priority || 0) - (a.cost_priority || 0))
    : [];

  const currentBucket = data?.sub_buckets?.[tab];
  const rows = currentBucket?.rows || [];
  const totals = currentBucket?.totals || {};

  return (
    <div className="mb-5 border border-slate-200 rounded-lg bg-white shadow-sm" data-testid="staff-correction-panel">
      {/* ── Header ── */}
      <button
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-50 transition-colors rounded-t-lg"
        onClick={() => setOpen(o => !o)}
        data-testid="staff-queue-toggle"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <Users size={15} className="text-slate-500 shrink-0" />
          <span className="font-semibold text-slate-800 text-sm">Items needing attention</span>
          {data && (
            <>
              <span className="text-xs font-medium px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded">
                Staff review {cc.c_staff}
              </span>
              <span className="text-xs font-medium px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded">
                Owner review {cc.c_ammar}
              </span>
              <span className="text-xs font-medium px-2 py-0.5 bg-slate-50 text-slate-600 border border-slate-200 rounded">
                Structural {cc.d_structural}
              </span>
              <span className="text-xs text-slate-400">
                {total} pending
              </span>
            </>
          )}

        </div>
        <div className="flex items-center gap-2 shrink-0">
          {loading && <RefreshCw size={13} className="animate-spin text-slate-400" />}
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>

      {open && data && (
        <div className="border-t border-slate-200">
          {/* ── Tab bar ── */}
          <div className="flex items-center gap-0 border-b border-slate-200 bg-slate-50/50 px-4 overflow-x-auto">
            {SQUAD_TABS.map(t => {
              const bkt = data.sub_buckets[t.key];
              const cnt = bkt?.totals?.count ?? 0;
              const m   = SQUAD_META[t.key];
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`squad-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap
                    ${tab === t.key
                      ? "border-slate-700 text-slate-800 bg-white"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"}`}
                >
                  <span className={`w-4 h-4 rounded-sm flex items-center justify-center text-white text-[10px] font-bold ${m.badge}`}>
                    {cnt}
                  </span>
                  {t.short}
                </button>
              );
            })}
            <button onClick={load} className="ml-auto px-3 py-2 text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
              <RefreshCw size={11}/> Refresh
            </button>
          </div>

          {/* ── Tab description + totals strip ── */}
          {currentBucket && (
            <div className="px-5 py-2 bg-slate-50 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-slate-600 max-w-2xl">{currentBucket.description}</p>
              <div className="flex gap-4 text-xs text-slate-500 shrink-0">
                <span title="Stock cost: S2+S3+S6a">cost <b className="text-slate-700 tabular-nums">{fmt(totals.cost_total)}</b></span>
                <span title="S4 PLU revenue — retail domain, separate" className="text-amber-600">
                  PLU rev <b className="tabular-nums">{fmt(totals.s4_plu_revenue)}</b>
                </span>
              </div>
            </div>
          )}

          {/* ── Top 5 High-Cost Staff Exceptions callout (c_staff tab only) ── */}
          {tab === "c_staff" && rows.length > 0 && (() => {
            const top5 = [...rows]
              .sort((a, b) => (b.cost_priority || 0) - (a.cost_priority || 0))
              .slice(0, 5);
            // Disposition display tags — read-only, owner confirms separately
            const DISP = {
              map:       { cls: "bg-blue-100 text-blue-800 border-blue-300",   label: "map" },
              exclude:   { cls: "bg-red-100 text-red-700 border-red-300",      label: "exclude" },
              escalate:  { cls: "bg-amber-100 text-amber-800 border-amber-300",label: "escalate" },
            };
            return (
              <div className="mx-4 mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="high-cost-staff-callout">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={13} className="text-blue-600 shrink-0"/>
                  <span className="text-xs font-semibold text-blue-800">5 Highest-Cost Staff Exceptions</span>
                  <span className="text-[10px] text-blue-500 italic">— display only · owner confirms disposition separately</span>
                </div>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-slate-500 text-left">
                      <th className="pr-3 pb-1 font-medium">PLU</th>
                      <th className="pr-3 pb-1 font-medium">Description</th>
                      <th className="pr-3 pb-1 font-medium text-right">Cost £</th>
                      <th className="pb-1 font-medium">Suggested dispositions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top5.map(r => (
                      <tr key={r.mc_id} className="border-t border-blue-100" data-testid={`high-cost-row-${r.mc_id}`}>
                        <td className="pr-3 py-1.5 font-mono text-slate-600">{r.itemcode}</td>
                        <td className="pr-3 py-1.5 font-medium text-slate-800 max-w-[160px] truncate" title={r.source_description}>{r.source_description}</td>
                        <td className="pr-3 py-1.5 text-right tabular-nums font-semibold text-slate-700">{fmt(r.cost_priority)}</td>
                        <td className="py-1.5">
                          <div className="flex gap-1 flex-wrap">
                            {Object.entries(DISP).map(([key, d]) => (
                              <span key={key}
                                className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-medium ${d.cls}`}
                                data-testid={`disp-tag-${key}-${r.mc_id}`}
                                title={`Suggested disposition: ${d.label} — display only, owner must confirm`}
                              >
                                {d.label}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-blue-500 mt-2 italic">
                  Dispositions are read-only suggestions. Click any row in the table below to open the full resolution panel.
                </p>
              </div>
            );
          })()}

          {/* ── Row table ── */}
          <div className="overflow-x-auto">
            {rows.length === 0 ? (
              <div className="px-5 py-6 text-center text-slate-400 text-sm italic">
                No rows in this bucket.
              </div>
            ) : (
              <table className="w-full text-xs border-collapse">
                <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-500 w-20">PLU</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-500">Description</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-500 w-10">Grp</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-500 w-12">Pack</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-500 w-14">Conf</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-500 w-22" title="Stock cost S2+S3+S6a">Cost</th>
                    <th className="px-3 py-2 text-right font-medium text-amber-600 w-22" title="S4 PLU revenue — retail domain">PLU Rev</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-500 w-20">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const ev = row.evidence_card;
                    const excl = row.exclusion_reason_template;
                    const m = SQUAD_META[row.sub_bucket || (row.match_status === "strong_suggestion" ? "B" : "c_staff")];
                    return (
                      <React.Fragment key={row.mc_id}>
                        <tr
                          className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${i % 2 === 0 ? "" : "bg-slate-50/30"}`}
                          onClick={() => tab === "c_staff" && onSelectRow && onSelectRow(row)}
                          data-testid={`squad-row-${row.mc_id}`}
                        >
                          <td className="px-3 py-2 font-mono text-slate-600">{row.itemcode}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              {m && (
                                <span className={`shrink-0 px-1 py-0.5 text-[9px] font-bold text-white rounded ${m.badge}`}>
                                  {m.label}
                                </span>
                              )}
                              <span className="font-medium text-slate-800">{row.source_description}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-slate-500">{row.group_code}</td>
                          <td className="px-3 py-2 text-slate-500">{row.size_raw || "—"}</td>
                          <td className="px-3 py-2 text-right"><ConfBadge conf={row.confidence} /></td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-700">{fmt(row.cost_priority)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmt(row.s4_plu_revenue)}</td>
                          <td className="px-3 py-2">
                            {tab === "c_staff" && (
                              <button
                                onClick={(e) => { e.stopPropagation(); onSelectRow && onSelectRow(row); }}
                                data-testid={`squad-review-btn-${row.mc_id}`}
                                className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-medium"
                              >
                                <Eye size={10}/> Review
                              </button>
                            )}
                            {tab === "B" && (
                              <button
                                onClick={(e) => { e.stopPropagation(); onOpenBatchReview && onOpenBatchReview(); }}
                                data-testid={`squad-batch-btn-${row.mc_id}`}
                                className="flex items-center gap-1 px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-[10px] font-medium"
                              >
                                <CheckSquare size={10}/> Batch
                              </button>
                            )}
                            {(tab === "c_ammar" || tab === "d_structural") && (
                              <span className="text-[10px]">
                                {tab === "c_ammar" ? (
                                  <button
                                    data-testid={`owner-open-resolution-btn-${row.mc_id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onSelectRow && onSelectRow(row);
                                    }}
                                    title="Open the full candidates panel for this item — no decision submitted"
                                    className="flex items-center gap-1 px-2 py-0.5 bg-slate-400 hover:bg-slate-500 text-white rounded text-[10px] font-medium"
                                  >
                                    <Eye size={10} /> Candidates
                                  </button>
                                ) : (
                                  row.exclusion_type === "food_and_snacks"
                                    ? <button
                                        data-testid={`g14-review-btn-${row.mc_id}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExclDone(prev => {
                                            const cur = prev[row.mc_id];
                                            if (cur && cur.scope) return prev; // already done
                                            return { ...prev, [row.mc_id]: cur === undefined ? null : undefined };
                                          });
                                        }}
                                        className="px-2 py-0.5 bg-slate-600 hover:bg-slate-700 text-white rounded text-[10px] font-medium"
                                      >Review</button>
                                    : <span className="text-slate-400 italic">Blocked</span>
                                )}
                              </span>
                            )}
                          </td>
                        </tr>

                        {/* Evidence card (c_ammar only) — persistent "Add to Product Master" action */}
                        {ev && (
                          <tr className="border-t border-rose-100 bg-rose-50/40" data-testid={`evidence-card-${row.mc_id}`}>
                            <td colSpan={8} className="px-3 py-2.5">
                              <div className="flex flex-col gap-2">
                                {/* Brand context + decision question */}
                                <div className="flex flex-wrap items-baseline gap-3 text-xs">
                                  <span className="font-bold text-rose-700 uppercase tracking-wide">{ev.brand_class}</span>
                                  <span className="font-semibold text-rose-800">{ev.decision_question}</span>
                                </div>

                                {/* Pre-confirm pills — show what the owner has already confirmed for this item */}
                                {_OWNER_PREFILLS[String(row.itemcode)] && (
                                  <div className="flex flex-wrap gap-1.5" data-testid={`owner-prefill-pills-${row.mc_id}`}>
                                    {_OWNER_PREFILLS[String(row.itemcode)].proposed_name && (
                                      <ConfirmedPill label="Name" value={_OWNER_PREFILLS[String(row.itemcode)].proposed_name} />
                                    )}
                                    {_OWNER_PREFILLS[String(row.itemcode)].pack_confirmed && (
                                      <ConfirmedPill label="Pack" value={`${_OWNER_PREFILLS[String(row.itemcode)].pack_qty}${_OWNER_PREFILLS[String(row.itemcode)].pack_type} (${_OWNER_PREFILLS[String(row.itemcode)].pack_label})`} />
                                    )}
                                    {!_OWNER_PREFILLS[String(row.itemcode)].pack_confirmed && (
                                      <UnknownPill label="Pack size" />
                                    )}
                                    {_OWNER_PREFILLS[String(row.itemcode)].selling_confirmed && (
                                      <ConfirmedPill label="Serving" value={_OWNER_PREFILLS[String(row.itemcode)].selling_label} />
                                    )}
                                    {_OWNER_PREFILLS[String(row.itemcode)].classification_hint && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
                                        {_OWNER_PREFILLS[String(row.itemcode)].provisional
                                          ? <AlertTriangle size={9} className="text-amber-600"/>
                                          : <Info size={9}/>
                                        }
                                        {_OWNER_PREFILLS[String(row.itemcode)].classification_hint}
                                        {_OWNER_PREFILLS[String(row.itemcode)].provisional && " (provisional)"}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Bottom bar: cost/PLU + persistent "Add to Product Master" action */}
                                <div className="flex flex-wrap items-center gap-3 text-xs">
                                  <button
                                    data-testid={`evidence-add-product-btn-${row.mc_id}`}
                                    onClick={(e) => { e.stopPropagation(); handleOpenNewProductFromEvidenceCard(row); }}
                                    className="flex items-center gap-1.5 px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-[11px] font-semibold shrink-0"
                                    title="Open the governed Add to Product Master form — AI field suggestions load automatically"
                                  >
                                    <PlusCircle size={11}/> Add product
                                  </button>
                                  <span className="ml-auto flex gap-4 shrink-0">
                                    <span className="text-rose-700 font-medium tabular-nums" title="Stock cost: S2 opening + S3 deliveries + S6a closing">
                                      stock cost: <b>{fmt(row.cost_priority)}</b>
                                    </span>
                                    <span className="text-amber-700 font-medium tabular-nums" title="S4 PLU revenue — retail domain, separate">
                                      PLU rev: <b>{fmt(ev.retail_domain?.s4_plu_revenue)}</b>
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Exclusion card (d_structural Group 14) */}
                        {tab === "d_structural" && row.exclusion_type === "food_and_snacks" && (
                          exclDone[row.mc_id] ? (
                            /* Audit trail — shown after successful submission */
                            <tr className="border-t border-green-100 bg-green-50/40" data-testid={`g14-excl-done-${row.mc_id}`}>
                              <td colSpan={8} className="px-3 py-2">
                                <span className="text-xs text-green-700 font-medium">
                                  Exclusion recorded — scope: <b>{exclDone[row.mc_id].scope}</b> · reason: "{exclDone[row.mc_id].reason}"
                                </span>
                              </td>
                            </tr>
                          ) : exclDone[row.mc_id] !== undefined ? (
                            /* Review panel open */
                            <tr className="border-t border-slate-100 bg-slate-50/60" data-testid={`g14-excl-form-row-${row.mc_id}`}>
                              <td colSpan={8} className="px-0 py-0">
                                <Group14ExclusionCard
                                  row={row}
                                  s1RunId={s1RunId}
                                  onDone={handleExclDone}
                                />
                              </td>
                            </tr>
                          ) : null
                        )}

                        {/* Exclusion reason template (d_structural non-Group-14 only) */}
                        {excl && !(tab === "d_structural" && row.exclusion_type === "food_and_snacks") && (
                          <tr className="border-t border-slate-100 bg-slate-50/50">
                            <td colSpan={8} className="px-3 py-1.5">
                              <span className="text-[11px] text-slate-500 italic">
                                Reason template: "{excl}"
                              </span>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="border-t border-slate-200 bg-slate-50">
                  <tr>
                    <td colSpan={5} className="px-3 py-1.5 text-right text-xs text-slate-400 font-medium">
                      {rows.length} rows · totals:
                    </td>
                    <td className="px-3 py-1.5 text-right text-xs font-bold text-slate-700 tabular-nums">
                      {fmt(totals.cost_total)}
                    </td>
                    <td className="px-3 py-1.5 text-right text-xs font-bold text-amber-700 tabular-nums">
                      {fmt(totals.s4_plu_revenue)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* ── Invariant footer ── */}
          <div className="px-5 py-2 bg-amber-50 border-t border-amber-100 rounded-b-lg flex items-center justify-between">
            <p className="text-xs text-amber-700 font-medium">
              {total} pending{" "}
              <span className="text-amber-600/80 font-normal">
                — {cc.c_staff} staff review · {cc.c_ammar} owner review · {cc.d_structural} structural · {cc.B} strong suggestion · {cc.A} exact
              </span>
            </p>
          </div>
        </div>
      )}

      {open && loading && (
        <div className="flex items-center justify-center py-8 text-slate-400 text-sm border-t border-slate-100">
          <RefreshCw size={14} className="animate-spin mr-2" /> Building staff queue…
        </div>
      )}

      {/* Persistent "Add to Product Master" modal — opened from evidence card */}
      {newProductRow && (
        <GoverNedNewProductModal
          row={newProductRow}
          s1RunId={s1RunId}
          siteId={siteId}
          aiPrefill={newProductAIPrefill}
          aiPrefillLoading={newProductAIPrefillLoading}
          onSuccess={(d) => {
            setNewProductRow(null);
            setNewProductAIPrefill(null);
            // Reload queue so the row shows as approved
            if (typeof load === "function") load();
          }}
          onCancel={() => { setNewProductRow(null); setNewProductAIPrefill(null); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function DispositionQueuePanel({ s1RunId, onSelectRow }) {
  const [open, setOpen]     = useState(true);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!s1RunId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/admin/psb-stage1b/${s1RunId}/disposition-queue`
      );
      setData(d);
      // Auto-expand non-empty groups
      const ex = {};
      (d.groups || []).forEach(g => { if (g.count > 0) ex[g.class] = true; });
      setExpanded(ex);
    } catch { /* silently ignore — queue is supplementary */ }
    finally { setLoading(false); }
  }, [s1RunId]);

  useEffect(() => { load(); }, [load]);

  if (!data && !loading) return null;

  const toggleGroup = (cls) => setExpanded(e => ({ ...e, [cls]: !e[cls] }));

  return (
    <div className="mb-6 border border-slate-200 rounded-lg bg-white shadow-sm" data-testid="disposition-queue-panel">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-50 transition-colors rounded-t-lg"
        onClick={() => setOpen(o => !o)}
        data-testid="dq-toggle-btn"
      >
        <div className="flex items-center gap-3">
          <GitBranch size={16} className="text-slate-500" />
          <span className="font-semibold text-slate-800 text-sm">Residual Disposition Queue</span>
          {data && (
            <span className="text-xs text-slate-500">
              {data.total_pending} pending · priority basis: stock cost (S2+S3+S6a)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {loading && <RefreshCw size={13} className="animate-spin text-slate-400" />}
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-200 divide-y divide-slate-100">
          {/* Summary strip */}
          {data && (
            <div className="px-5 py-2 flex flex-wrap gap-3 bg-slate-50 border-b border-slate-100">
              {data.groups.map(g => {
                const m = DISP_META[g.class] || {};
                return (
                  <div key={g.class} className="flex items-center gap-1.5 text-xs">
                    <span className={`w-2 h-2 rounded-full ${m.dot}`} />
                    <span className="text-slate-600 font-medium">{g.label}:</span>
                    <span className="font-semibold text-slate-800">{g.count}</span>
                  </div>
                );
              })}
              {data.domain_totals && (
                <div className="ml-auto flex gap-4 text-xs text-slate-500 border-l border-slate-200 pl-3">
                  <span>S2 open: <b>{fmt(data.domain_totals.s2_opening_cost)}</b></span>
                  <span>S3 deliv: <b>{fmt(data.domain_totals.s3_deliveries_cost)}</b></span>
                  <span>S3 credit: <b>{fmt(data.domain_totals.s3_credits)}</b></span>
                  <span>S6a close: <b>{fmt(data.domain_totals.s6a_closing_cost)}</b></span>
                  <span className="text-slate-400">|</span>
                  <span className="text-amber-700">S4 PLU rev (separate): <b>{fmt(data.domain_totals.s4_plu_revenue)}</b></span>
                </div>
              )}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
              <RefreshCw size={14} className="animate-spin mr-2" /> Building queue…
            </div>
          )}

          {/* Per-class accordions */}
          {data && data.groups.map(g => {
            if (g.count === 0) return null;
            const m = DISP_META[g.class] || {};
            const isOpen = expanded[g.class];
            return (
              <div key={g.class}>
                <button
                  className="w-full flex items-center justify-between px-5 py-2.5 text-left hover:bg-slate-50 transition-colors"
                  onClick={() => toggleGroup(g.class)}
                  data-testid={`dq-group-${g.class}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${m.dot}`} />
                    <span className="text-sm font-medium text-slate-700">{g.label}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${m.color}`}>{g.count}</span>
                    <span className="text-xs text-slate-400">{g.description}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {g.domain_totals && (
                      <span className="text-slate-400">
                        S2 {fmt(g.domain_totals.s2_open_cost)} · S3 {fmt(g.domain_totals.s3_deliv_cost)} · S6a {fmt(g.domain_totals.s6a_close_cost)} · PLU {fmt(g.domain_totals.s4_plu_revenue)}
                      </span>
                    )}
                    {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead className="bg-slate-50 border-y border-slate-100">
                        <tr>
                          <th className="px-3 py-1.5 text-left font-medium text-slate-500 w-20">PLU</th>
                          <th className="px-3 py-1.5 text-left font-medium text-slate-500">Description</th>
                          <th className="px-3 py-1.5 text-left font-medium text-slate-500 w-16">Grp</th>
                          <th className="px-3 py-1.5 text-left font-medium text-slate-500 w-14">Pack</th>
                          <th className="px-3 py-1.5 text-left font-medium text-slate-500">Candidate</th>
                          <th className="px-3 py-1.5 text-right font-medium text-slate-500 w-14">Conf</th>
                          <th className="px-3 py-1.5 text-right font-medium text-slate-500 w-20" title="S2 opening stock cost at WAC">S2 Open</th>
                          <th className="px-3 py-1.5 text-right font-medium text-slate-500 w-20" title="S3 delivery cost (positive) / credits (negative)">S3 Deliv</th>
                          <th className="px-3 py-1.5 text-right font-medium text-slate-500 w-20" title="S6a closing stock cost at WAC">S6a Close</th>
                          <th className="px-3 py-1.5 text-right font-medium text-amber-600 w-20" title="S4 till revenue — separate retail dimension, not stock cost">S4 PLU Rev</th>
                          <th className="px-3 py-1.5 text-right font-medium text-slate-500 w-20" title="Stock-domain review priority (S2+S3+S6a)">Priority</th>
                          <th className="px-3 py-1.5 text-left font-medium text-slate-500 w-36">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map((row, i) => (
                          <tr
                            key={row.mc_id}
                            className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${i % 2 === 0 ? "" : "bg-slate-50/50"}`}
                            onClick={() => onSelectRow && onSelectRow(row)}
                            data-testid={`dq-row-${row.mc_id}`}
                          >
                            <td className="px-3 py-2 font-mono text-slate-600">{row.itemcode}</td>
                            <td className="px-3 py-2 font-medium text-slate-800">
                              {row.source_description}
                              {row.is_duplicate_itemcode && (
                                <span className="ml-1 text-orange-500" title="Duplicate itemcode in this run">(dup)</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-500">{row.group_code} · {row.group_name?.split('/')[0]?.trim() || ''}</td>
                            <td className="px-3 py-2 text-slate-500">{row.size_raw || '—'}</td>
                            <td className="px-3 py-2 text-slate-600">
                              {row.matched_bp_name
                                ? <span>{row.matched_bp_name} <span className="text-slate-400">bp={row.matched_bp_id}</span></span>
                                : <span className="text-slate-400 italic">no match</span>
                              }
                            </td>
                            <td className="px-3 py-2 text-right"><ConfBadge conf={row.confidence} /></td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmt(row.s2_open_cost)}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                              {row.s3_deliv_rows > 0
                                ? <span title={`${row.s3_deliv_rows} rows`}>{fmt(row.s3_deliv_cost)}{row.s3_credit_cost < 0 && <span className="text-slate-400 text-xs ml-0.5">/{fmt(row.s3_credit_cost)}</span>}</span>
                                : <span className="text-slate-300">—</span>
                              }
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-600">{row.s6a_close_cost !== 0 ? fmt(row.s6a_close_cost) : <span className="text-slate-300">—</span>}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmt(row.s4_plu_revenue)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{fmt(row.review_priority_score)}</td>
                            <td className="px-3 py-2">
                              <span className={`text-xs font-medium ${m.actionColor}`}>{m.action}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t border-slate-200 bg-slate-50">
                        <tr>
                          <td colSpan={6} className="px-3 py-1.5 text-right text-xs text-slate-500 font-medium">Group totals:</td>
                          <td className="px-3 py-1.5 text-right text-xs font-bold text-slate-700 tabular-nums">{fmt(g.domain_totals?.s2_open_cost)}</td>
                          <td className="px-3 py-1.5 text-right text-xs font-bold text-slate-700 tabular-nums">{fmt(g.domain_totals?.s3_deliv_cost)}</td>
                          <td className="px-3 py-1.5 text-right text-xs font-bold text-slate-700 tabular-nums">{fmt(g.domain_totals?.s6a_close_cost)}</td>
                          <td className="px-3 py-1.5 text-right text-xs font-bold text-amber-700 tabular-nums">{fmt(g.domain_totals?.s4_plu_revenue)}</td>
                          <td className="px-3 py-1.5 text-right text-xs font-bold text-slate-700 tabular-nums">{fmt(g.domain_totals?.review_priority)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* Footer note */}
          {data && (
            <div className="px-5 py-2.5 bg-amber-50 rounded-b-lg flex items-center justify-between border-t border-amber-100">
              <p className="text-xs text-amber-700 font-medium">
                All buckets are CANDIDATES only — no bucket is an approved outcome. Priority column = stock-domain cost (S2+S3+S6a). S4 PLU revenue is a separate retail figure — it is NOT added to stock cost.
              </p>
              <button onClick={load} className="ml-4 flex-shrink-0 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                <RefreshCw size={11} /> Refresh
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Bulk strong-suggestion panel ──────────────────────────────────────────────

function BulkStrongPanel({ s1RunId, onDone, onClose }) {
  const [rows, setRows]         = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Per-row results after a confirmed approval (data.per_row_results)
  const [approvalResults, setApprovalResults] = useState(null);
  // Guard against rapid double-click — single in-flight confirm at a time
  const confirmingRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/admin/psb-stage1b/${s1RunId}/exceptions?match_status=strong_suggestion&decision=pending&page_size=100`
        );
        setRows(data.rows || []);
      } catch { toast.error("Failed to load strong suggestions"); }
      finally { setLoading(false); }
    };
    load();
  }, [s1RunId]);

  const toggleRow = (id) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const handlePreview = async () => {
    if (!selected.size) { toast.warning("Select at least one row"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(
        `/admin/psb-stage1b/${s1RunId}/bulk-approve-strong`,
        { candidate_ids: [...selected], confirmed: false }
      );
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const handleApprove = async () => {
    if (!preview) return;
    // Double-submit guard: reject second click while first is in flight
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirming(true);
    try {
      const { data } = await api.post(
        `/admin/psb-stage1b/${s1RunId}/bulk-approve-strong`,
        { candidate_ids: [...selected], confirmed: true }
      );
      toast.success(`Approved ${data.approved_count} strong suggestions`);
      // Show per-row audit results
      setApprovalResults(data.per_row_results || []);
      setPreview(null);
      setSelected(new Set());
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setConfirming(false);
      confirmingRef.current = false;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" data-testid="bulk-strong-panel">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800">Bulk Approve Strong Suggestions</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Select individual rows to approve. Each must pass all hard conflict gates.
              Rows with gate conflicts are excluded automatically.
              No one-click approve-all.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XCircle size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <RefreshCw size={18} className="animate-spin mr-2" /> Loading…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">No pending strong suggestions</div>
          )}
          {!loading && rows.length > 0 && (
            <table className="w-full text-xs border-collapse">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-2 text-left w-8"></th>
                  <th className="px-2 py-2 text-left">Code</th>
                  <th className="px-2 py-2 text-left">Description</th>
                  <th className="px-2 py-2 text-left">Matched Product</th>
                  <th className="px-2 py-2 text-right">Conf</th>
                  <th className="px-2 py-2 text-right">PLU inc-VAT</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.mc_id} className={`border-t border-slate-100 ${selected.has(r.mc_id) ? "bg-indigo-50" : ""}`}>
                    <td className="px-2 py-2">
                      <button onClick={() => toggleRow(r.mc_id)} data-testid={`strong-select-${r.mc_id}`}>
                        {selected.has(r.mc_id) ? <CheckSquare size={14} className="text-indigo-600"/> : <Square size={14} className="text-slate-400"/>}
                      </button>
                    </td>
                    <td className="px-2 py-2 font-mono">{r.itemcode}</td>
                    <td className="px-2 py-2 font-medium">{r.source_description}</td>
                    <td className="px-2 py-2 text-slate-600">{r.matched_bp_name || "—"}</td>
                    <td className="px-2 py-2 text-right"><ConfBadge conf={r.confidence}/></td>
                    <td className="px-2 py-2 text-right font-mono">{fmt(r.plu_value_inc_vat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Impact preview */}
        {preview && (
          <div className="border-t border-amber-200 bg-amber-50 px-5 py-3">
            <div className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
              <AlertTriangle size={13}/> Impact Preview — review before confirming
            </div>
            <div className="flex flex-wrap gap-4 text-xs mb-2">
              <span>Approvable: <strong>{preview.impact?.approvable_count}</strong></span>
              <span>Excluded by gate conflict: <strong className="text-red-700">{preview.impact?.excluded_by_gate}</strong></span>
              <span>PLU inc-VAT: <strong>{fmt(preview.impact?.total_plu_inc_vat)}</strong></span>
              <span>Open cost: <strong>{fmt(preview.impact?.total_open_cost)}</strong></span>
              <span>Close cost: <strong>{fmt(preview.impact?.total_close_cost)}</strong></span>
            </div>
            {preview.excluded_by_gate?.length > 0 && (
              <details className="text-xs text-red-700 mb-2">
                <summary className="cursor-pointer">Show {preview.excluded_by_gate.length} excluded-by-gate rows</summary>
                <ul className="mt-1 space-y-0.5 ml-3">
                  {preview.excluded_by_gate.map(r => (
                    <li key={r.mc_id}>{r.itemcode}: {r.reason}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">{selected.size} of {rows.length} selected</span>
          <div className="flex gap-2">
            <button
              onClick={handlePreview}
              disabled={!selected.size || loading}
              className="px-4 py-2 border border-amber-400 text-amber-700 rounded text-sm hover:bg-amber-50 disabled:opacity-50"
              data-testid="bulk-strong-preview-btn"
            >
              Review Impact
            </button>
            <button
              onClick={handleApprove}
              disabled={!preview || confirming || loading}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm disabled:opacity-50"
              data-testid="bulk-strong-confirm-btn"
            >
              {confirming ? "Approving…" : `Confirm Approval (${preview?.impact?.approvable_count ?? 0})`}
            </button>
          </div>
        </div>

        {/* Per-row audit results after confirmation */}
        {approvalResults && approvalResults.length > 0 && (
          <div className="border-t border-slate-200 px-5 py-3 max-h-48 overflow-y-auto" data-testid="bulk-strong-per-row-results">
            <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-emerald-600" /> Per-row approval audit
            </div>
            <table className="w-full text-xs border-collapse">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-2 py-1 text-left">Code</th>
                  <th className="px-2 py-1 text-left">Matched Product</th>
                  <th className="px-2 py-1 text-center">Conf</th>
                  <th className="px-2 py-1 text-left">Gates</th>
                </tr>
              </thead>
              <tbody>
                {approvalResults.map(r => (
                  <tr key={r.mc_id} className="border-t border-slate-100" data-testid={`bulk-result-row-${r.mc_id}`}>
                    <td className="px-2 py-1 font-mono">{r.itemcode}</td>
                    <td className="px-2 py-1 text-slate-600">{r.matched_bp_name || "—"}</td>
                    <td className="px-2 py-1 text-center"><ConfBadge conf={r.confidence}/></td>
                    <td className="px-2 py-1">
                      {r.gates_summary ? (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(r.gates_summary).map(([gate, g]) => (
                            <span key={gate} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${g.passed ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
                              title={g.reason || ''}>
                              {gate === 'pack_size' ? 'Pack' : gate.charAt(0).toUpperCase() + gate.slice(1)}
                              {g.passed ? ' ✓' : ' ✗'}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-[10px]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Coverage bar ─────────────────────────────────────────────────────────────

function CoverageBar({ coverage }) {
  if (!coverage) return null;
  const { total_candidates, approved_count, held_count } = coverage;
  const approvedPct = total_candidates ? (approved_count / total_candidates) * 100 : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mb-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700">Mapping Coverage</span>
        <span className="text-sm text-slate-500">
          {approved_count} approved / {held_count} pending / {total_candidates} total
        </span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500 rounded-full transition-all duration-500"
          style={{ width: `${approvedPct}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-emerald-700 font-medium">{approvedPct.toFixed(1)}% approved</span>
        <span className="text-xs text-amber-700">{held_count} still pending</span>
      </div>
    </div>
  );
}

// ── Evidence panel (expandable) ───────────────────────────────────────────────

function EvidencePanel({ row }) {
  return (
    <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 text-xs text-slate-600 space-y-2">
      <div className="font-semibold text-slate-800 mb-1">Evidence</div>
      {row.reasons && row.reasons.length > 0 ? (
        <ul className="space-y-1 list-disc list-inside">
          {row.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      ) : (
        <span className="italic text-slate-400">No reasons recorded</span>
      )}
      {row.conflict_count > 0 && (
        <div className="mt-2">
          <span className="font-semibold text-amber-700">
            {row.conflict_count} conflicting candidate{row.conflict_count !== 1 ? "s" : ""}
          </span>
          {Array.isArray(row.conflicts) && row.conflicts.slice(0, 3).map((c, i) => (
            <div key={i} className="mt-1 text-slate-500">
              ↳ {typeof c === "object" ? JSON.stringify(c).slice(0, 80) : c}
            </div>
          ))}
        </div>
      )}
      {row.ai_suggestion && (
        <div className="mt-2 italic text-indigo-600">AI note: {row.ai_suggestion}</div>
      )}
      <div className="mt-2 pt-2 border-t border-slate-200 flex flex-wrap gap-4 text-slate-500">
        <span>PLU inc-VAT: {fmt(row.plu_value_inc_vat)}</span>
        <span>Open cost: {fmt(row.open_cost)}</span>
        <span>Close cost: {fmt(row.close_cost)}</span>
        <span>Bar price: {fmt(row.source_barprice)}</span>
        {row.open_wac > 0 && <span>WAC: {fmt(row.open_wac)}</span>}
      </div>
    </div>
  );
}

// ── Strict exact-matches owner-review group ──────────────────────────────────
//
// Lists the subset of pending rows that the resolver has classified 'exact'
// (normalised source name identical to active master name AND every hard
// identity gate passes).  Nothing is preselected; owner ticks rows or Review
// All to tick the visible set, then clicks Confirm.  Confirm routes through
// the existing per-row approve path — same audit trail, same gates.
//
// No bulk control is offered for strong_suggestion, review_required, blocked
// or proposed_new rows.
function StrictExactReview({ s1RunId, payload, selected, setSelected, onConfirm, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const items = payload?.items || [];
  const toggleAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.mc_id)));
    }
  };
  const toggleOne = (mc_id) => {
    const next = new Set(selected);
    if (next.has(mc_id)) next.delete(mc_id); else next.add(mc_id);
    setSelected(next);
  };
  const handleConfirm = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Confirm ${selected.size} strict exact match${selected.size === 1 ? "" : "es"}? Each row will be routed through the standard approve path with full gate re-check.`)) return;
    setSubmitting(true);
    try {
      await onConfirm(Array.from(selected));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="rounded-lg border border-emerald-300 bg-white" data-testid="strict-exact-review-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-emerald-100 bg-emerald-50">
        <div>
          <div className="text-sm font-semibold text-emerald-900">
            Strict exact matches — owner review group ({items.length})
          </div>
          <div className="text-xs text-emerald-800 mt-0.5">
            Nothing is preselected. Tick rows to confirm; each confirm re-runs full identity gates server-side.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleAll}
            data-testid="strict-exact-toggle-all"
            className="px-3 py-1.5 text-xs border border-emerald-300 text-emerald-800 rounded hover:bg-emerald-100"
          >
            {selected.size === items.length && items.length > 0 ? "Clear all" : "Review all"}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selected.size || submitting}
            data-testid="strict-exact-confirm-btn"
            className="px-3 py-1.5 text-xs bg-emerald-700 hover:bg-emerald-800 text-white rounded disabled:opacity-40"
          >
            {submitting ? "Confirming…" : `Confirm selected (${selected.size})`}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs border border-slate-300 text-slate-600 rounded hover:bg-slate-50"
            data-testid="strict-exact-close-btn"
          >
            Close
          </button>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map(item => (
          <div
            key={item.mc_id}
            className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50"
            data-testid={`strict-exact-item-${item.mc_id}`}
          >
            <input
              type="checkbox"
              checked={selected.has(item.mc_id)}
              onChange={() => toggleOne(item.mc_id)}
              className="mt-1 w-4 h-4"
              data-testid={`strict-exact-check-${item.mc_id}`}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-slate-500">#{item.itemcode}</span>
                <span className="font-semibold text-sm text-slate-800">{item.source.description}</span>
                <ChevronRight size={13} className="text-slate-400" />
                <span className="text-sm text-emerald-900 font-medium">{item.target_master.product_name}</span>
                <span className="text-xs text-slate-500">
                  (code {item.target_master.product_code} · {item.target_master.subcategory})
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Source GROUP {item.source.group_code} ({item.source.group_name}),
                size {item.source.size_raw},
                {item.source.yield_raw ? ` yield ${item.source.yield_raw},` : ""}
                {" "}cat {item.target_master.cat_slug}.
                {" "}<span className="text-emerald-700">{item.evidence_line}</span>
              </div>
            </div>
          </div>
        ))}
        {!items.length && (
          <div className="px-4 py-6 text-center text-sm text-slate-400 italic">
            No strict exact matches currently pending.
          </div>
        )}
      </div>
    </div>
  );
}


// ── Before/after cascade result ───────────────────────────────────────────────

function CascadeResult({ result }) {
  if (!result) return null;
  const ba = result.before_after || {};
  const stages = [
    { key: "s2",  label: "S2 Opening"  },
    { key: "s4",  label: "S4 Sales"    },
    { key: "s6a", label: "S6A Closing" },
  ];
  const totalDelta = stages.reduce((acc, { key }) => {
    const b = ba[key]?.before || {};
    const a = ba[key]?.after  || {};
    return acc + ((a.importable || 0) - (b.importable || 0));
  }, 0);
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-4 text-sm" data-testid="cascade-result">
      <div className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
        <GitBranch size={15} />
        {totalDelta > 0
          ? `Cascade complete — ${totalDelta} new rows propagated`
          : "Cascade complete — all approved rows already propagated (idempotent)"}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-emerald-100 text-emerald-800">
              <th className="px-3 py-1.5 text-left">Stage</th>
              <th className="px-3 py-1.5 text-right">Before importable</th>
              <th className="px-3 py-1.5 text-right">Before held</th>
              <th className="px-3 py-1.5 text-right">After importable</th>
              <th className="px-3 py-1.5 text-right">After held</th>
              <th className="px-3 py-1.5 text-right">Δ importable</th>
            </tr>
          </thead>
          <tbody>
            {stages.map(({ key, label }) => {
              const b = ba[key]?.before || {};
              const a = ba[key]?.after  || {};
              const delta = (a.importable || 0) - (b.importable || 0);
              return (
                <tr key={key} className="border-t border-emerald-200">
                  <td className="px-3 py-1.5 font-medium text-emerald-900">{label}</td>
                  <td className="px-3 py-1.5 text-right">{b.importable ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right text-amber-700">{b.held ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-700 font-medium">{a.importable ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right text-amber-700">{a.held ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right font-semibold text-emerald-700">
                    {delta > 0 ? `+${delta}` : delta}
                  </td>
                </tr>
              );
            })}
            {ba.sos != null && (
              <tr className="border-t border-emerald-200 bg-emerald-50">
                <td className="px-3 py-1.5 font-medium text-emerald-900">Opening Stock rows</td>
                <td className="px-3 py-1.5 text-right">{ba.sos?.before ?? "—"}</td>
                <td className="px-3 py-1.5 text-right">—</td>
                <td className="px-3 py-1.5 text-right text-emerald-700 font-medium">{ba.sos?.after ?? "—"}</td>
                <td className="px-3 py-1.5 text-right">—</td>
                <td className="px-3 py-1.5 text-right font-semibold text-emerald-700">
                  {(ba.sos?.after - ba.sos?.before) > 0
                    ? `+${ba.sos.after - ba.sos.before}` : "0"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-xs text-emerald-700 space-y-1">
        <div>S3 documents resolved: <strong>{result.s3_docs_resolved}</strong>
          {result.s3_docs_resolved === 0 && " (all documents still mixed — partial lines remain held)"}
        </div>
        <div className="italic text-slate-500">{result.idempotency_note}</div>
      </div>
    </div>
  );
}

// ── Stage 1C: Post-Mix Review Panel ──────────────────────────────────────────

function PostmixPanel({ s1RunId, onApproved }) {
  const [data,     setData]    = useState(null);
  const [loading,  setLoading] = useState(false);
  const [expanded, setExpanded]= useState(true);
  const [approving,setApproving]=useState(new Set());

  const loadPanel = useCallback(async () => {
    if (!s1RunId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/admin/psb-stage1b/${s1RunId}/postmix-panel`
      );
      setData(d);
    } catch (e) {
      // Non-critical — panel silently hides if unavailable
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s1RunId]);

  useEffect(() => { loadPanel(); }, [loadPanel]);

  const handleApprove = async (row) => {
    const wb = row.workbook_match;
    if (!wb) return;
    setApproving(prev => new Set([...prev, row.mc_id]));
    try {
      await api.post(
        `/admin/psb-stage1b/${s1RunId}/postmix-panel/${row.mc_id}/approve`,
        {
          az_bp_id:              row.proposed_bp.bp_id,
          workbook_product_name: wb.name,
        }
      );
      toast.success(`Approved: ${row.description} → ${row.proposed_bp.product_name}`);
      loadPanel();
      if (onApproved) onApproved();
    } catch (e) {
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || e.message;
      toast.error(typeof msg === "object" ? JSON.stringify(msg) : String(msg));
    } finally {
      setApproving(prev => { const n = new Set(prev); n.delete(row.mc_id); return n; });
    }
  };

  // Only show when there are pending postmix strong_suggestion rows with workbook matches
  const approvableRows = (data?.rows || []).filter(
    r => r.proposed_bp && r.workbook_match && r.decision === "pending" && r.match_status === "strong_suggestion"
  );
  const reviewRows = (data?.rows || []).filter(
    r => r.decision === "pending" && (!r.proposed_bp || !r.workbook_match)
  );

  if (!data || (approvableRows.length === 0 && reviewRows.length === 0)) return null;

  return (
    <div className="mb-4 border border-teal-200 rounded-lg bg-teal-50/40" data-testid="postmix-panel">
      <button
        onClick={() => setExpanded(x => !x)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        data-testid="postmix-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <FlaskConical size={14} className="text-teal-700" />
          <span className="text-sm font-semibold text-teal-800">
            Post-Mix Products
          </span>
          <span className="text-xs bg-teal-100 border border-teal-200 text-teal-700 rounded px-1.5 py-0.5">
            {approvableRows.length} ready · {reviewRows.length} needs research
          </span>
          {loading && <RefreshCw size={11} className="animate-spin text-teal-500" />}
        </div>
        {expanded ? <ChevronUp size={14} className="text-teal-600"/> : <ChevronDown size={14} className="text-teal-600"/>}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          <p className="text-xs text-teal-700 mb-3">
            Post-mix rows need explicit approval — verify the product and case size before confirming.
            Rows with an automatic match are shown first; rows without a match need manual attention.
          </p>

          {/* Approvable rows — workbook match found */}
          {approvableRows.map(row => {
            const wb   = row.workbook_match;
            const bp   = row.proposed_bp;
            const busy = approving.has(row.mc_id);
            return (
              <div key={row.mc_id}
                className="bg-white border border-teal-200 rounded-lg p-3"
                data-testid={`postmix-row-${row.mc_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {/* Source */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        {row.itemcode}
                      </span>
                      <span className="text-sm font-semibold text-slate-800">{row.description}</span>
                      <span className="text-xs text-slate-500">size={row.size_raw?.trim() || "—"}</span>
                    </div>

                    {/* Evidence grid */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-slate-600 mt-1 mb-2">
                      <div>
                        <span className="text-slate-400">Workbook entry: </span>
                        <span className="font-medium text-teal-700">{wb.name}</span>
                        <span className="ml-1 text-slate-400">(score {(wb.score * 100).toFixed(0)}%)</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Carb : Syrup = </span>
                        <span className="font-mono">{wb.ratio_carb} : {wb.ratio_syrup}</span>
                        <span className="text-slate-400 ml-1">· {wb.litres_in_box}L/box</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Yield / litre: </span>
                        <span className="font-mono font-medium">{wb.yield_per_litre?.toFixed(2)} servings</span>
                        <span className="text-slate-400 ml-1">({wb.glass_ml?.toFixed(0)} ml glass)</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Source yield: </span>
                        <span className="font-mono">{row.yield_raw ?? "—"}</span>
                        <span className="text-slate-400 ml-1">· Purchase basis: </span>
                        <span className="font-mono font-medium text-teal-700">{row.purchase_basis_ml} ml</span>
                      </div>
                      <div>
                        <span className="text-slate-400">PLU sales: </span>
                        <span className="font-mono">£{row.plu_value_inc_vat?.toFixed(2)}</span>
                        <span className="text-slate-400"> ({row.plu_qty?.toFixed(0)} qty)</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Open/Close cost: </span>
                        <span className="font-mono">£{row.open_cost?.toFixed(2)} / £{row.close_cost?.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Proposed product */}
                    <div className="flex items-center gap-2 mt-1">
                      <ArrowRight size={12} className="text-teal-500 flex-shrink-0" />
                      <span className="text-xs text-slate-500">Proposed: </span>
                      <span className="text-sm font-semibold text-teal-800">{bp.product_name}</span>
                      <span className="text-xs text-slate-400 font-mono">AZ {bp.product_code}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded border ${
                        bp.has_litre_uom
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                          : "bg-amber-50 border-amber-200 text-amber-700"
                      }`}>
                        {bp.has_litre_uom
                          ? `${row.purchase_basis_ml ?? "?"}ml UOM exists`
                          : `${row.purchase_basis_ml ?? "?"}ml UOM will be added`}
                      </span>
                    </div>
                  </div>

                  {/* Approve button */}
                  <button
                    onClick={() => handleApprove(row)}
                    disabled={busy}
                    data-testid={`postmix-approve-btn-${row.mc_id}`}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium rounded disabled:opacity-50 transition-colors"
                  >
                    {busy ? <RefreshCw size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>}
                    {busy ? "Approving…" : "Approve"}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Review-required rows — no workbook match */}
          {reviewRows.length > 0 && (
            <div className="mt-1">
              <p className="text-xs font-semibold text-slate-500 mb-1">
                {reviewRows.length} row{reviewRows.length > 1 ? "s" : ""} need attention (no automatic match found):
              </p>
              {reviewRows.map(row => (
                <div key={row.mc_id}
                  className="flex items-center gap-2 px-2 py-1 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800 mb-1"
                  data-testid={`postmix-review-row-${row.mc_id}`}
                >
                  <AlertTriangle size={11}/>
                  <span className="font-mono text-orange-600">{row.itemcode}</span>
                  <span className="font-medium">{row.description}</span>
                  <span className="text-orange-500">— use Exceptions table to research and map manually</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



export default function PsbStage1BPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-pinned run — set when navigating from Stage 1 diagnostic tools
  const urlS1RunId = searchParams.get('s1_run_id');
  // site_id is passed by PsbStage1Page for Back-navigation continuity
  const urlSiteId  = searchParams.get('site_id');
  // conversion_id is set when navigating from the Route A wizard — used so
  // Continue returns to the Route A wizard rather than the Route B progress page.
  const urlConversionId = searchParams.get('conversion_id');
  // filter/view state encoded in URL so navigate-away-and-back restores context
  const urlViewMode        = searchParams.get('viewMode');
  const urlFilterDecision  = searchParams.get('filterDecision');
  // category/subcategory filter state preserved across Back navigation
  const urlFilterCategoryId = searchParams.get('filterCategoryId');
  const urlFilterSubcatId   = searchParams.get('filterSubcatId');

  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [s1RunId, setS1RunId] = useState(null);
  const [pinnedRunError, setPinnedRunError] = useState(null);
  // Req 3: truthful loading state — true until the first loadSessions() completes
  const [isInitializing, setIsInitializing] = useState(true);

  const [exceptions, setExceptions]   = useState([]);
  const [coverage, setCoverage]       = useState(null);
  const [filterOptions, setFilterOptions] = useState({ match_statuses: [], group_codes: [] });
  // Item 5: AbbeyZone taxonomy hierarchy (loaded per run, replaces legacy group codes)
  const [taxonomyHierarchy, setTaxonomyHierarchy] = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const PAGE_SIZE = 50;

  const [filterStatus,      setFilterStatus]      = useState("");
  const [filterCategoryId,  setFilterCategoryId]  = useState(urlFilterCategoryId || "");
  const [filterSubcatId,    setFilterSubcatId]     = useState(urlFilterSubcatId   || "");
  const [filterDecision,    setFilterDecision]     = useState(urlFilterDecision   || "pending");
  const [filterSearch,      setFilterSearch]       = useState("");
  const [expandedRows,  setExpandedRows]  = useState(new Set());
  const [selected,      setSelected]      = useState(new Set());

  const [loading,       setLoading]       = useState(false);
  const [cascadeLoading,setCascadeLoading]= useState(false);
  const [cascadeResult, setCascadeResult] = useState(null);

  // Progressive disclosure state
  const [viewMode,           setViewMode]           = useState(urlViewMode || "needs_review"); // "needs_review" | "all" | "strict_exact"
  const [showAdminDiagnostics, setShowAdminDiagnostics] = useState(false);
  const [autoAdvancePending,   setAutoAdvancePending]   = useState(false);

  // Strict exact-matches payload (grouped review)
  const [strictExact,        setStrictExact]        = useState(null); // {total, items[], preselected}
  const [strictExactLoading, setStrictExactLoading] = useState(false);
  const [strictSelected,     setStrictSelected]     = useState(new Set()); // mc_ids owner has ticked
  const [rerankLoading,      setRerankLoading]      = useState(false);

  // Single-click expand: itemcode to auto-expand after next exceptions load
  const [pendingExpandItemcode, setPendingExpandItemcode] = useState(null);

  // ── v2: Resolution state ──────────────────────────────────────────────────
  const [detailData,    setDetailData]    = useState({});  // mc_id → detail
  const [detailLoading, setDetailLoading] = useState(new Set());
  const [showBulkStrong,setShowBulkStrong]= useState(false);
  const [showPriceReview, setShowPriceReview] = useState(false);
  const [excludeTarget, setExcludeTarget] = useState(null);  // full row object

  // Gate override state — populated when backend returns hard_conflict_gate_failed
  // (variant/category gate failed but admin override IS permitted).
  const [overrideState, setOverrideState] = useState(null);
  // shape: { mcId, action, azBpId, failedGates: string[], errorMessage: string }

  // Issue 6: active summary tile for filter highlighting
  const [activeTile, setActiveTile] = useState(null);

  // This page is admin-only (all its /api/admin/psb-stage1b/* endpoints
  // require is_admin on the backend).  The UI-level isAdmin constant mirrors
  // that gate so conditional admin-only panels (batch-prefill, bridge-preview)
  // are only rendered when the user has successfully loaded the sessions list.
  // We derive from successful session load rather than reading auth tokens.
  const isAdmin = sessions.length > 0 || !!s1RunId;
  const [resolvingMcIds, setResolvingMcIds] = useState(new Set());
  const markResolving    = useCallback((id) => setResolvingMcIds(s => new Set([...s, id])), []);
  const unmarkResolving  = useCallback((id) => setResolvingMcIds(s => { const n = new Set(s); n.delete(id); return n; }), []);

  // ── Package A: batch AI prefill state ────────────────────────────────────
  // Tracks the in-progress batch prefill; does NOT auto-accept anything.
  // null = idle; object = in-progress or completed summary
  const [batchPrefill, setBatchPrefill] = useState(null);
  // shape: { running: bool, total: int, queued: int, completed: int, failed: int, skipped: int }

  // Lightweight read-only status from the /batch-prefill-status endpoint.
  // Persists across navigation because it reflects DB truth (re-fetched on run change).
  // null = not yet checked; object = coverage summary from backend
  const [batchStatus, setBatchStatus] = useState(null);
  // shape: { enabled, total_eligible, cached, decided, uncached_eligible, failed, all_done, none_needed }
  const batchInFlightRef = useRef(false);  // guards against duplicate auto-triggers

  // Bridge preview panel — toggled from header, visible per-run
  const [showBridgePanel, setShowBridgePanel] = useState(false);

  // ── Fetch sessions ────────────────────────────────────────────────────────

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/psb-stage1b/sessions');
      const list = data.sessions || [];
      // Sort sessions alphabetically by site name (case-insensitive) so the
      // selector presents a consistent, findable list regardless of API order.
      const sorted = [...list].sort((a, b) =>
        (a.site_name || '').localeCompare(b.site_name || '', undefined, { sensitivity: 'base' })
      );
      setSessions(sorted);

      if (urlS1RunId) {
        // Pinned mode: find the exact session, fail closed if not present — never fall back
        const pinned = list.find(s => s.s1_run_id === urlS1RunId);
        if (pinned) {
          setSelectedSession(pinned);
          setS1RunId(pinned.s1_run_id);
        } else {
          // Not in sessions list — could be pre-import state (S2/S4 not yet applied).
          // Try the nav-context endpoint which works from Stage 1 data alone.
          try {
            const { data: ctx } = await api.get(
              `/admin/psb-stage1b/runs/${urlS1RunId}/nav-context`
            );
            // Build a synthetic session so the page can render identity + stats
            const syntheticSession = {
              s1_run_id:              ctx.s1_run_id,
              site_name:              ctx.target_site_name,
              session_reference:      `${ctx.source_house}${ctx.source_internal_name ? ` — ${ctx.source_internal_name}` : ''}`,
              total_candidates:       ctx.total_candidates,
              approved_count:         ctx.approved_count,
              held_count:             ctx.held_count,
              review_required_count:  ctx.review_required_count,
              import_route:           ctx.import_route ?? null,
              pre_import:             true,
            };
            setSessions(prev => {
              // Only add if not already present
              if (prev.find(s => s.s1_run_id === ctx.s1_run_id)) return prev;
              return [syntheticSession, ...prev];
            });
            setSelectedSession(syntheticSession);
            setS1RunId(ctx.s1_run_id);
          } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || 'run not found or has no match candidates';
            setPinnedRunError(
              `Run ${urlS1RunId.slice(0, 8)} cannot be loaded for review: ${detail}. ` +
              'Do not select a different run for this stocktake.'
            );
          }
        }
        return;
      }

      if (list.length === 1 && !selectedSession) {
        // Only auto-select when there is exactly one unambiguous session
        setSelectedSession(list[0]);
        setS1RunId(list[0].s1_run_id);
      }
      // Multiple sessions: always require an explicit choice — no approval-count ranking,
      // no sessionStorage restore. The reviewer must pick the run they intend to work on.
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired — please refresh the page to continue");
      } else {
        toast.error("Failed to load sessions");
      }
    } finally {
      // Req 3: always clear initializing flag so loading state is truthful
      setIsInitializing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Item 5: load AbbeyZone taxonomy hierarchy whenever a run is selected
  useEffect(() => {
    if (!s1RunId) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/psb-stage1b/runs/${s1RunId}/taxonomy-categories`
        );
        setTaxonomyHierarchy(data.categories || []);
      } catch (_) {
        // Non-critical — taxonomy filter stays empty on error
        setTaxonomyHierarchy([]);
      }
    })();
  }, [s1RunId]);

  // ── Auto-check AI batch status when run is selected ──────────────────────
  // Resets per-run state, checks DB coverage, then auto-triggers if any
  // eligible rows are uncached. Guards against duplicate in-flight triggers.
  // Navigating away and back re-fetches from DB (the source of truth) without
  // repeating paid AI calls for already-cached rows.
  const checkBatchStatus = useCallback(async (runId) => {
    if (!runId) return;
    try {
      const { data } = await api.get(
        `/admin/psb-stage1b/${runId}/exception-candidates/batch-prefill-status`
      );
      setBatchStatus(data);
      return data;
    } catch (e) {
      // Non-fatal: status unavailable (e.g. pre-import state)
      setBatchStatus({ enabled: false, _error: e?.response?.data?.detail || e.message });
      return null;
    }
  }, []);

  useEffect(() => {
    if (!s1RunId) { setBatchStatus(null); setBatchPrefill(null); batchInFlightRef.current = false; return; }
    checkBatchStatus(s1RunId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s1RunId]);

  // ── A2: Live progress polling while batch is in-flight ────────────────────
  // Polls batch-prefill-status every 4 s so the "N of M ready" counter reflects
  // actual DB progress, not just the pre-batch snapshot. Polling stops as soon
  // as batchPrefill.running becomes false (batch finished or failed).
  const jobRunning = batchStatus?.job?.state === 'running';
  const loadExceptionsRef = useRef(null);
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (!jobRunning || !s1RunId) return;
    const poll = setInterval(() => { checkBatchStatus(s1RunId); }, 3000);
    return () => clearInterval(poll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobRunning, s1RunId]);
  // When a run finishes, refresh the queue so new suggestions show and report the honest outcome.
  useEffect(() => {
    const job = batchStatus?.job;
    if (wasRunningRef.current && job && job.state !== 'running') {
      if (job.succeeded > 0) loadExceptionsRef.current?.();
      if (job.failed > 0) toast.warning(`${job.succeeded} of ${job.eligible} suggestions loaded, ${job.failed} failed — use Retry.`);
      else if (job.state === 'completed') toast.success(`${job.succeeded} of ${job.eligible} suggestions loaded.`);
    }
    wasRunningRef.current = jobRunning;
  }, [batchStatus?.job, jobRunning]);

  // ── AI pre-fill is user-initiated only — no auto-trigger ──────────────────
  // Previously this fired automatically the moment uncached_eligible > 0,
  // including on a passive page revisit/resume. For a run with many pending
  // rows (e.g. a carried-forward or partially-worked run) that silently
  // started several minutes of concurrent AI + DB work the user never asked
  // for, degrading the whole app while it ran. Opening/resuming this page no
  // longer starts any work; the user must press "Get AI suggestions" /
  // "Load missing" (handleBatchPrefill) explicitly. batchStatus still loads
  // read-only so the amber "N suggestions needed" state renders correctly.

  // ── Fetch exceptions ─────────────────────────────────────────────────────
  // AbortController ref: aborts any in-flight request the moment a new filter
  // selection fires, preventing stale responses from overwriting current state.
  const exceptionsAbortRef = useRef(null);

  const loadExceptions = useCallback(async () => {
    if (!s1RunId) return;
    // Cancel any previous in-flight request immediately.
    if (exceptionsAbortRef.current) {
      exceptionsAbortRef.current.abort();
    }
    const controller = new AbortController();
    exceptionsAbortRef.current = controller;

    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
      // In "needs_review" mode, the virtual filter handles status+decision server-side
      if (viewMode === "needs_review") {
        params.set("match_status", "needs_review");
      } else {
        if (filterStatus)   params.set("match_status", filterStatus);
        if (filterDecision) params.set("decision",     filterDecision);
      }
      if (filterCategoryId) params.set("category_id",    filterCategoryId);
      if (filterSubcatId)   params.set("subcategory_id", filterSubcatId);
      if (filterSearch)   params.set("search",       filterSearch);
      const { data } = await api.get(
        `/admin/psb-stage1b/${s1RunId}/exceptions?${params}`,
        { signal: controller.signal }
      );
      // Only update state if this request is still the latest (not aborted).
      if (!controller.signal.aborted) {
        setExceptions(data.rows || []);
        setCoverage(data.coverage);
        setFilterOptions(data.filter_options || { match_statuses: [], group_codes: [] });
        setTotal(data.total || 0);
      }
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') return;
      toast.error("Failed to load exceptions");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s1RunId, page, viewMode, filterStatus, filterCategoryId, filterSubcatId, filterDecision, filterSearch]);
  loadExceptionsRef.current = loadExceptions;

  useEffect(() => { loadExceptions(); }, [loadExceptions]);

  // ── Load strict-exact-matches payload on run select / refresh ─────────────
  const loadStrictExact = useCallback(async () => {
    if (!s1RunId) return;
    setStrictExactLoading(true);
    try {
      const { data } = await api.get(
        `/admin/psb-stage1b/${s1RunId}/strict-exact-matches`
      );
      setStrictExact(data);
      // Nothing preselected — explicit requirement.
      setStrictSelected(new Set());
    } catch (e) {
      // Non-fatal; banner just stays hidden
      setStrictExact(null);
    } finally {
      setStrictExactLoading(false);
    }
  }, [s1RunId]);
  useEffect(() => { loadStrictExact(); }, [loadStrictExact]);

  // ── Idempotent rerank — explicit owner action, no auto-approvals ──────────
  const handleRerankPending = useCallback(async () => {
    if (!s1RunId || rerankLoading) return;
    setRerankLoading(true);
    try {
      const { data } = await api.post(
        `/admin/psb-stage1b/${s1RunId}/rerank-pending`,
        {}
      );
      toast.success(
        `Rerank complete — ${data.changed} re-classified, ` +
        `${data.unchanged_short_circuit} unchanged.`
      );
      await Promise.all([loadExceptions(), loadStrictExact()]);
    } catch (e) {
      toast.error("Rerank failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRerankLoading(false);
    }
  }, [s1RunId, rerankLoading, loadExceptions, loadStrictExact]);

  // ── Auto-reconcile site inventory for already-approved rows on run select ──
  // Idempotent — safe to repeat. Backfills any rows approved before site
  // assignment was introduced. Runs silently in background; never blocks UX.
  useEffect(() => {
    if (!s1RunId) return;
    api.post(
      `/admin/psb-stage1b/${s1RunId}/reconcile-site-inventory`,
      {}
    ).catch(e => console.warn("[PSB] Reconcile site inventory:", e?.response?.data || e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s1RunId]);

  // Auto-expand a specific row after exceptions reload (used by Staff Queue single-click Review)
  useEffect(() => {
    if (!pendingExpandItemcode || exceptions.length === 0) return;
    const target = exceptions.find(e => e.itemcode === pendingExpandItemcode);
    if (target) {
      setPendingExpandItemcode(null);
      handleToggleExpand(target.mc_id);
      setTimeout(() => {
        document.getElementById(`exception-row-${target.mc_id}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exceptions, pendingExpandItemcode]);

  // Reset page when filters or view mode change
  useEffect(() => { setPage(1); }, [viewMode, filterStatus, filterCategoryId, filterSubcatId, filterDecision, filterSearch]);

  // ── Approve ──────────────────────────────────────────────────────────────

  const handleApprove = async (mcIds, mode = "individual") => {
    if (!mcIds.length) return;
    // Task 3: mark each row as in-flight to prevent double-click
    mcIds.forEach(markResolving);
    try {
      await api.post(
        `/admin/psb-stage1b/${s1RunId}/approve`,
        { candidate_ids: mcIds, mode }
      );
      toast.success(`Approved ${mcIds.length} candidate${mcIds.length > 1 ? "s" : ""}`);
      setSelected(new Set());
      if (mcIds.length === 1 && mode === "individual") setAutoAdvancePending(true);
      loadExceptions();
    } catch (e) {
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || e.message;
      toast.error(typeof msg === "object" ? JSON.stringify(msg) : msg);
    } finally {
      mcIds.forEach(unmarkResolving);
    }
  };

  const handleUnapprove = async (mcIds) => {
    if (!mcIds.length) return;
    try {
      await api.post(
        `/admin/psb-stage1b/${s1RunId}/unapprove`,
        { candidate_ids: mcIds }
      );
      toast.success(`Reverted ${mcIds.length} to pending`);
      setSelected(new Set());
      loadExceptions();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  // Bulk approve only exact rows in current selection
  const handleBulkApprove = async () => {
    const exactIds = exceptions
      .filter(r => selected.has(r.mc_id) && r.match_status === "exact" && r.decision !== "approved")
      .map(r => r.mc_id);
    if (!exactIds.length) {
      toast.warning("No exact-match candidates selected — bulk approve requires exact-match only");
      return;
    }
    await handleApprove(exactIds, "bulk");
  };

  // Task 3: Confirm selected matches — works for ALL eligible (non-blocked, approvable) rows
  const handleConfirmSelectedMatches = async () => {
    const eligibleIds = exceptions
      .filter(r => selected.has(r.mc_id) && r.is_approvable && r.decision !== "approved")
      .map(r => r.mc_id);
    if (!eligibleIds.length) {
      toast.warning("No eligible candidates selected to confirm");
      return;
    }
    await handleApprove(eligibleIds, "individual");
  };

  // ── Cascade ──────────────────────────────────────────────────────────────

  const handleCascade = async () => {
    setCascadeLoading(true);
    setCascadeResult(null);
    try {
      const { data } = await api.post(
        `/admin/psb-stage1b/${s1RunId}/cascade`,
        {}
      );
      setCascadeResult(data);
      toast.success(
        `Cascade complete — ${data.s4_rows_updated} S4 rows, ${data.s6a_rows_updated} S6A rows, ` +
        `${data.s2_rows_updated} S2 rows, ${data.sos_rows_inserted} opening stock`
      );
      loadExceptions();
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setCascadeLoading(false);
    }
  };

  // ── v2: Load row detail (lazy) ────────────────────────────────────────────

  const loadRowDetail = useCallback(async (mcId, opts = {}) => {
    if (detailData[mcId] && !opts.forceRefresh) return;  // already loaded
    setDetailLoading(prev => { const n = new Set(prev); n.add(mcId); return n; });
    // api service refreshes the auth header on every request via its interceptor —
    // no stale-token risk even when the callback is memoised.
    try {
      const { data } = await api.get(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${mcId}`
      );
      setDetailData(prev => ({ ...prev, [mcId]: data }));
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired — please refresh the page to reload the evidence card");
      } else {
        toast.error("Failed to load candidate detail");
      }
    } finally {
      setDetailLoading(prev => { const n = new Set(prev); n.delete(mcId); return n; });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s1RunId, detailData]);

  const toggleExpand = useCallback((id) => setExpandedRows(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  }), []);

  const handleToggleExpand = useCallback((mcId) => {
    toggleExpand(mcId);
    loadRowDetail(mcId);
  }, [toggleExpand, loadRowDetail]);

  // ── Auto-advance: after a resolve/approve, find next pending item ──────────
  useEffect(() => {
    if (!autoAdvancePending || loading) return;
    const nextItem = exceptions.find(e => e.decision === "pending");
    if (nextItem) {
      setAutoAdvancePending(false);
      setExpandedRows(new Set([nextItem.mc_id]));
      loadRowDetail(nextItem.mc_id);
      setTimeout(() => {
        document.getElementById(`exception-row-${nextItem.mc_id}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } else {
      const totalPgs = Math.ceil(total / PAGE_SIZE);
      if (page < totalPgs) {
        // Advance to next page; autoAdvancePending stays true until next load
        setPage(p => p + 1);
      } else {
        setAutoAdvancePending(false);
        if (total > 0) toast.success("All items in this view have been reviewed.");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdvancePending, exceptions, loading]);

  const handleResolve = async (mcId, action, azBpId, adminOverrideReason = null) => {
    // Task 3: prevent double-submit
    if (resolvingMcIds.has(mcId)) return false;
    markResolving(mcId);
    try {
      await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${mcId}/resolve`,
        {
          action,
          az_bp_id: azBpId || null,
          ...(adminOverrideReason ? { admin_override_reason: adminOverrideReason } : {}),
        }
      );
      setOverrideState(null);  // clear any pending override on success
      toast.success(action === "approve" ? "Approved" : action === "map_to" ? "Mapped" : "Resolved");
      setDetailData(prev => { const n = {...prev}; delete n[mcId]; return n; });
      setAutoAdvancePending(true);
      loadExceptions();
      loadSessions();
      return true;  // signal success to callers (e.g. override panel button)
    } catch (e) {
      const d = e.response?.data?.detail;
      const errObj = typeof d === "object" && d !== null ? d : null;
      // Non-overridable gate failure (pack-size mismatch or blocked/proposed_new status)
      if (errObj?.error === "hard_conflict_gate_failed_no_override") {
        toast.error(errObj.message || "Gate conflict — override not permitted for pack-size or status block.");
        return false;
      }
      // All other failures (already_approved without reason, proposed_new, etc.)
      const msg = errObj?.message || (typeof d === "string" ? d : e.message);
      toast.error(msg);
      return false;
    } finally {
      unmarkResolving(mcId);
    }
  };

  const handleExcludeConfirm = async (mcId, reason, scope) => {
    try {
      await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${mcId}/resolve`,
        { action: "exclude", exclusion_reason: reason, exclusion_scope: scope }
      );
      toast.success("Row excluded");
      setExcludeTarget(null);
      setDetailData(prev => { const n = {...prev}; delete n[mcId]; return n; });
      // Reload both exceptions (updates CoverageBar) AND session list (updates
      // session dropdown pending count) so all three surfaces agree immediately.
      loadExceptions();
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  // ── v2: AI assist ─────────────────────────────────────────────────────────

  const handleAIAssist = async (mcId) => {
    try {
      const { data } = await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${mcId}/ai-assist`,
        {}
      );
      if (!data.enabled) {
        toast.info("AI assistance is disabled. Set PSB_AI_SUGGESTIONS=true to enable in Preview.");
        return;
      }
      if (data.suggestion) {
        setDetailData(prev => {
          const existing = prev[mcId] || {};
          return { ...prev, [mcId]: { ...existing, source: { ...(existing.source||{}), ai_suggestion: data.suggestion } } };
        });
        // Also refresh the exception list to show ai_suggestion in expanded panel
        loadExceptions();
        toast.success("AI analysis complete");
      } else {
        toast.info("AI analysis returned no suggestion for this row");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  // ── Package A: Batch AI prefill ───────────────────────────────────────────
  // Manually trigger batch prefill (Retry/Load missing). The auto-trigger
  // above fires at first opportunity; this handler is for manual retry of
  // missing/failed rows only. force=true re-processes cached rows too.
  // Advisory only — never accepts, maps or approves anything.
  const handleBatchPrefill = async ({ force = false } = {}) => {
    if (!s1RunId) return;
    if (batchInFlightRef.current) return; // prevent duplicate start
    batchInFlightRef.current = true;
    setBatchPrefill({ running: true, starting: true });
    try {
      const { data } = await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/batch-prefill${force ? '?force=true' : ''}`,
        {},
      );
      if (!data.enabled) {
        toast.info("AI is disabled — set PSB_AI_SUGGESTIONS=true to enable batch prefill.");
        setBatchPrefill(null);
        setBatchStatus(prev => prev ? { ...prev, enabled: false } : { enabled: false });
        return;
      }
      if (data.already_running) toast.info("An AI run is already in progress for this run — showing its progress.");
      else if (!data.started) toast.info("All eligible rows already have AI suggestions.");
      setBatchPrefill(null);
      await checkBatchStatus(s1RunId);
    } catch (e) {
      setBatchPrefill(null);
      toast.error(`Could not start AI suggestions: ${e.response?.data?.detail || e.message}`);
    } finally {
      batchInFlightRef.current = false;
    }
  };

  // ── v2: Research online ───────────────────────────────────────────────────

  const handleResearchOnline = async (mcId, barcode) => {
    try {
      const { data } = await api.post(
        `/admin/psb-stage1b/${s1RunId}/exception-candidates/${mcId}/research-online`,
        { barcode: barcode || null }
      );
      // Issue 1: reload detail WITHOUT deleting first so the panel stays
      // populated even if the reload fails.  The reload will overwrite with
      // fresh data on success; on failure the old data is preserved.
      loadRowDetail(mcId, { forceRefresh: true });
      if (data.evidence?.found) {
        toast.success(`Research found: ${data.evidence.product_name || "result"} (${data.data_quality})`);
      } else {
        toast.info(
          data.evidence?.network_unavailable
            ? "Network unavailable — research is optional and never blocks the workflow"
            : "No result found for this product"
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      // Panel stays open — detail is preserved, error shown as toast
    }
  };

  // ── CSV export ────────────────────────────────────────────────────────────

  const handleExportCsv = () => {
    if (!s1RunId) return;
    const params = new URLSearchParams({ decision: filterDecision || "pending" });
    if (filterStatus) params.set("match_status", filterStatus);
    window.open(
      `${API}/api/admin/psb-stage1b/${s1RunId}/export-csv?${params}&token=${token}`,
      "_blank"
    );
  };

  // ── Selection ────────────────────────────────────────────────────────────

  const toggleRow = (id) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleAll = () => {
    if (selected.size === exceptions.length) setSelected(new Set());
    else setSelected(new Set(exceptions.map(r => r.mc_id)));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const selectedList = [...selected];
  const selectionHasNonApprovable = selectedList.some(id => {
    const r = exceptions.find(e => e.mc_id === id);
    return r && !r.is_approvable;
  });
  const selectionHasApprovable = selectedList.some(id => {
    const r = exceptions.find(e => e.mc_id === id);
    return r && r.is_approvable && r.decision !== "approved";
  });
  const selectionHasExactOnly = selectedList.every(id => {
    const r = exceptions.find(e => e.mc_id === id);
    return r && r.match_status === "exact";
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Shield size={14} />
              <span className="font-semibold text-slate-800">Review product matches</span>
            </div>
            <p className="text-xs text-slate-500 max-w-2xl">
              Confirm each product match for this import run.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 py-6">

        {/* Session selector — hidden in pinned-run mode to prevent accidental switching */}
        {sessions.length > 0 && !urlS1RunId && (
          <div className="mb-5 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-slate-600">Review run:</span>
            <select
              className="border border-slate-300 rounded px-3 py-1.5 text-sm bg-white min-w-[28rem]"
              value={s1RunId || ""}
              onChange={e => {
                const val = e.target.value;
                if (!val) {
                  setSelectedSession(null);
                  setS1RunId(null);
                  return;
                }
                const s = sessions.find(x => x.s1_run_id === val);
                if (s) {
                  setSelectedSession(s);
                  setS1RunId(s.s1_run_id);
                  setPage(1);
                  setViewMode("needs_review");
                  setFilterDecision("pending");
                  setFilterStatus("");
                  setFilterSearch("");
                }
              }}
              data-testid="session-select"
            >
              <option value="">— Choose review run —</option>
              {sessions.map((s, idx) => {
                // Strip "— DO NOT USE" suffixes — display only.
                const cleanName = (s.site_name || '').replace(/[—–-]?\s*DO NOT USE\s*/gi, '').trim();
                const shortId   = s.s1_run_id.slice(0, 8);
                const pending   = s.held_count ?? (s.total_candidates - s.approved_count);
                const dateStr   = s.created_at
                  ? new Date(s.created_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })
                  : '';
                return (
                  <option key={`${s.s1_run_id}-${s.session_id || idx}`} value={s.s1_run_id}>
                    {shortId} · {s.session_reference} — {cleanName}{dateStr ? ` · ${dateStr}` : ''} · {s.approved_count} approved, {pending} pending
                  </option>
                );
              })}
            </select>
            {selectedSession?.last_cascade_at && (
              <span className="text-xs text-slate-400">
                Last cascade: {new Date(selectedSession.last_cascade_at).toLocaleString()}
              </span>
            )}
          </div>
        )}

        {/* Pinned-run isolation error — fail closed, shown above selector */}
        {pinnedRunError && (
          <div
            className="mb-5 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900"
            data-testid="pinned-run-error-banner"
          >
            <Shield size={15} className="shrink-0 text-red-600 mt-0.5" />
            <div>
              <span className="font-semibold">Run isolation — no data loaded. </span>
              {pinnedRunError}
            </div>
          </div>
        )}

        {/* Pre-import identity banner — shown when navigated from historical conversion but S2/S4 not yet applied */}
        {selectedSession?.pre_import && s1RunId && (
          <div
            className="mb-5 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
            data-testid="pre-import-identity-banner"
          >
            <Shield size={15} className="shrink-0 text-blue-500 mt-0.5" />
            <div>
              <span className="font-semibold">
                {selectedSession.session_reference || selectedSession.site_name}
                {selectedSession.site_name && selectedSession.session_reference !== selectedSession.site_name
                  ? ` \u2192 ${selectedSession.site_name}`
                  : ''}
              </span>
              {' '}&middot; Run {s1RunId.slice(0, 8)}
              {' '}&middot; {selectedSession.total_candidates} products
              {selectedSession.review_required_count != null
                ? `, ${selectedSession.review_required_count} need a decision`
                : ''}
              <div className="mt-0.5 text-blue-700 text-xs">
                {selectedSession.import_route === 'route_a'
                  ? 'The opening stock file has not been imported yet. You can review and approve product matches now; the opening stock position will populate once the file is imported. No sales or transaction data is imported on this route.'
                  : 'The opening stock and sales files have not been imported yet. You can review and approve product matches now; financial data columns will populate once those files are imported.'}
              </div>
            </div>
          </div>
        )}

        {/* Explicit-selection prompt — shown when multiple sessions exist and none chosen yet */}
        {sessions.length > 1 && !s1RunId && !pinnedRunError && (
          <div
            className="mb-5 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            data-testid="session-selection-required-banner"
          >
            <Shield size={15} className="shrink-0 text-amber-600" />
            <span>
              <span className="font-semibold">Choose a review run.</span>{" "}
              Select the run you want to review from the selector above.
              Each run is identified by a short ID, site, date, and its approved / pending count.
            </span>
          </div>
        )}

        {/* ── Bridge Preview Panel — shown when button toggled, keyed by s1_run_id ── */}
        {/* ── Content — only rendered after an explicit run is chosen ── */}
        {!s1RunId ? (
          // Req 3: while the initial loadSessions() is in flight and a run was pinned
          // via URL, show a spinner rather than the false "No review run selected" message.
          isInitializing && urlS1RunId ? (
            <div className="flex flex-col items-center justify-center py-24 text-center text-slate-400">
              <div className="w-8 h-8 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin mb-4" />
              <p className="text-sm text-slate-500">Restoring review run…</p>
            </div>
          ) : (
            sessions.length > 0 && (
              <div className="flex flex-col items-center justify-center py-24 text-center text-slate-400">
                <Shield size={40} className="mb-4 text-slate-300" />
                <p className="text-sm font-semibold text-slate-500">No review run selected</p>
                <p className="text-xs mt-1 max-w-sm">
                  Choose a run from the selector above. Once selected, the queue will show
                  pending items ready for review.
                </p>
              </div>
            )
          )
        ) : (
          <>

        {/* Coverage bar */}
        <CoverageBar coverage={coverage} />

        {/* Summary panel */}
        <SummaryPanel
          coverage={coverage}
          activeTile={activeTile}
          onTileClick={(tileKey) => {
            // Issue 6: clicking a tile filters the list and highlights the tile
            setActiveTile(prev => prev === tileKey ? null : tileKey);
            setPage(1);
            setExpandedRows(new Set());
            if (tileKey === "needs_review") {
              setViewMode("needs_review");
              setFilterStatus("");
              setFilterDecision("pending");
            } else if (tileKey === "matched") {
              setViewMode("all");
              setFilterStatus("");
              setFilterDecision("approved");
            } else if (tileKey === "blocked") {
              setViewMode("all");
              setFilterStatus("blocked");
              setFilterDecision("pending");
            } else if (tileKey === "proposed_new") {
              setViewMode("all");
              setFilterStatus("proposed_new");
              setFilterDecision("pending");
            }
          }}
        />

        {/* ── Package A: Batch AI Prefill status panel (Admin only) ─────── */}
        {isAdmin && (() => {
          // Derive the colour-coded badge state from DB status + in-flight info
          const st        = batchStatus;
          const job       = st?.job;
          const inFlight  = batchPrefill?.running || job?.state === 'running';

          // Determine phase for colour + copy
          // red:   AI disabled, or has failed rows after a run, or status check error
          // amber: in-flight (loading)
          // green: all eligible rows have suggestions, or none needed
          // grey:  status not yet known
          let phase = 'grey'; // not yet loaded
          let statusText = 'Checking…';
          let subText    = '';

          if (inFlight) {
            phase      = 'amber';
            // Live counts come from the persisted server job (polled every 3 s, survives reload).
            statusText = job?.state === 'running' ? 'Running' : 'Starting…';
            subText    = job?.state === 'running' ? `${job.processed} of ${job.eligible} processed` : '';
          } else if (st) {
            if (!st.enabled) {
              phase      = 'red';
              statusText = 'AI suggestions unavailable';
              subText    = st._error ? st._error : 'PSB_AI_SUGGESTIONS not set';
            } else if (st._error) {
              phase      = 'red';
              statusText = 'Status check failed';
              subText    = st._error;
            } else if (st.failed > 0) {
              // At least one row has a persisted error marker.
              // DB state is authoritative: st.failed is written atomically
              // before checkBatchStatus is called, so no lastRun guard needed.
              phase      = 'red';
              statusText = `${st.failed} suggestion${st.failed !== 1 ? 's' : ''} failed`;
              subText    = `${st.cached} ready · ${st.failed} failed`;
            } else if (st.all_done) {
              phase      = 'green';
              if (st.none_needed || st.total_eligible === 0) {
                statusText = 'None needed';
                subText    = 'No eligible rows in this run';
              } else {
                statusText = `${st.cached} suggestion${st.cached !== 1 ? 's' : ''} ready`;
                subText    = st.decided > 0 ? `${st.decided} rows already decided` : 'All eligible rows pre-filled';
              }
            } else if (st.uncached_eligible > 0) {
              // Not actively in-flight — AI pre-fill is user-initiated only, so
              // show an honest "needed" state with a "Get AI suggestions" /
              // "Load missing" button rather than silently starting work.
              phase      = 'amber';
              const need = st.uncached_eligible;
              statusText = `${need} suggestion${need !== 1 ? 's' : ''} needed`;
              subText    = st.cached > 0
                ? `${st.cached} of ${st.total_eligible} ready`
                : `${st.total_eligible} row${st.total_eligible !== 1 ? 's' : ''} to process`;
            }
          }

          const dot = {
            grey:  'bg-slate-400',
            amber: 'bg-amber-400',
            green: 'bg-emerald-500',
            red:   'bg-red-500',
          }[phase];
          const border = {
            grey:  'border-slate-200 bg-slate-50',
            amber: 'border-amber-200 bg-amber-50',
            green: 'border-emerald-200 bg-emerald-50',
            red:   'border-red-200 bg-red-50',
          }[phase];
          const textColor = {
            grey:  'text-slate-700',
            amber: 'text-amber-800',
            green: 'text-emerald-800',
            red:   'text-red-800',
          }[phase];
          const subColor = {
            grey:  'text-slate-500',
            amber: 'text-amber-700',
            green: 'text-emerald-700',
            red:   'text-red-700',
          }[phase];

          // Show Retry button only when AI is enabled and there are uncached/failed rows
          const canRetry = st?.enabled && !inFlight && (
            (st.uncached_eligible ?? 0) > 0 || (st.failed ?? 0) > 0
          );
          const isRetry = (st?.failed ?? 0) > 0 || ['interrupted', 'failed', 'completed_with_failures'].includes(job?.state);
          // Force-retry: re-process cached rows too (use sparingly)
          const canForce = st?.enabled && !inFlight && st?.failed > 0;

          return (
            <div
              className={`mb-4 rounded-lg border p-3 ${border}`}
              data-testid="batch-prefill-panel"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${dot} ${phase === 'amber' ? 'animate-pulse' : ''}`} />
                  <Sparkles size={13} className={`flex-shrink-0 ${textColor}`} />
                  <span className={`text-sm font-medium ${textColor}`}>
                    AI Suggestions
                  </span>
                  <span className={`text-xs ${subColor} sm:truncate`} data-testid="batch-prefill-summary">
                    — {statusText}{subText ? ` · ${subText}` : ''}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {inFlight && (
                    <Loader2 size={13} className={`animate-spin ${textColor}`} />
                  )}
                  {canRetry && (
                    <button
                      onClick={() => handleBatchPrefill({ force: false })}
                      disabled={inFlight}
                      className="px-3 py-1 border border-current text-xs rounded hover:opacity-80 disabled:opacity-40"
                      style={{ color: phase === 'red' ? '#b91c1c' : '#d97706' }}
                      data-testid="batch-prefill-run-btn"
                      title="Runs only rows that failed or were never processed — keeps good suggestions and never touches decided rows"
                    >
                      {isRetry ? 'Retry failed + unprocessed' : 'Get AI suggestions'}
                    </button>
                  )}
                  {canForce && (
                    <button
                      onClick={() => handleBatchPrefill({ force: true })}
                      disabled={inFlight}
                      className="px-3 py-1 border border-slate-300 text-slate-600 text-xs rounded hover:bg-slate-50 disabled:opacity-40"
                      data-testid="batch-prefill-retry-btn"
                      title="Force-retry all failed rows (does not re-process already-decided rows)"
                    >
                      Force retry
                    </button>
                  )}
                </div>
              </div>
              <AiBatchProgress job={job} />
            </div>
          );
        })()}

        {/* ── PRIMARY QUEUE: Staff / Owner / Blocked / Approved ── */}
        <StaffCorrectionPanel
          s1RunId={s1RunId}
          siteId={selectedSession?.site_id || urlSiteId || coverage?.target_site_id}
          onOpenBatchReview={() => setShowBulkStrong(true)}
          onSelectRow={(row) => {
            setViewMode("needs_review");
            setFilterDecision("pending");
            setFilterStatus("");
            setFilterSearch(row.itemcode || "");
            // Single-click expand: auto-open the evidence/resolution panel after list reloads
            setPendingExpandItemcode(row.itemcode || null);
            setTimeout(() => {
              document.getElementById("exception-list-anchor")?.scrollIntoView({ behavior: "smooth" });
            }, 150);
          }}
        />

        {/* ── Strict exact-matches review group (owner-controlled) ──────── */}
        {strictExact && strictExact.total > 0 && (
          <div
            className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 p-4"
            data-testid="strict-exact-matches-banner"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-700" />
                  <span className="font-semibold text-sm text-emerald-900">
                    {strictExact.total} strict exact match{strictExact.total === 1 ? "" : "es"} ready for your confirmation
                  </span>
                </div>
                <p className="mt-1 text-xs text-emerald-800 max-w-3xl">
                  Normalised name matches an active master and every identity gate passes.
                  Tick the rows you want to confirm; nothing is preselected and no decision is applied until you click Confirm.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={handleRerankPending}
                  disabled={rerankLoading}
                  className="px-3 py-1.5 border border-emerald-300 text-emerald-800 rounded text-xs hover:bg-emerald-100 disabled:opacity-50"
                  data-testid="rerank-pending-btn"
                  title="Re-score pending rows against the current master and alias registry"
                >
                  <RefreshCw size={11} className={`inline mr-1 ${rerankLoading ? "animate-spin" : ""}`} />
                  Re-score pending
                </button>
                <button
                  onClick={() => setViewMode("strict_exact")}
                  className={`px-3 py-1.5 rounded text-xs font-medium ${
                    viewMode === "strict_exact"
                      ? "bg-emerald-700 text-white"
                      : "border border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                  }`}
                  data-testid="strict-exact-open-btn"
                >
                  Open review group
                </button>
              </div>
            </div>
          </div>
        )}

        {viewMode === "strict_exact" && strictExact && (
          <StrictExactReview
            s1RunId={s1RunId}
            payload={strictExact}
            selected={strictSelected}
            setSelected={setStrictSelected}
            onConfirm={async (mcIds) => {
              // Reuse existing per-row approve path — same audit trail, same gates.
              await handleApprove(mcIds, "strict_exact");
              await Promise.all([loadExceptions(), loadStrictExact()]);
              setStrictSelected(new Set());
            }}
            onClose={() => {
              setViewMode("needs_review");
              setFilterDecision("pending");
              setFilterStatus("");
              setActiveTile(null);
            }}
          />
        )}

        {viewMode !== "strict_exact" && (
        <>
        {/* View mode tabs */}
        <div id="exception-list-anchor" className="flex flex-wrap items-center gap-1 mb-4" data-testid="view-tabs">
          <button
            onClick={() => { setViewMode("needs_review"); setFilterStatus(""); setFilterDecision("pending"); setActiveTile(null); }}
            data-testid="view-tab-needs-review"
            className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
              viewMode === "needs_review"
                ? "bg-amber-600 text-white"
                : "border border-slate-300 text-slate-600 hover:bg-slate-50"
            }`}
          >
          Needs a decision
            {coverage && (
              <span className="ml-1.5 text-xs opacity-75">
                ({(coverage.by_match_status?.strong_suggestion?.pending || 0) +
                  (coverage.by_match_status?.review_required?.pending   || 0)})
              </span>
            )}
          </button>
          <button
            onClick={() => { setViewMode("all"); setFilterStatus(""); setFilterDecision(""); setActiveTile(null); }}
            data-testid="view-tab-all"
            className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
              viewMode === "all"
                ? "bg-slate-700 text-white"
                : "border border-slate-300 text-slate-600 hover:bg-slate-50"
            }`}
          >
            All Rows
            {coverage && (
              <span className="ml-1.5 text-xs opacity-75">
                ({coverage.total_candidates})
              </span>
            )}
          </button>
          <span className="ml-2 text-xs text-slate-400 italic">
          {viewMode === "needs_review" ? "Products waiting for your confirmation" : "All products and statuses"}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={loadExceptions}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded text-sm hover:bg-slate-50"
              data-testid="refresh-btn"
              title="Refresh list"
              aria-label="Refresh list"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              <span className="text-xs text-slate-500">Refresh</span>
            </button>
          </div>
        </div>

        {/* ── Admin Diagnostics (collapsed by default) ── */}
        <div className="mb-4">
          <button
            onClick={() => setShowAdminDiagnostics(t => !t)}
            data-testid="admin-diagnostics-toggle"
            className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
          >
            <Wrench size={12} />
            Admin Diagnostics
            {showAdminDiagnostics ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
            {!showAdminDiagnostics && (
              <span className="font-normal text-slate-400">— advanced import review tools</span>
            )}
          </button>
          {showAdminDiagnostics && (
            <div className="mt-3 space-y-4" data-testid="admin-diagnostics-panel">

              {/* Post-Mix Review (moved from main view) */}
              <PostmixPanel
                s1RunId={s1RunId}
                onApproved={loadExceptions}
              />

              {/* Non-stock items review */}
              {s1RunId && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={13} className="text-amber-600"/>
                    <span className="text-xs font-semibold text-slate-700">Non-stock items</span>
                    <button
                      onClick={() => setShowBridgePanel(p => !p)}
                      className={`ml-auto flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-medium
                        ${showBridgePanel
                          ? "border-amber-400 bg-amber-100 text-amber-800"
                          : "border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700"}`}
                      data-testid="review-source-bridge-link"
                    >
                      {showBridgePanel ? "Hide" : "Review non-stock items"}
                    </button>
                  </div>
                  {showBridgePanel && <BridgePreviewPanel s1RunId={s1RunId} />}
                </div>
              )}

              {/* Cascade Pipeline + Bulk Tools */}
              <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-start">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                    <GitBranch size={13}/> Cascade Pipeline
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    Propagate all approved mappings into downstream stages. Idempotent — safe to run multiple times.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleCascade}
                      disabled={cascadeLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium text-sm disabled:opacity-50"
                      data-testid="cascade-btn"
                    >
                      <GitBranch size={15} />
                      {cascadeLoading ? "Cascading…" : "Cascade Pipeline"}
                    </button>
                    <button
                      onClick={() => setShowBulkStrong(true)}
                      className="flex items-center gap-2 px-4 py-2 border border-amber-400 text-amber-700 hover:bg-amber-50 rounded font-medium text-sm"
                      data-testid="bulk-strong-open-btn"
                    >
                      <CheckSquare size={15} /> Review Strong Suggestions
                    </button>
                    <button
                      onClick={handleExportCsv}
                      className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50"
                      data-testid="export-csv-btn"
                    >
                      <Download size={14} /> Export CSV
                    </button>
                    <a
                      href="/stocktaking/psb-stage1b-alias-registry"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50 text-slate-600"
                      data-testid="alias-registry-link"
                    >
                      <ExternalLink size={14}/> Alias Registry
                    </a>
                  </div>
                </div>
              </div>

              {/* A/B/C/D Action Ledger */}
              <AbcdLedgerPanel
                s1RunId={s1RunId}
                onOpenBatchReview={() => setShowBulkStrong(true)}
                onOpenDecisionQueue={() => {
                  setViewMode("needs_review");
                  setFilterDecision("pending");
                  setFilterStatus("");
                  setFilterSearch("");
                  setTimeout(() => {
                    document.getElementById("exception-list-anchor")?.scrollIntoView({ behavior: "smooth" });
                  }, 150);
                }}
              />

              {/* Disposition Queue */}
              <DispositionQueuePanel
                s1RunId={s1RunId}
                onSelectRow={(row) => {
                  setViewMode("needs_review");
                  setFilterDecision("pending");
                  setFilterStatus("");
                  setFilterSearch(row.itemcode);
                  setTimeout(() => {
                    document.getElementById("exception-list-anchor")?.scrollIntoView({ behavior: "smooth" });
                  }, 200);
                }}
              />
            </div>
          )}
          {cascadeResult && <CascadeResult result={cascadeResult} />}
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap gap-3 items-center">
          <Filter size={14} className="text-slate-400" />
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2 text-slate-400" />
            <input
              className="pl-8 pr-3 py-1.5 border border-slate-300 rounded text-sm w-52"
              placeholder="Search itemcode / description…"
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              data-testid="search-input"
            />
          </div>
          {/* Item 5: AbbeyZone taxonomy hierarchy filter (replaces legacy PSB group codes) */}
          {taxonomyHierarchy.length > 0 ? (
            <>
              <select
                className="border border-slate-300 rounded px-3 py-1.5 text-sm"
                value={filterCategoryId}
                onChange={e => { setFilterCategoryId(e.target.value); setFilterSubcatId(""); }}
                data-testid="filter-category"
              >
                <option value="">All categories</option>
                {taxonomyHierarchy.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} ({cat.candidate_count ?? "?"})
                  </option>
                ))}
              </select>
              {filterCategoryId && (
                <select
                  className="border border-slate-300 rounded px-3 py-1.5 text-sm"
                  value={filterSubcatId}
                  onChange={e => setFilterSubcatId(e.target.value)}
                  data-testid="filter-subcategory"
                >
                  <option value="">All subcategories</option>
                  {(taxonomyHierarchy.find(c => String(c.id) === String(filterCategoryId))?.subcategories || []).map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.candidate_count ?? "?"})
                    </option>
                  ))}
                </select>
              )}
            </>
          ) : (
            /* Fallback to legacy group codes when taxonomy is not yet loaded */
            <select
              className="border border-slate-300 rounded px-3 py-1.5 text-sm"
              value={filterCategoryId}
              onChange={e => setFilterCategoryId(e.target.value)}
              data-testid="filter-group"
            >
              <option value="">All groups</option>
              {filterOptions.group_codes.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          )}
          {/* Status + decision filters only in "all" view */}
          {viewMode === "all" && (
            <>
              <select
                className="border border-slate-300 rounded px-3 py-1.5 text-sm"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                data-testid="filter-status"
              >
                <option value="">All statuses</option>
                {filterOptions.match_statuses.map(({ value, count }) => (
                  <option key={value} value={value}>
                    {STATUS_META[value]?.label || value} ({count})
                  </option>
                ))}
              </select>
              <select
                className="border border-slate-300 rounded px-3 py-1.5 text-sm"
                value={filterDecision}
                onChange={e => setFilterDecision(e.target.value)}
                data-testid="filter-decision"
              >
                <option value="">All decisions</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="excluded">Excluded</option>
              </select>
            </>
          )}
          {viewMode === "needs_review" && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Showing products waiting for confirmation
            </span>
          )}
          {(filterCategoryId || filterSubcatId || filterSearch || (viewMode === "all" && (filterStatus || filterDecision))) && (
            <button
              onClick={() => {
                setFilterCategoryId(""); setFilterSubcatId(""); setFilterSearch("");
                if (viewMode === "all") { setFilterStatus(""); setFilterDecision(""); }
              }}
              className="text-xs text-slate-500 hover:text-slate-800 underline"
              data-testid="clear-filters-btn"
            >
              Clear filters
            </button>
          )}
          <span className="ml-auto text-xs text-slate-400">{total} result{total !== 1 ? "s" : ""}</span>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2.5 mb-3 flex items-center gap-3 text-sm flex-wrap">
            <span className="font-medium text-indigo-800">{selected.size} selected</span>
            {selectionHasNonApprovable && (
              <span className="text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle size={12} /> Selection includes blocked rows — remove them to confirm
              </span>
            )}
            {selectionHasApprovable && !selectionHasNonApprovable && (
              <>
                {/* Task 3: "Confirm selected matches" works for ALL eligible rows */}
                <button
                  onClick={handleConfirmSelectedMatches}
                  className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium flex items-center gap-1"
                  data-testid="confirm-selected-matches-btn"
                >
                  <CheckCheck size={13} /> Confirm selected matches
                </button>
                {selectionHasExactOnly && (
                  <button
                    onClick={handleBulkApprove}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium"
                    data-testid="bulk-approve-btn"
                  >
                    Bulk Approve (exact-only)
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => {
                const approvableIds = selectedList.filter(id => {
                  const r = exceptions.find(e => e.mc_id === id);
                  return r && r.is_approvable && r.decision === "approved";
                });
                if (approvableIds.length) handleUnapprove(approvableIds);
              }}
              className="px-3 py-1 border border-slate-300 rounded text-xs hover:bg-white"
            >
              Revert selected to pending
            </button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500 underline ml-auto">
              Clear selection
            </button>
          </div>
        )}

        {/* Exception table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <RefreshCw size={20} className="animate-spin mr-2" /> Loading…
            </div>
          ) : exceptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <CheckCircle2 size={32} className="mb-3 text-emerald-400" />
              <div className="text-sm font-medium">No exceptions match current filters</div>
              <div className="text-xs mt-1">
                {filterDecision === "pending"
                  ? "All pending exceptions have been resolved for this filter."
                  : "Try adjusting your filters."}
              </div>
            </div>
          ) : (
            <table className="w-full text-sm border-collapse" data-testid="exceptions-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
                <tr>
                  <th className="w-8 px-3 py-2.5">
                    <input type="checkbox" checked={selected.size === exceptions.length && exceptions.length > 0}
                      onChange={toggleAll} className="rounded" data-testid="select-all" />
                  </th>
                  <th className="px-3 py-2.5 text-left w-20">Code</th>
                  <th className="px-3 py-2.5 text-left">Description</th>
                  <th className="px-3 py-2.5 text-left w-24">Size / Group</th>
                  <th className="px-3 py-2.5 text-left w-36">Match Status</th>
                  <th className="px-3 py-2.5 text-left">Matched Product</th>
                  <th className="px-3 py-2.5 text-right w-24">Actions</th>
                  <th className="w-8 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {exceptions.map((row) => (
                  <React.Fragment key={row.mc_id}>
                    <tr
                      id={`exception-row-${row.mc_id}`}
                      className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${
                        selected.has(row.mc_id) ? "bg-indigo-50" : ""
                      }`}
                      data-testid={`exception-row-${row.mc_id}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(row.mc_id)}
                          onChange={() => toggleRow(row.mc_id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{row.itemcode}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800 text-xs">{row.source_description}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {row.size_raw || "—"}<br/>
                        <span className="text-slate-400">{row.group_code}</span>
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={row.match_status} />
                        {row.decision === "az_adjustment" && (
                          <span
                            className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200"
                            title="Routed to Management › Allowances"
                            data-testid={`az-adj-badge-${row.mc_id}`}
                          >
                            AZ Adj.
                          </span>
                        )}
                        {row.conflict_count > 0 && (
                          <span className="ml-1 text-amber-500" title={`${row.conflict_count} conflicts`}>
                            <AlertTriangle size={11} />
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        {row.matched_bp_name || <span className="italic text-slate-400">None</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {/* Terminal-decision guards: never offer a product approval path
                            once the row has been conclusively routed or excluded */}
                        {row.decision === "az_adjustment" ? (
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200 flex items-center gap-0.5 justify-end"
                            title="Routed to Management › Allowances — non-product row"
                            data-testid={`az-adj-inline-${row.mc_id}`}
                          >
                            AZ Allowance
                          </span>
                        ) : row.decision === "excluded" ? (
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200 flex items-center gap-0.5 justify-end"
                            title="Excluded from this run"
                            data-testid={`excluded-inline-${row.mc_id}`}
                          >
                            Excluded
                          </span>
                        ) : row.is_approvable ? (
                          row.decision === "approved" ? (
                            <button
                              onClick={() => handleUnapprove([row.mc_id])}
                              className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50 text-slate-600"
                              data-testid={`unapprove-btn-${row.mc_id}`}
                            >
                              Revert
                            </button>
                          ) : (row.conflict_count > 0 && !row.matched_bp_name) ? (
                            <span
                              className="text-xs text-amber-600 flex items-center gap-0.5 justify-end"
                              title={`${row.conflict_count} conflicting candidates — expand row to resolve before approving`}
                              data-testid={`conflicts-pending-${row.mc_id}`}
                            >
                              <AlertTriangle size={11} /> Resolve first
                            </span>
                          ) : (
                            <button
                              onClick={() => {
                                if (resolvingMcIds.has(row.mc_id)) return;
                                handleApprove([row.mc_id], "individual");
                              }}
                              disabled={resolvingMcIds.has(row.mc_id)}
                              className="text-xs px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                              data-testid={`approve-btn-${row.mc_id}`}
                            >
                              {resolvingMcIds.has(row.mc_id)
                                ? <><RefreshCw size={10} className="animate-spin" /> Confirming…</>
                                : 'Confirm match'}
                            </button>
                          )
                        ) : (
                          <span className="text-xs text-red-400 flex items-center gap-0.5 justify-end" title="Not approvable">
                            <Lock size={11} /> N/A
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <button onClick={() => { toggleExpand(row.mc_id); loadRowDetail(row.mc_id); }}
                          data-testid={`expand-btn-${row.mc_id}`}
                          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-1.5 py-1 rounded hover:bg-slate-100"
                          aria-label={expandedRows.has(row.mc_id) ? "Close detail" : "Review detail"}
                        >
                          <span>{expandedRows.has(row.mc_id) ? "Close" : "Review"}</span>
                          {expandedRows.has(row.mc_id) ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
                        </button>
                      </td>
                    </tr>
                    {expandedRows.has(row.mc_id) && (
                      <tr className="border-t-0">
                        <td colSpan={12} className="p-0">
                          <ResolutionPanel
                            row={row}
                            detail={detailData[row.mc_id]}
                            loading={detailLoading.has(row.mc_id)}
                            s1RunId={s1RunId}
                            siteId={selectedSession?.site_id || urlSiteId || coverage?.target_site_id}
                            overrideState={overrideState?.mcId === row.mc_id ? overrideState : null}
                            onResolve={(action, azBpId) => {
                              if (action === "approve") handleApprove([row.mc_id], "individual");
                              else handleResolve(row.mc_id, action, azBpId);
                            }}
                            onResolveWithOverride={(action, azBpId, reason) =>
                              handleResolve(row.mc_id, action, azBpId, reason)
                            }
                            onClearOverride={() => setOverrideState(null)}
                            onRequestOverride={(azBpId, failedGates, errorMessage, isProposedNew) =>
                              setOverrideState({ mcId: row.mc_id, action: "map_to", azBpId, failedGates, errorMessage, isProposedNew: !!isProposedNew })
                            }
                            onAIAssist={() => handleAIAssist(row.mc_id)}
                            onResearch={(barcode) => handleResearchOnline(row.mc_id, barcode)}
                            onExclude={() => setExcludeTarget(row)}
                            onNewProductCreated={() => { setAutoAdvancePending(true); loadExceptions(); loadSessions(); }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && viewMode !== "strict_exact" && (
          <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
            <span>Page {page} of {totalPages} ({total} total)</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50"
                data-testid="prev-page"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50"
                data-testid="next-page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
        </>
        )}

        {/* Navigation */}
        <div className="mt-6 pt-4 border-t border-slate-200 flex justify-between items-center text-sm">
          <button
            onClick={() => {
              // strict_exact mode: Back returns to the needs_review list of
              // this import (preserving run context) rather than navigating away.
              if (viewMode === 'strict_exact') {
                setViewMode('needs_review');
                setFilterDecision('pending');
                setFilterStatus('');
                setActiveTile(null);
                setExpandedRows(new Set());
                return;
              }
              // For all other modes: prefer the React Router state.from which is
              // set by any in-app navigation to this page.  Fall back to the
              // import page rather than navigate(-1) which can exit the SPA entirely
              // when this page was opened via a direct link or a bookmark.
              const from = location.state?.from;
              if (from) {
                navigate(from);
              } else {
                navigate('/stocktaking/import');
              }
            }}
            className="text-slate-500 hover:text-slate-800 flex items-center gap-1"
            data-testid="back-to-import"
          >
            {viewMode === 'strict_exact' ? '← Back to review list' : '← Back'}
          </button>
          <div className="text-xs text-slate-400 text-center">
            {coverage && coverage.held_count > 0 ? (
              <>
                <span className="text-amber-600 font-medium">{coverage.held_count} item{coverage.held_count !== 1 ? "s" : ""} still need{coverage.held_count === 1 ? "s" : ""} a decision</span>
                {" — "}resolve these before proceeding.
              </>
            ) : coverage && coverage.held_count === 0 ? (
              "All products confirmed. Ready to run the import."
            ) : (
              "Resolve all items to proceed."
            )}
          </div>
          {coverage && coverage.held_count > 0 ? (
            <span
              className="text-slate-400 flex items-center gap-1 cursor-not-allowed select-none"
              data-testid="stage7-blocked-btn"
              title="Resolve all pending items first"
            >
              Continue <Lock size={14} />
            </span>
          ) : (
            <div className="flex items-center gap-3">
              {/* 2026-11 — available as soon as every match is resolved;
                  deliberately NOT gated on Stage 7 valuation-lock or Stage 8
                  dispatch, which stay separate concerns. */}
              <button
                onClick={() => setShowPriceReview(true)}
                className="text-slate-600 hover:text-slate-900 flex items-center gap-1 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50"
                data-testid="psb-price-review-open-btn"
              >
                Review source prices & selling measures
              </button>
              <button
                onClick={() => {
                  // If this page was reached from the Route A wizard, return there.
                  // Otherwise use the Route B background-pipeline progress page.
                  if (urlConversionId) {
                    navigate(`/stocktaking/import/historical-stocktake?conversion_id=${urlConversionId}`);
                  } else {
                    navigate(`/stocktaking/import/psb-progress?s1_run_id=${s1RunId}&back_s1=${s1RunId}`);
                  }
                }}
                className="text-slate-500 hover:text-slate-800 flex items-center gap-1"
                data-testid="stage7-nav-btn"
              >
                Continue <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>

        {showPriceReview && (
          <PsbPriceReviewPanel s1RunId={s1RunId} onClose={() => setShowPriceReview(false)} />
        )}

        {/* Exclude dialog */}
        {excludeTarget && (
          <ExcludeDialog
            row={excludeTarget}
            onConfirm={(reason, scope) => handleExcludeConfirm(excludeTarget.mc_id, reason, scope)}
            onCancel={() => setExcludeTarget(null)}
          />
        )}

        {/* Bulk strong-suggestion panel */}
        {showBulkStrong && s1RunId && (
          <BulkStrongPanel
            s1RunId={s1RunId}
            onDone={() => { loadExceptions(); loadSessions(); }}
            onClose={() => setShowBulkStrong(false)}
          />
        )}

          </>
        )}
      </div>
    </div>
  );
}
